// src/components/PatientReport.tsx - Med-Surg, ICU, and Rehab Shift Report Forms
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Patient } from '../types';
import { useTheme } from '../contexts/ThemeContext';
import { 
  Info, 
  Heart, 
  Pill, 
  Activity, 
  Thermometer, 
  TestTube, 
  Home,
  ChevronDown,
  ChevronUp,
  Construction,
  Users,
  ArrowLeft,
  Wind,
  Syringe,
  Brain,
  Stethoscope,
  FileText,
  Target,
  MessageSquare,
  Accessibility,
  Utensils,
  Smile,
  Calendar,
  ClipboardList,
  Zap,
  Clock,
  AlertCircle,
  AlertTriangle,
  Scissors,
  Droplets,
  Shield,
  CheckCircle,
  ShieldCheck,
  User,
  TrendingUp,
  MapPin,
  BookOpen
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8080";

// Utility function to translate ICD-10 codes and medical abbreviations in text
const translateMedicalTerms = async (text: string): Promise<string> => {
  if (!text) return text;
  let translatedText = text;
  
  // First translate ICD-10 codes
  try {
    const icd10Response = await fetch(`${BACKEND_URL}/translate-icd10`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text: translatedText })
    });
    const icd10Data = await icd10Response.json();
    if (icd10Data.success && icd10Data.has_translations) {
      translatedText = icd10Data.translated_text;
    }
  } catch (error) {
    console.error('Error translating ICD-10 codes:', error);
  }
  
  // Then translate medical abbreviations
  try {
    const abbrevResponse = await fetch(`${BACKEND_URL}/translate-abbreviations`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text: translatedText })
    });
    const abbrevData = await abbrevResponse.json();
    if (abbrevData.success && abbrevData.has_translations) {
      translatedText = abbrevData.translated_text;
    }
  } catch (error) {
    console.error('Error translating medical abbreviations:', error);
  }
  
  return translatedText;
};

// Utility function to calculate age from date of birth
const calculateAgeFromDOB = (dob: string): string => {
  if (!dob) return '';
  try {
    const birthDate = new Date(dob);
    if (isNaN(birthDate.getTime())) return '';
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return `${age} y/o`;
  } catch {
    return '';
  }
};

// Generic report data - stored as any to allow different department structures
interface PatientReportProps {
  patientData: Patient | null;
  savedReport: Record<string, unknown> | undefined;
  onSaveReport: (mrn: string, report: Record<string, unknown>) => void;
  loggedInUser: { first_name: string; last_name: string; email: string } | null;
  onReportSubmitted?: () => void;
  skipDbFetch?: boolean; // If true, don't fetch from DB - use savedReport from parent state
  onInitialLoad?: (mrn: string) => void; // Callback when report is first loaded from DB
}

// Med-Surg Report Data Type
interface MedSurgReportData {
  patientOverview: { admitDx: string; codeStatus: string; allergies: string; };
  vitalsOxygen: { bp: string; hr: string; temp: string; oxygen: string; };
  medications: { scheduledMeds: string; prnMedsGiven: string; };
  mobilitySkin: { activity: string; skin: string; };
  painManagement: { painScore: string; painControl: string; };
  labsTests: { latestLabs: string; };
  dischargePlanning: { dischargeGoal: string; needs: string; };
  otherNotes: string;
}

// Rehab Report Data Type
interface RehabReportData {
  patientOverview: { name: string; mrn: string; rehabDx: string; dateOfInjury: string; codeStatus: string; precautions: string; };
  cognitionCommunication: { orientation: string; speech: string; followsCommands: string; memoryInsight: string; };
  mobilityTransfers: { bedMobility: string; transfers: string; ambulation: string; wheelchairUse: string; bracingOrthotics: string; };
  adls: { bathing: string; dressing: string; toileting: string; feeding: string; };
  therapyServices: { pt: string; ot: string; slp: string; lastSessionSummary: string; therapyTolerance: string; };
  painSpasticity: { painScore: string; painLocation: string; spasticity: string; painControlEffectiveness: string; };
  medications: { scheduledMeds: string; prnsUsed: string; anticoagulation: string; spasticityMeds: string; };
  skinSafety: { skinIntegrity: string; pressureAreas: string; fallRisk: string; alarmsSafetyDevices: string; };
  eliminationNutrition: { dietType: string; swallowPrecautions: string; bladderProgram: string; bowelProgram: string; };
  psychosocial: { moodAffect: string; motivation: string; familyInvolvement: string; };
  dischargePlanning: { estDischargeDate: string; dischargeLocation: string; equipmentNeeds: string; caregiverTraining: string; barriers: string; };
  additionalNotes: { events: string; changesFromPriorShift: string; followUps: string; };
  otherNotes: string;
}

// ICU Report Data Type
interface ICUReportData {
  patientInfo: { name: string; mrn: string; admitDx: string; allergies: string; codeStatus: string; };
  icuVitals: { ventMode: string; fio2: string; peep: string; tidalVol: string; rr: string; };
  continuousInfusions: { vasopressors: string; sedationAnalgesia: string; dose: string; rate: string; titrationGoals: string; };
  hemodynamics: { arterialLine: string; cvp: string; map: string; cardiacOutput: string; };
  neuro: { sedationScale: string; pupils: string; deliriumAssess: string; };
  respiratory: { airwayType: string; secretions: string; lungSounds: string; };
  linesTubes: { centralArterial: string; chestTubes: string; dialysisCatheter: string; };
  labsAbgs: { latestAbg: string; electrolyteTrends: string; lactate: string; };
  planGoals: { ventWeaningPlan: string; pressorWean: string; dailyGoals: string; };
  additionalNotes: { familyConsults: string; pendingStudies: string; };
  otherNotes: string;
}

// PACU Report Data Type
interface PACUReportData {
  patientOverview: { name: string; mrn: string; procedure: string; surgeon: string; anesthesiaType: string; codeStatus: string; };
  immediatePostOp: { arrivalTime: string; phase: string; pacuBay: string; levelOfConsciousness: string; painScore: string; nauseaVomiting: string; };
  airwayBreathing: { airwayType: string; o2Delivery: string; spo2: string; respiratoryRate: string; breathSounds: string; airwayEvents: string; };
  circulation: { bp: string; hr: string; rhythm: string; ivAccess: string; fluidsGiven: string; estimatedBloodLoss: string; };
  neurologic: { loc: string; orientation: string; motorSensory: string; };
  painComfort: { painLocation: string; painScore2: string; analgesiaGiven: string; effectiveness: string; };
  nauseaGI: { nvStatus: string; antiemeticsGiven: string; tolerance: string; };
  surgicalSiteDrains: { incisionLocation: string; dressingStatus: string; drainType: string; output: string; };
  urinaryStatus: { voided: string; bladderScan: string; catheter: string; };
  safetyRecoveryScores: { aldreteScore: string; fallRisk: string; temperature: string; };
  medicationsGiven: { analgesics: string; antiemetics: string; reversals: string; };
  dischargeReadiness: { criteriaMet: string; disposition: string; receivingUnit: string; };
  additionalNotes: { events: string; complications: string; followUps: string; };
  otherNotes: string;
}

// ER Report Data Type
interface ERReportData {
  patientOverview: { age: string; sex: string; chiefComplaint: string; arrivalTime: string; acuity: string; };
  triageSafety: { initialVitals: string; painScore: string; allergies: string; codeStatus: string; isolationSafety: string; };
  primaryAssessment: { airwayStatus: string; breathing: string; circulation: string; disability: string; exposure: string; };
  focusedHistory: { hpiSummary: string; pertinentPmh: string; homeMeds: string; lastPoIntake: string; };
  interventionsPerformed: { ivs: string; o2: string; fluids: string; medicationsGiven: string; procedures: string; };
  diagnostics: { labsOrdered: string; imagingOrdered: string; ekg: string; };
  responseToTreatment: { painReassessment: string; vitalTrends: string; clinicalChanges: string; };
  linesDevices: { ivsLines: string; foley: string; ngTube: string; splints: string; monitors: string; };
  consults: { consultingServices: string; recommendations: string; };
  dispositionPlanning: { disposition: string; admittingService: string; bedAssignment: string; };
  patientEducationBelongings: { discharge: string; dischargeInstructions: string; belongingsSecured: string; };
  additionalNotes: { events: string; concerns: string; };
  otherNotes: string;
}

// NICU Report Data Type
interface NICUReportData {
  patientBirthInfo: { babyName: string; gestationalAge: string; postMenstrualAge: string; dayOfLife: string; birthWeight: string; currentWeight: string; admitDx: string; codeStatus: string; };
  vitals: { hr: string; rr: string; bp: string; spo2: string; temp: string; };
  respiratorySupport: { supportType: string; ventMode: string; fio2: string; peep: string; cpap: string; rate: string; apneaBradyDesatEvents: string; };
  cardiovascular: { map: string; murmur: string; echoFindings: string; perfusion: string; capRefill: string; };
  linesAccess: { uvc: string; uac: string; picc: string; pivs: string; ett: string; };
  fluidsNutrition: { ivFluids: string; tpn: string; lipids: string; enteralFeeds: string; feedType: string; route: string; volume: string; feedingTolerance: string; };
  medications: { scheduledMeds: string; continuousInfusions: string; antibiotics: string; };
  neuroComfort: { tone: string; activity: string; painSedationScale: string; comfortMeasures: string; };
  labsMonitoring: { abgs: string; glucose: string; electrolytes: string; bilirubin: string; cultures: string; };
  skinDevelopment: { skinIntegrity: string; positioning: string; developmentalCare: string; };
  familySocial: { parentsUpdated: string; kangarooCare: string; socialWork: string; lactation: string; };
  planDailyGoals: { respiratoryPlan: string; feedingAdvancement: string; antibioticPlan: string; dischargeBarriers: string; };
  additionalNotes: { events: string; changesFromPriorShift: string; };
  otherNotes: string;
}

// Pediatrics Report Data Type
interface PediatricsReportData {
  patientOverview: { age: string; weight: string; admitDx: string; codeStatus: string; allergies: string; };
  vitals: { hr: string; rr: string; bp: string; spo2: string; temp: string; painScore: string; };
  growthNutrition: { dietType: string; intake: string; ivFluids: string; dailyWeight: string; };
  respiratory: { o2Delivery: string; spo2Trend: string; breathSounds: string; workOfBreathing: string; coughSecretions: string; };
  cardiac: { heartSounds: string; capRefill: string; edema: string; };
  neuroBehavior: { loc: string; orientation: string; behaviorMood: string; developmentalLevel: string; };
  painComfort: { painLocation: string; painScaleUsed: string; painInterventions: string; effectiveness: string; };
  medications: { scheduledMeds: string; prnsGiven: string; antibiotics: string; };
  giElimination: { abdomen: string; bowelSounds: string; stool: string; urineOutput: string; };
  skinSafety: { skinIntegrity: string; ivSiteChecks: string; fallRisk: string; isolationPrecautions: string; };
  familyPsychosocial: { parentCaregiverPresent: string; educationProvided: string; copingConcerns: string; };
  schoolDevelopment: { schoolNeeds: string; playChildLifeInvolvement: string; };
  planDischarge: { planForShift: string; pendingTests: string; dischargeBarriers: string; };
  additionalNotes: { events: string; changesFromPriorShift: string; };
  otherNotes: string;
}

// Oncology Report Data Type
interface OncologyReportData {
  patientOverview: { age: string; cancerType: string; stage: string; codeStatus: string; allergies: string; };
  vitals: { hr: string; bp: string; rr: string; temp: string; spo2: string; painScore: string; };
  treatmentStatus: { currentChemoRadiation: string; cycleNumber: string; dayNumber: string; centralLineType: string; };
  hematology: { wbc: string; anc: string; hgb: string; platelets: string; neutropenicPrecautions: string; };
  symptoms: { pain: string; nausea: string; fatigue: string; mucositis: string; neuropathy: string; };
  infectionRisk: { tempTrends: string; culturesPending: string; antibioticsAntivirals: string; };
  giNutrition: { diet: string; appetite: string; nv: string; bowelPattern: string; };
  skinLines: { portPiccCondition: string; skinIntegrity: string; };
  psychosocial: { emotionalStatus: string; supportSystem: string; };
  plan: { nextTreatment: string; labsPending: string; dischargeBarriers: string; };
  notes: { events: string; providerUpdates: string; };
  otherNotes: string;
}

// Neurology Report Data Type
interface NeurologyReportData {
  patientOverview: { age: string; primaryNeuroDx: string; codeStatus: string; allergies: string; };
  vitals: { hr: string; bp: string; rr: string; temp: string; spo2: string; };
  neuroAssessment: { loc: string; orientation: string; gcsNihss: string; pupils: string; motor: string; sensation: string; };
  seizureManagement: { seizurePrecautions: string; lastSeizure: string; aeds: string; };
  mobilitySafety: { assistLevel: string; fallRisk: string; aspirationRisk: string; };
  icpMonitoring: { icpMonitor: string; neuroChecksFrequency: string; };
  swallowSpeech: { swallowEval: string; dietConsistency: string; };
  elimination: { bladder: string; bowelFunction: string; };
  plan: { imagingPending: string; neuroConsults: string; therapyOrders: string; };
  notes: { changesFromBaseline: string; };
  otherNotes: string;
}

// Cardiology Report Data Type
interface CardiologyReportData {
  patientOverview: { age: string; cardiacDx: string; codeStatus: string; allergies: string; };
  vitals: { hr: string; bp: string; rr: string; spo2: string; temp: string; painChest: string; };
  cardiacMonitoring: { rhythm: string; telemetryAlarms: string; ekgChanges: string; };
  perfusion: { capRefill: string; edema: string; pulses: string; };
  respiratory: { o2Support: string; breathSounds: string; };
  medications: { cardiacDrips: string; anticoagulants: string; prns: string; };
  fluids: { ivFluids: string; ioBalance: string; dailyWeight: string; };
  activity: { activityOrders: string; responseToActivity: string; };
  procedures: { recentCathSurgery: string; siteChecks: string; };
  plan: { cardiologyGoals: string; testsPending: string; dischargePlanning: string; };
  notes: { events: string; rhythmChanges: string; };
  otherNotes: string;
}

// Labor & Delivery Report Data Type
interface LaborDeliveryReportData {
  maternalOverview: { gp: string; gestationalAge: string; bloodType: string; gbsStatus: string; };
  vitals: { bp: string; hr: string; temp: string; spo2: string; painScore: string; };
  fetalMonitoring: { fhrBaseline: string; variability: string; decelerations: string; };
  laborStatus: { cervicalExam: string; contractionPattern: string; membraneStatus: string; };
  medications: { oxytocin: string; magnesium: string; painControl: string; };
  maternalAssessment: { bleeding: string; edema: string; urineOutput: string; };
  riskFactors: { preEclampsia: string; gestationalDiabetes: string; infectionRisk: string; };
  deliveryPlan: { vaginalCsection: string; providerNotified: string; nicuOnStandby: string; };
  postpartum: { fundus: string; lochia: string; perineum: string; };
  notes: { events: string; providerUpdates: string; };
  otherNotes: string;
}

// Psychiatry Report Data Type
interface PsychiatryReportData {
  patientOverview: { age: string; psychDx: string; legalStatus: string; };
  vitals: { hr: string; bp: string; temp: string; rr: string; };
  mentalStatus: { appearance: string; moodAffect: string; thoughtProcess: string; };
  safety: { suicideRisk: string; homicideRisk: string; observationLevel: string; };
  behavior: { agitation: string; cooperation: string; sleepPattern: string; };
  medications: { psychMeds: string; prnsGiven: string; sideEffects: string; };
  substanceUse: { withdrawalRisk: string; ciwaCows: string; };
  therapy: { groupParticipation: string; oneOnOneTherapy: string; };
  familySocial: { supportSystem: string; housingResources: string; };
  plan: { psychGoals: string; dischargeCriteria: string; followUpNeeds: string; };
  notes: { notesField: string; escalations: string; };
  otherNotes: string;
}

// Women's Services (OB) Report Data Type
interface OBReportData {
  patientOverview: { age: string; gP: string; gestationalAge: string; postpartumDay: string; admitDx: string; codeStatus: string; allergies: string; };
  patientPlanPreferences: { infantSexDisclosure: string; feedingPreference: string; bathTiming: string; vaccinesNewborn: string; circumcisionPreference: string; roomingInPreference: string; culturalSpecialRequests: string; };
  vitals: { bp: string; hr: string; rr: string; temp: string; spo2: string; painScore: string; };
  maternalAssessment: { fundus: string; lochia: string; perineumIncisionStatus: string; edema: string; };
  postpartumStatus: { deliveryType: string; complications: string; activityLevel: string; lastCervicalExam: string; membraneStatus: string; };
  breastfeedingLactation: { breastfeeding: string; latchQuality: string; pumping: string; lactationConsult: string; };
  giGu: { voidingStatus: string; flatus: string; bowelFunction: string; };
  medications: { scheduledMeds: string; prnsGiven: string; magnesiumOxytocin: string; };
  labs: { hgbHct: string; bloodTypeRh: string; gbsStatus: string; };
  infantStatus: { infantLocation: string; feedingMethod: string; safetyBandsVerified: string; };
  educationSafety: { postpartumEducationCompleted: string; safeSleepReviewed: string; warningSignsReviewed: string; };
  psychosocial: { moodAffect: string; supportPersonPresent: string; };
  dischargePlanning: { dischargeDate: string; followUpAppointments: string; homeNeeds: string; };
  additionalNotes: { events: string; providerUpdates: string; };
  otherNotes: string;
}

const emptyMedSurgReport: MedSurgReportData = {
  patientOverview: { admitDx: '', codeStatus: '', allergies: '' },
  vitalsOxygen: { bp: '', hr: '', temp: '', oxygen: '' },
  medications: { scheduledMeds: '', prnMedsGiven: '' },
  mobilitySkin: { activity: '', skin: '' },
  painManagement: { painScore: '', painControl: '' },
  labsTests: { latestLabs: '' },
  dischargePlanning: { dischargeGoal: '', needs: '' },
  otherNotes: ''
};

const emptyICUReport: ICUReportData = {
  patientInfo: { name: '', mrn: '', admitDx: '', allergies: '', codeStatus: '' },
  icuVitals: { ventMode: '', fio2: '', peep: '', tidalVol: '', rr: '' },
  continuousInfusions: { vasopressors: '', sedationAnalgesia: '', dose: '', rate: '', titrationGoals: '' },
  hemodynamics: { arterialLine: '', cvp: '', map: '', cardiacOutput: '' },
  neuro: { sedationScale: '', pupils: '', deliriumAssess: '' },
  respiratory: { airwayType: '', secretions: '', lungSounds: '' },
  linesTubes: { centralArterial: '', chestTubes: '', dialysisCatheter: '' },
  labsAbgs: { latestAbg: '', electrolyteTrends: '', lactate: '' },
  planGoals: { ventWeaningPlan: '', pressorWean: '', dailyGoals: '' },
  additionalNotes: { familyConsults: '', pendingStudies: '' },
  otherNotes: ''
};

const emptyRehabReport: RehabReportData = {
  patientOverview: { name: '', mrn: '', rehabDx: '', dateOfInjury: '', codeStatus: '', precautions: '' },
  cognitionCommunication: { orientation: '', speech: '', followsCommands: '', memoryInsight: '' },
  mobilityTransfers: { bedMobility: '', transfers: '', ambulation: '', wheelchairUse: '', bracingOrthotics: '' },
  adls: { bathing: '', dressing: '', toileting: '', feeding: '' },
  therapyServices: { pt: '', ot: '', slp: '', lastSessionSummary: '', therapyTolerance: '' },
  painSpasticity: { painScore: '', painLocation: '', spasticity: '', painControlEffectiveness: '' },
  medications: { scheduledMeds: '', prnsUsed: '', anticoagulation: '', spasticityMeds: '' },
  skinSafety: { skinIntegrity: '', pressureAreas: '', fallRisk: '', alarmsSafetyDevices: '' },
  eliminationNutrition: { dietType: '', swallowPrecautions: '', bladderProgram: '', bowelProgram: '' },
  psychosocial: { moodAffect: '', motivation: '', familyInvolvement: '' },
  dischargePlanning: { estDischargeDate: '', dischargeLocation: '', equipmentNeeds: '', caregiverTraining: '', barriers: '' },
  additionalNotes: { events: '', changesFromPriorShift: '', followUps: '' },
  otherNotes: ''
};

const emptyPACUReport: PACUReportData = {
  patientOverview: { name: '', mrn: '', procedure: '', surgeon: '', anesthesiaType: '', codeStatus: '' },
  immediatePostOp: { arrivalTime: '', phase: '', pacuBay: '', levelOfConsciousness: '', painScore: '', nauseaVomiting: '' },
  airwayBreathing: { airwayType: '', o2Delivery: '', spo2: '', respiratoryRate: '', breathSounds: '', airwayEvents: '' },
  circulation: { bp: '', hr: '', rhythm: '', ivAccess: '', fluidsGiven: '', estimatedBloodLoss: '' },
  neurologic: { loc: '', orientation: '', motorSensory: '' },
  painComfort: { painLocation: '', painScore2: '', analgesiaGiven: '', effectiveness: '' },
  nauseaGI: { nvStatus: '', antiemeticsGiven: '', tolerance: '' },
  surgicalSiteDrains: { incisionLocation: '', dressingStatus: '', drainType: '', output: '' },
  urinaryStatus: { voided: '', bladderScan: '', catheter: '' },
  safetyRecoveryScores: { aldreteScore: '', fallRisk: '', temperature: '' },
  medicationsGiven: { analgesics: '', antiemetics: '', reversals: '' },
  dischargeReadiness: { criteriaMet: '', disposition: '', receivingUnit: '' },
  additionalNotes: { events: '', complications: '', followUps: '' },
  otherNotes: ''
};

const emptyERReport: ERReportData = {
  patientOverview: { age: '', sex: '', chiefComplaint: '', arrivalTime: '', acuity: '' },
  triageSafety: { initialVitals: '', painScore: '', allergies: '', codeStatus: '', isolationSafety: '' },
  primaryAssessment: { airwayStatus: '', breathing: '', circulation: '', disability: '', exposure: '' },
  focusedHistory: { hpiSummary: '', pertinentPmh: '', homeMeds: '', lastPoIntake: '' },
  interventionsPerformed: { ivs: '', o2: '', fluids: '', medicationsGiven: '', procedures: '' },
  diagnostics: { labsOrdered: '', imagingOrdered: '', ekg: '' },
  responseToTreatment: { painReassessment: '', vitalTrends: '', clinicalChanges: '' },
  linesDevices: { ivsLines: '', foley: '', ngTube: '', splints: '', monitors: '' },
  consults: { consultingServices: '', recommendations: '' },
  dispositionPlanning: { disposition: '', admittingService: '', bedAssignment: '' },
  patientEducationBelongings: { discharge: '', dischargeInstructions: '', belongingsSecured: '' },
  additionalNotes: { events: '', concerns: '' },
  otherNotes: ''
};

const emptyNICUReport: NICUReportData = {
  patientBirthInfo: { babyName: '', gestationalAge: '', postMenstrualAge: '', dayOfLife: '', birthWeight: '', currentWeight: '', admitDx: '', codeStatus: '' },
  vitals: { hr: '', rr: '', bp: '', spo2: '', temp: '' },
  respiratorySupport: { supportType: '', ventMode: '', fio2: '', peep: '', cpap: '', rate: '', apneaBradyDesatEvents: '' },
  cardiovascular: { map: '', murmur: '', echoFindings: '', perfusion: '', capRefill: '' },
  linesAccess: { uvc: '', uac: '', picc: '', pivs: '', ett: '' },
  fluidsNutrition: { ivFluids: '', tpn: '', lipids: '', enteralFeeds: '', feedType: '', route: '', volume: '', feedingTolerance: '' },
  medications: { scheduledMeds: '', continuousInfusions: '', antibiotics: '' },
  neuroComfort: { tone: '', activity: '', painSedationScale: '', comfortMeasures: '' },
  labsMonitoring: { abgs: '', glucose: '', electrolytes: '', bilirubin: '', cultures: '' },
  skinDevelopment: { skinIntegrity: '', positioning: '', developmentalCare: '' },
  familySocial: { parentsUpdated: '', kangarooCare: '', socialWork: '', lactation: '' },
  planDailyGoals: { respiratoryPlan: '', feedingAdvancement: '', antibioticPlan: '', dischargeBarriers: '' },
  additionalNotes: { events: '', changesFromPriorShift: '' },
  otherNotes: ''
};

const emptyPediatricsReport: PediatricsReportData = {
  patientOverview: { age: '', weight: '', admitDx: '', codeStatus: '', allergies: '' },
  vitals: { hr: '', rr: '', bp: '', spo2: '', temp: '', painScore: '' },
  growthNutrition: { dietType: '', intake: '', ivFluids: '', dailyWeight: '' },
  respiratory: { o2Delivery: '', spo2Trend: '', breathSounds: '', workOfBreathing: '', coughSecretions: '' },
  cardiac: { heartSounds: '', capRefill: '', edema: '' },
  neuroBehavior: { loc: '', orientation: '', behaviorMood: '', developmentalLevel: '' },
  painComfort: { painLocation: '', painScaleUsed: '', painInterventions: '', effectiveness: '' },
  medications: { scheduledMeds: '', prnsGiven: '', antibiotics: '' },
  giElimination: { abdomen: '', bowelSounds: '', stool: '', urineOutput: '' },
  skinSafety: { skinIntegrity: '', ivSiteChecks: '', fallRisk: '', isolationPrecautions: '' },
  familyPsychosocial: { parentCaregiverPresent: '', educationProvided: '', copingConcerns: '' },
  schoolDevelopment: { schoolNeeds: '', playChildLifeInvolvement: '' },
  planDischarge: { planForShift: '', pendingTests: '', dischargeBarriers: '' },
  additionalNotes: { events: '', changesFromPriorShift: '' },
  otherNotes: ''
};

const emptyOncologyReport: OncologyReportData = {
  patientOverview: { age: '', cancerType: '', stage: '', codeStatus: '', allergies: '' },
  vitals: { hr: '', bp: '', rr: '', temp: '', spo2: '', painScore: '' },
  treatmentStatus: { currentChemoRadiation: '', cycleNumber: '', dayNumber: '', centralLineType: '' },
  hematology: { wbc: '', anc: '', hgb: '', platelets: '', neutropenicPrecautions: '' },
  symptoms: { pain: '', nausea: '', fatigue: '', mucositis: '', neuropathy: '' },
  infectionRisk: { tempTrends: '', culturesPending: '', antibioticsAntivirals: '' },
  giNutrition: { diet: '', appetite: '', nv: '', bowelPattern: '' },
  skinLines: { portPiccCondition: '', skinIntegrity: '' },
  psychosocial: { emotionalStatus: '', supportSystem: '' },
  plan: { nextTreatment: '', labsPending: '', dischargeBarriers: '' },
  notes: { events: '', providerUpdates: '' },
  otherNotes: ''
};

const emptyNeurologyReport: NeurologyReportData = {
  patientOverview: { age: '', primaryNeuroDx: '', codeStatus: '', allergies: '' },
  vitals: { hr: '', bp: '', rr: '', temp: '', spo2: '' },
  neuroAssessment: { loc: '', orientation: '', gcsNihss: '', pupils: '', motor: '', sensation: '' },
  seizureManagement: { seizurePrecautions: '', lastSeizure: '', aeds: '' },
  mobilitySafety: { assistLevel: '', fallRisk: '', aspirationRisk: '' },
  icpMonitoring: { icpMonitor: '', neuroChecksFrequency: '' },
  swallowSpeech: { swallowEval: '', dietConsistency: '' },
  elimination: { bladder: '', bowelFunction: '' },
  plan: { imagingPending: '', neuroConsults: '', therapyOrders: '' },
  notes: { changesFromBaseline: '' },
  otherNotes: ''
};

const emptyCardiologyReport: CardiologyReportData = {
  patientOverview: { age: '', cardiacDx: '', codeStatus: '', allergies: '' },
  vitals: { hr: '', bp: '', rr: '', spo2: '', temp: '', painChest: '' },
  cardiacMonitoring: { rhythm: '', telemetryAlarms: '', ekgChanges: '' },
  perfusion: { capRefill: '', edema: '', pulses: '' },
  respiratory: { o2Support: '', breathSounds: '' },
  medications: { cardiacDrips: '', anticoagulants: '', prns: '' },
  fluids: { ivFluids: '', ioBalance: '', dailyWeight: '' },
  activity: { activityOrders: '', responseToActivity: '' },
  procedures: { recentCathSurgery: '', siteChecks: '' },
  plan: { cardiologyGoals: '', testsPending: '', dischargePlanning: '' },
  notes: { events: '', rhythmChanges: '' },
  otherNotes: ''
};

const emptyLaborDeliveryReport: LaborDeliveryReportData = {
  maternalOverview: { gp: '', gestationalAge: '', bloodType: '', gbsStatus: '' },
  vitals: { bp: '', hr: '', temp: '', spo2: '', painScore: '' },
  fetalMonitoring: { fhrBaseline: '', variability: '', decelerations: '' },
  laborStatus: { cervicalExam: '', contractionPattern: '', membraneStatus: '' },
  medications: { oxytocin: '', magnesium: '', painControl: '' },
  maternalAssessment: { bleeding: '', edema: '', urineOutput: '' },
  riskFactors: { preEclampsia: '', gestationalDiabetes: '', infectionRisk: '' },
  deliveryPlan: { vaginalCsection: '', providerNotified: '', nicuOnStandby: '' },
  postpartum: { fundus: '', lochia: '', perineum: '' },
  notes: { events: '', providerUpdates: '' },
  otherNotes: ''
};

const emptyPsychiatryReport: PsychiatryReportData = {
  patientOverview: { age: '', psychDx: '', legalStatus: '' },
  vitals: { hr: '', bp: '', temp: '', rr: '' },
  mentalStatus: { appearance: '', moodAffect: '', thoughtProcess: '' },
  safety: { suicideRisk: '', homicideRisk: '', observationLevel: '' },
  behavior: { agitation: '', cooperation: '', sleepPattern: '' },
  medications: { psychMeds: '', prnsGiven: '', sideEffects: '' },
  substanceUse: { withdrawalRisk: '', ciwaCows: '' },
  therapy: { groupParticipation: '', oneOnOneTherapy: '' },
  familySocial: { supportSystem: '', housingResources: '' },
  plan: { psychGoals: '', dischargeCriteria: '', followUpNeeds: '' },
  notes: { notesField: '', escalations: '' },
  otherNotes: ''
};

const emptyOBReport: OBReportData = {
  patientOverview: { age: '', gP: '', gestationalAge: '', postpartumDay: '', admitDx: '', codeStatus: '', allergies: '' },
  patientPlanPreferences: { infantSexDisclosure: '', feedingPreference: '', bathTiming: '', vaccinesNewborn: '', circumcisionPreference: '', roomingInPreference: '', culturalSpecialRequests: '' },
  vitals: { bp: '', hr: '', rr: '', temp: '', spo2: '', painScore: '' },
  maternalAssessment: { fundus: '', lochia: '', perineumIncisionStatus: '', edema: '' },
  postpartumStatus: { deliveryType: '', complications: '', activityLevel: '', lastCervicalExam: '', membraneStatus: '' },
  breastfeedingLactation: { breastfeeding: '', latchQuality: '', pumping: '', lactationConsult: '' },
  giGu: { voidingStatus: '', flatus: '', bowelFunction: '' },
  medications: { scheduledMeds: '', prnsGiven: '', magnesiumOxytocin: '' },
  labs: { hgbHct: '', bloodTypeRh: '', gbsStatus: '' },
  infantStatus: { infantLocation: '', feedingMethod: '', safetyBandsVerified: '' },
  educationSafety: { postpartumEducationCompleted: '', safeSleepReviewed: '', warningSignsReviewed: '' },
  psychosocial: { moodAffect: '', supportPersonPresent: '' },
  dischargePlanning: { dischargeDate: '', followUpAppointments: '', homeNeeds: '' },
  additionalNotes: { events: '', providerUpdates: '' },
  otherNotes: ''
};

// ============================================
// Shared Components (defined outside to prevent re-creation)
// ============================================
interface SectionHeaderProps {
  icon: React.ElementType;
  title: string;
  isExpanded: boolean;
  onToggle: () => void;
  headerBgColor: string;
  borderColor: string;
  labelColor: string;
}

const SectionHeader: React.FC<SectionHeaderProps> = ({ 
  icon: Icon, title, isExpanded, onToggle, headerBgColor, borderColor, labelColor 
}) => (
  <div className="report-section-header" onClick={onToggle}
    style={{ backgroundColor: headerBgColor, borderColor: borderColor, cursor: 'pointer' }}>
    <div className="report-section-title">
      <Icon size={20} color="#3b82f6" />
      <span style={{ color: '#3b82f6', fontWeight: 600 }}>{title}</span>
    </div>
    {isExpanded ? <ChevronUp size={20} color={labelColor} /> : <ChevronDown size={20} color={labelColor} />}
  </div>
);

interface InputFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  cardBgColor: string;
  borderColor: string;
  textColor: string;
  labelColor: string;
}

const InputField: React.FC<InputFieldProps> = ({ 
  label, value: rawValue, onChange, placeholder = '', cardBgColor, borderColor, textColor, labelColor 
}) => {
  // Ensure value is always a string to prevent undefined errors
  const value = rawValue ?? '';
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);
  
  // Check if text overflows the input
  const isOverflowing = inputRef.current 
    ? inputRef.current.scrollWidth > inputRef.current.clientWidth 
    : value.length > 30;

  // Show tooltip on hover if text overflows and not focused
  const showTooltip = isHovered && isOverflowing && !isFocused && value.trim().length > 0;

  // Auto-resize textarea when focused
  const prevFocusedRef = React.useRef(false);
  React.useEffect(() => {
    if (isFocused && textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 150) + 'px';
      // Only focus and move cursor to end when first becoming focused
      if (!prevFocusedRef.current) {
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(value.length, value.length);
      }
    }
    prevFocusedRef.current = isFocused;
  }, [isFocused, value]);

  // Handle blur with ICD-10 and medical abbreviation translation
  const handleBlur = async () => {
    setIsFocused(false);
    if (value.trim()) {
      setIsTranslating(true);
      const translatedText = await translateMedicalTerms(value);
      if (translatedText !== value) {
        onChange(translatedText);
      }
      setIsTranslating(false);
    }
  };

  return (
    <div 
      className="report-input-group"
      style={{ position: 'relative' }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <label style={{ color: labelColor }}>{label}:{isTranslating && <span style={{ marginLeft: '8px', fontSize: '11px', color: '#3b82f6' }}>translating...</span>}</label>
      {isFocused ? (
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={handleBlur}
          placeholder={placeholder}
          className="report-input report-input-expanded"
          style={{ 
            backgroundColor: cardBgColor, 
            borderColor: '#3b82f6', 
            color: textColor,
            minHeight: '36px',
            maxHeight: '150px',
            resize: 'none',
            overflow: 'auto',
            lineHeight: '1.4',
            padding: '8px 10px',
            boxShadow: '0 0 0 2px rgba(59, 130, 246, 0.3)',
          }}
        />
      ) : (
        <input 
          ref={inputRef}
          type="text" 
          value={value} 
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setIsFocused(true)}
          placeholder={placeholder}
          className="report-input" 
          style={{ 
            backgroundColor: cardBgColor, 
            borderColor: borderColor, 
            color: textColor,
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
          }} 
        />
      )}
      {showTooltip && (
        <div 
          className="report-input-tooltip"
          style={{
            position: 'absolute',
            bottom: '100%',
            left: '0',
            right: '0',
            marginBottom: '4px',
            padding: '8px 12px',
            backgroundColor: '#1e293b',
            color: '#f1f5f9',
            borderRadius: '6px',
            fontSize: '13px',
            lineHeight: '1.4',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
            zIndex: 1000,
            maxHeight: '120px',
            overflow: 'auto',
            wordBreak: 'break-word',
            whiteSpace: 'pre-wrap',
          }}
        >
          {value}
          <div 
            style={{
              position: 'absolute',
              bottom: '-6px',
              left: '20px',
              width: '0',
              height: '0',
              borderLeft: '6px solid transparent',
              borderRight: '6px solid transparent',
              borderTop: '6px solid #1e293b',
            }}
          />
        </div>
      )}
    </div>
  );
};

const formatDateInput = (value: string): string => {
  const digits = value.replace(/\D/g, '');
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)}`;
};

const DateInputField: React.FC<InputFieldProps> = ({ 
  label, value, onChange, placeholder = 'MM/DD/YYYY', cardBgColor, borderColor, textColor, labelColor 
}) => (
  <div className="report-input-group">
    <label style={{ color: labelColor }}>{label}:</label>
    <input 
      type="text" 
      value={value} 
      onChange={(e) => onChange(formatDateInput(e.target.value))} 
      placeholder={placeholder}
      maxLength={10}
      className="report-input" 
      style={{ backgroundColor: cardBgColor, borderColor: borderColor, color: textColor }} 
    />
  </div>
);

// ============================================
// Med-Surg Report Component
// ============================================
const MedSurgReport: React.FC<{
  patientData: Patient;
  savedReport: MedSurgReportData | undefined;
  onSaveReport: (mrn: string, report: MedSurgReportData) => void;
  loggedInUser: { first_name: string; last_name: string; email: string } | null;
  onReportSubmitted?: () => void;
}> = ({ patientData, savedReport, onSaveReport, loggedInUser, onReportSubmitted }) => {
  const { theme } = useTheme();
  // Merge savedReport with empty template to ensure all nested objects exist
  const [report, setReport] = useState<MedSurgReportData>(() => savedReport ? {
    ...emptyMedSurgReport,
    ...savedReport,
    patientOverview: { ...emptyMedSurgReport.patientOverview, ...savedReport.patientOverview },
    vitalsOxygen: { ...emptyMedSurgReport.vitalsOxygen, ...savedReport.vitalsOxygen },
    medications: { ...emptyMedSurgReport.medications, ...savedReport.medications },
    mobilitySkin: { ...emptyMedSurgReport.mobilitySkin, ...savedReport.mobilitySkin },
    painManagement: { ...emptyMedSurgReport.painManagement, ...savedReport.painManagement },
    labsTests: { ...emptyMedSurgReport.labsTests, ...savedReport.labsTests },
    dischargePlanning: { ...emptyMedSurgReport.dischargePlanning, ...savedReport.dischargePlanning },
    otherNotes: savedReport.otherNotes || '',
  } : emptyMedSurgReport);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    patientOverview: true, vitalsOxygen: true, medications: true,
    mobilitySkin: true, painManagement: true, labsTests: true, dischargePlanning: true, otherNotes: true
  });
  const [saveStatus, setSaveStatus] = useState<string>('');
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [previousSubmitter, setPreviousSubmitter] = useState<{ name: string; timestamp: string } | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Fetch the latest saved report from the database when patient changes
  // Only fetch from DB if we don't already have savedReport in parent state (prevents re-fetch on tab switch)
  useEffect(() => {
    const fetchSavedReport = async () => {
      if (!patientData?.MRN) {
        setIsLoading(false);
        return;
      }
      
      // If we already have savedReport from parent, use it and skip DB fetch
      // This preserves activity-synced updates and prevents overwriting on tab switch
      if (savedReport) {
        setReport({
          ...emptyMedSurgReport,
          ...savedReport,
          patientOverview: { ...emptyMedSurgReport.patientOverview, ...savedReport.patientOverview },
          vitalsOxygen: { ...emptyMedSurgReport.vitalsOxygen, ...savedReport.vitalsOxygen },
          medications: { ...emptyMedSurgReport.medications, ...savedReport.medications },
          mobilitySkin: { ...emptyMedSurgReport.mobilitySkin, ...savedReport.mobilitySkin },
          painManagement: { ...emptyMedSurgReport.painManagement, ...savedReport.painManagement },
          labsTests: { ...emptyMedSurgReport.labsTests, ...savedReport.labsTests },
          dischargePlanning: { ...emptyMedSurgReport.dischargePlanning, ...savedReport.dischargePlanning },
        });
        setIsLoading(false);
        // Still fetch metadata (previous submitter) but don't overwrite report data
        try {
          const response = await axios.get<{
            success: boolean;
            report?: { submitted_by: string; timestamp: string; };
          }>(`${BACKEND_URL}/report/medsurg/${patientData.MRN}`);
          if (response.data.success && response.data.report) {
            setPreviousSubmitter({
              name: response.data.report.submitted_by,
              timestamp: response.data.report.timestamp
            });
          }
        } catch { /* ignore metadata fetch errors */ }
        return;
      }
      
      setIsLoading(true);
      try {
        const response = await axios.get<{
          success: boolean;
          report?: {
            submitted_by: string;
            timestamp: string;
            report_data: MedSurgReportData;
          };
        }>(`${BACKEND_URL}/report/medsurg/${patientData.MRN}`);
        if (response.data.success && response.data.report) {
          const dbReport = response.data.report;
          // Set the report data from database
          setReport(dbReport.report_data as MedSurgReportData);
          // Also update parent state
          onSaveReport(patientData.MRN, dbReport.report_data as MedSurgReportData);
          // Set the previous submitter info
          setPreviousSubmitter({
            name: dbReport.submitted_by,
            timestamp: dbReport.timestamp
          });
        } else {
          // No saved report found, use empty
          setReport(emptyMedSurgReport);
          setPreviousSubmitter(null);
        }
      } catch (error) {
        // No report found or error - use empty
        setReport(emptyMedSurgReport);
        setPreviousSubmitter(null);
      }
      setIsLoading(false);
    };

    fetchSavedReport();
  }, [patientData?.MRN, savedReport]);

  // Update from local savedReport if it changes (e.g., from activity sync)
  useEffect(() => {
    if (savedReport) {
      setReport({
        ...emptyMedSurgReport,
        ...savedReport,
        patientOverview: { ...emptyMedSurgReport.patientOverview, ...savedReport.patientOverview },
        vitalsOxygen: { ...emptyMedSurgReport.vitalsOxygen, ...savedReport.vitalsOxygen },
        medications: { ...emptyMedSurgReport.medications, ...savedReport.medications },
        mobilitySkin: { ...emptyMedSurgReport.mobilitySkin, ...savedReport.mobilitySkin },
        painManagement: { ...emptyMedSurgReport.painManagement, ...savedReport.painManagement },
        labsTests: { ...emptyMedSurgReport.labsTests, ...savedReport.labsTests },
        dischargePlanning: { ...emptyMedSurgReport.dischargePlanning, ...savedReport.dischargePlanning },
      });
    }
  }, [savedReport]);

  const cardBgColor = theme === 'dark' ? '#1e293b' : '#ffffff';
  const borderColor = theme === 'dark' ? '#334155' : '#e2e8f0';
  const headerBgColor = theme === 'dark' ? '#0f172a' : '#f1f5f9';
  const textColor = theme === 'dark' ? '#f1f5f9' : '#1e293b';
  const labelColor = theme === 'dark' ? '#94a3b8' : '#64748b';

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const handleInputChange = (section: keyof MedSurgReportData, field: string, value: string) => {
    setReport(prev => {
      let newReport: MedSurgReportData;
      if (section === 'otherNotes') {
        // otherNotes is a string field, not an object
        newReport = { ...prev, otherNotes: value };
      } else {
        newReport = {
          ...prev,
          [section]: { ...(prev[section] as Record<string, string>), [field]: value }
        };
      }
      // Auto-save locally when field changes (for tab switching)
      onSaveReport(patientData.MRN, newReport);
      return newReport;
    });
  };

  const handleSave = async () => {
    // Save to local state first
    onSaveReport(patientData.MRN, report);
    
    // Then save to database
    if (loggedInUser) {
      setIsSaving(true);
      try {
        const submittedBy = `${loggedInUser.first_name} ${loggedInUser.last_name}`;
        await axios.post(`${BACKEND_URL}/report/medsurg/save`, {
          patient_name: patientData.Name,
          mrn: patientData.MRN,
          submitted_by: submittedBy,
          email: loggedInUser.email,
          report_data: report
        });
        // Update the previous submitter to show current user
        setPreviousSubmitter({
          name: submittedBy,
          timestamp: new Date().toISOString()
        });
        setSaveStatus('Report saved successfully!');
        // Notify parent that report was submitted
        onReportSubmitted?.();
      } catch (error) {
        console.error('Error saving report to database:', error);
        setSaveStatus('Saved locally (database error)');
      }
      setIsSaving(false);
    } else {
      setSaveStatus('Report saved locally!');
    }
    setTimeout(() => setSaveStatus(''), 3000);
  };

  const styleProps = { cardBgColor, borderColor, textColor, labelColor };

  // Format timestamp for display
  const formatTimestamp = (timestamp: string) => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return timestamp;
    }
  };

  if (isLoading) {
    return (
      <div className="patient-report-container" style={{ backgroundColor: cardBgColor, padding: '40px', textAlign: 'center' }}>
        <div style={{ color: labelColor }}>Loading report...</div>
      </div>
    );
  }

  return (
    <>
      <div className="report-header-container" style={{ 
        backgroundColor: '#3b82f6', 
        borderRadius: '12px', 
        padding: '20px 24px',
        marginBottom: '20px'
      }}>
        <h2 style={{ color: 'white', margin: 0 }}>
          Report For: {patientData.Name} | {patientData.MRN} | Room {patientData.Room || 'N/A'}
        </h2>
        {previousSubmitter && (
          <p style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '14px', margin: '8px 0 0 0' }}>
            Previous report by: <strong>{previousSubmitter.name}</strong> • {formatTimestamp(previousSubmitter.timestamp)}
          </p>
        )}
        {!previousSubmitter && (
          <p style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '14px', margin: '8px 0 0 0' }}>
            No previous report found for this patient
          </p>
        )}
      </div>
      <div className="patient-report-container" style={{ backgroundColor: cardBgColor }}>
        {/* Patient Overview */}
      <div className="report-section" style={{ borderColor: borderColor }}>
        <SectionHeader icon={Info} title="Patient Overview" isExpanded={expandedSections.patientOverview} 
          onToggle={() => toggleSection('patientOverview')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
        {expandedSections.patientOverview && (
          <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Admit Dx" value={report.patientOverview.admitDx || patientData.Diagnosis} onChange={(v) => handleInputChange('patientOverview', 'admitDx', v)} placeholder="e.g., Pneumonia" {...styleProps} />
            <InputField label="Code Status" value={report.patientOverview.codeStatus} onChange={(v) => handleInputChange('patientOverview', 'codeStatus', v)} placeholder="e.g., Full Code" {...styleProps} />
            <InputField label="Allergies" value={report.patientOverview.allergies} onChange={(v) => handleInputChange('patientOverview', 'allergies', v)} placeholder="e.g., Penicillin" {...styleProps} />
          </div>
        )}
      </div>

      {/* Vitals & Oxygen */}
      <div className="report-section" style={{ borderColor: borderColor }}>
        <SectionHeader icon={Heart} title="Vitals & Oxygen" isExpanded={expandedSections.vitalsOxygen} 
          onToggle={() => toggleSection('vitalsOxygen')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
        {expandedSections.vitalsOxygen && (
          <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="BP" value={report.vitalsOxygen.bp} onChange={(v) => handleInputChange('vitalsOxygen', 'bp', v)} placeholder="e.g., 132/78" {...styleProps} />
            <InputField label="HR" value={report.vitalsOxygen.hr} onChange={(v) => handleInputChange('vitalsOxygen', 'hr', v)} placeholder="e.g., 88" {...styleProps} />
            <InputField label="Temp" value={report.vitalsOxygen.temp} onChange={(v) => handleInputChange('vitalsOxygen', 'temp', v)} placeholder="e.g., 37.2°C" {...styleProps} />
            <InputField label="Oxygen" value={report.vitalsOxygen.oxygen} onChange={(v) => handleInputChange('vitalsOxygen', 'oxygen', v)} placeholder="e.g., 2L NC" {...styleProps} />
          </div>
        )}
      </div>

      {/* Medications */}
      <div className="report-section" style={{ borderColor: borderColor }}>
        <SectionHeader icon={Pill} title="Medications" isExpanded={expandedSections.medications} 
          onToggle={() => toggleSection('medications')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
        {expandedSections.medications && (
          <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Scheduled Meds" value={report.medications.scheduledMeds} onChange={(v) => handleInputChange('medications', 'scheduledMeds', v)} placeholder="e.g., Lisinopril, Metoprolol" {...styleProps} />
            <InputField label="PRN Meds Given" value={report.medications.prnMedsGiven} onChange={(v) => handleInputChange('medications', 'prnMedsGiven', v)} placeholder="e.g., Morphine 2mg - effective" {...styleProps} />
          </div>
        )}
      </div>

      {/* Mobility & Skin */}
      <div className="report-section" style={{ borderColor: borderColor }}>
        <SectionHeader icon={Activity} title="Mobility & Skin" isExpanded={expandedSections.mobilitySkin} 
          onToggle={() => toggleSection('mobilitySkin')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
        {expandedSections.mobilitySkin && (
          <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Activity" value={report.mobilitySkin.activity} onChange={(v) => handleInputChange('mobilitySkin', 'activity', v)} placeholder="e.g., Needs assist to ambulate" {...styleProps} />
            <InputField label="Skin" value={report.mobilitySkin.skin} onChange={(v) => handleInputChange('mobilitySkin', 'skin', v)} placeholder="e.g., Sacral dressing, no new wounds" {...styleProps} />
          </div>
        )}
      </div>

      {/* Pain Management */}
      <div className="report-section" style={{ borderColor: borderColor }}>
        <SectionHeader icon={Thermometer} title="Pain Management" isExpanded={expandedSections.painManagement} 
          onToggle={() => toggleSection('painManagement')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
        {expandedSections.painManagement && (
          <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Pain Score" value={report.painManagement.painScore} onChange={(v) => handleInputChange('painManagement', 'painScore', v)} placeholder="e.g., 5/10" {...styleProps} />
            <InputField label="Pain Control" value={report.painManagement.painControl} onChange={(v) => handleInputChange('painManagement', 'painControl', v)} placeholder="e.g., Improved after morphine" {...styleProps} />
          </div>
        )}
      </div>

      {/* Labs & Tests */}
      <div className="report-section" style={{ borderColor: borderColor }}>
        <SectionHeader icon={TestTube} title="Labs & Tests" isExpanded={expandedSections.labsTests} 
          onToggle={() => toggleSection('labsTests')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
        {expandedSections.labsTests && (
          <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Latest Labs" value={report.labsTests.latestLabs} onChange={(v) => handleInputChange('labsTests', 'latestLabs', v)} placeholder="e.g., WBC 14.2 | CXR pending" {...styleProps} />
          </div>
        )}
      </div>

      {/* Discharge Planning */}
      <div className="report-section" style={{ borderColor: borderColor }}>
        <SectionHeader icon={Home} title="Discharge Planning" isExpanded={expandedSections.dischargePlanning} 
          onToggle={() => toggleSection('dischargePlanning')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
        {expandedSections.dischargePlanning && (
          <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Discharge Goal" value={report.dischargePlanning.dischargeGoal} onChange={(v) => handleInputChange('dischargePlanning', 'dischargeGoal', v)} placeholder="e.g., Possibly tomorrow" {...styleProps} />
            <InputField label="Needs" value={report.dischargePlanning.needs} onChange={(v) => handleInputChange('dischargePlanning', 'needs', v)} placeholder="e.g., PT consult, Home O2 setup" {...styleProps} />
          </div>
        )}
      </div>

      {/* Other Notes */}
      <div className="report-section" style={{ borderColor: borderColor }}>
        <SectionHeader icon={FileText} title="Other Notes" isExpanded={expandedSections.otherNotes} 
          onToggle={() => toggleSection('otherNotes')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
        {expandedSections.otherNotes && (
          <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <div className="report-field">
              <label style={{ color: labelColor }}>Additional Information</label>
              <textarea
                className="report-other-notes-textarea"
                value={report.otherNotes}
                onChange={(e) => handleInputChange('otherNotes', '', e.target.value)}
                placeholder="Any other information not captured in the fields above..."
                style={{ 
                  width: '100%', 
                  minHeight: '100px', 
                  padding: '8px 12px',
                  borderRadius: '6px',
                  color: textColor,
                  resize: 'vertical'
                }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Save Button */}
      <div className="report-save-container" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <button className="report-save-btn" onClick={handleSave}>Save Report</button>
        {saveStatus && <div className="report-save-status" style={{ color: saveStatus.includes('Error') ? '#ef4444' : '#10b981', marginTop: '8px' }}>{saveStatus}</div>}
      </div>
    </div>
    </>
  );
};

// ============================================
// ICU Report Component
// ============================================
const ICUReport: React.FC<{
  patientData: Patient;
  savedReport: ICUReportData | undefined;
  onSaveReport: (mrn: string, report: ICUReportData) => void;
  loggedInUser: { first_name: string; last_name: string; email: string } | null;
  onReportSubmitted?: () => void;
}> = ({ patientData, savedReport, onSaveReport, loggedInUser, onReportSubmitted }) => {
  const { theme } = useTheme();
  
  // Helper to merge loaded report with empty template to ensure all sections exist
  const mergeWithEmptyReport = (loadedReport: Partial<ICUReportData> | null | undefined): ICUReportData => {
    if (!loadedReport) return emptyICUReport;
    return {
      patientInfo: { ...emptyICUReport.patientInfo, ...loadedReport.patientInfo },
      icuVitals: { ...emptyICUReport.icuVitals, ...loadedReport.icuVitals },
      continuousInfusions: { ...emptyICUReport.continuousInfusions, ...loadedReport.continuousInfusions },
      hemodynamics: { ...emptyICUReport.hemodynamics, ...loadedReport.hemodynamics },
      neuro: { ...emptyICUReport.neuro, ...loadedReport.neuro },
      respiratory: { ...emptyICUReport.respiratory, ...loadedReport.respiratory },
      linesTubes: { ...emptyICUReport.linesTubes, ...loadedReport.linesTubes },
      labsAbgs: { ...emptyICUReport.labsAbgs, ...loadedReport.labsAbgs },
      planGoals: { ...emptyICUReport.planGoals, ...loadedReport.planGoals },
      additionalNotes: { ...emptyICUReport.additionalNotes, ...loadedReport.additionalNotes },
      otherNotes: loadedReport.otherNotes || '',
    };
  };

  const [report, setReport] = useState<ICUReportData>(() => mergeWithEmptyReport(savedReport));
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    patientInfo: true, icuVitals: true, continuousInfusions: true, hemodynamics: true,
    neuro: true, respiratory: true, linesTubes: true, labsAbgs: true, planGoals: true, additionalNotes: true, otherNotes: true
  });
  const [saveStatus, setSaveStatus] = useState<string>('');
  const [previousReport, setPreviousReport] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Load previous report when patient changes
  // Skip DB fetch if we already have savedReport in parent state
  useEffect(() => {
    if (!patientData?.MRN) return;
    
    // If we already have savedReport from parent, use it and skip DB fetch
    if (savedReport) {
      setReport(mergeWithEmptyReport(savedReport));
      // Still fetch metadata but don't overwrite report data
      fetch(`${BACKEND_URL}/report/icu/${patientData.MRN}`)
        .then(response => response.json())
        .then(data => {
          if (data.success && data.found && data.report) {
            setPreviousReport(data.report);
          }
        })
        .catch(() => {});
      return;
    }
    
    setIsLoading(true);
    fetch(`${BACKEND_URL}/report/icu/${patientData.MRN}`)
      .then(response => response.json())
      .then(data => {
        if (data.success && data.found && data.report) {
          setPreviousReport(data.report);
          setReport(mergeWithEmptyReport(data.report.report_data));
        } else {
          setPreviousReport(null);
          setReport(emptyICUReport);
        }
      })
      .catch(error => {
        console.error('Error loading ICU report:', error);
        setPreviousReport(null);
        setReport(emptyICUReport);
      })
      .finally(() => setIsLoading(false));
  }, [patientData?.MRN, savedReport]);

  // Update from local savedReport if it changes (e.g., from activity sync)
  useEffect(() => {
    if (savedReport) {
      setReport(mergeWithEmptyReport(savedReport));
    }
  }, [savedReport]);

  const cardBgColor = theme === 'dark' ? '#1e293b' : '#ffffff';
  const borderColor = theme === 'dark' ? '#334155' : '#e2e8f0';
  const headerBgColor = theme === 'dark' ? '#0f172a' : '#f1f5f9';
  const textColor = theme === 'dark' ? '#f1f5f9' : '#1e293b';
  const labelColor = theme === 'dark' ? '#94a3b8' : '#64748b';

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const handleInputChange = (section: keyof ICUReportData, field: string, value: string) => {
    setReport(prev => {
      let newReport;
      if (section === 'otherNotes') {
        newReport = { ...prev, otherNotes: value };
      } else {
        newReport = {
          ...prev,
          [section]: { ...(prev[section] as Record<string, string>), [field]: value }
        };
      }
      // Auto-save locally when field changes (for tab switching)
      onSaveReport(patientData.MRN, newReport);
      return newReport;
    });
  };

  const handleSave = async () => {
    if (!loggedInUser) {
      setSaveStatus('Error: User not logged in');
      setTimeout(() => setSaveStatus(''), 3000);
      return;
    }

    setSaveStatus('Saving...');
    
    try {
      const response = await fetch('http://localhost:8080/report/icu/save', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          patient_name: patientData.Name,
          mrn: patientData.MRN,
          submitted_by: `${loggedInUser.first_name} ${loggedInUser.last_name}`,
          email: loggedInUser.email,
          report_data: report
        })
      });

      const data = await response.json();
      
      if (data.success) {
        onSaveReport(patientData.MRN, report);
        setSaveStatus('Report saved successfully!');
        if (onReportSubmitted) onReportSubmitted();
        
        // Reload the report to show updated submission info
        const updatedResponse = await fetch(`http://localhost:8080/report/icu/${patientData.MRN}`);
        const updatedData = await updatedResponse.json();
        if (updatedData.success && updatedData.found && updatedData.report) {
          setPreviousReport(updatedData.report);
        }
      } else {
        setSaveStatus(`Error: ${data.message}`);
      }
    } catch (error) {
      console.error('Error saving ICU report:', error);
      setSaveStatus('Error saving report');
    }
    
    setTimeout(() => setSaveStatus(''), 3000);
  };

  const styleProps = { cardBgColor, borderColor, textColor, labelColor };

  const formatTimestamp = (timestamp: string) => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
    } catch { return timestamp; }
  };

  return (
    <>
      <div className="report-header-container" style={{ 
        backgroundColor: '#3b82f6', 
        borderRadius: '12px', 
        padding: '20px 24px',
        marginBottom: '20px'
      }}>
        <h2 style={{ color: 'white', margin: 0 }}>
          Report For: {patientData.Name} | {patientData.MRN} | Room {patientData.Room || 'N/A'}
        </h2>
        {isLoading && (
          <p style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '14px', margin: '8px 0 0 0' }}>
            Loading previous report...
          </p>
        )}
        {!isLoading && previousReport && (
          <p style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '14px', margin: '8px 0 0 0' }}>
            Previous report by: <strong>{previousReport.submitted_by}</strong> • {formatTimestamp(previousReport.timestamp)}
          </p>
        )}
        {!isLoading && !previousReport && (
          <p style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '14px', margin: '8px 0 0 0' }}>
            No previous report found for this patient
          </p>
        )}
      </div>
      <div className="patient-report-container" style={{ backgroundColor: cardBgColor }}>
      {/* Patient Info */}
      <div className="report-section" style={{ borderColor: borderColor }}>
        <SectionHeader icon={Info} title="Patient Info" isExpanded={expandedSections.patientInfo} 
          onToggle={() => toggleSection('patientInfo')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
        {expandedSections.patientInfo && (
          <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Admit Dx" value={report.patientInfo.admitDx || patientData.Diagnosis} onChange={(v) => handleInputChange('patientInfo', 'admitDx', v)} placeholder="e.g., Septic shock" {...styleProps} />
            <InputField label="Allergies" value={report.patientInfo.allergies} onChange={(v) => handleInputChange('patientInfo', 'allergies', v)} placeholder="e.g., NKDA" {...styleProps} />
            <InputField label="Code Status" value={report.patientInfo.codeStatus} onChange={(v) => handleInputChange('patientInfo', 'codeStatus', v)} placeholder="e.g., Full Code" {...styleProps} />
          </div>
        )}
      </div>

      {/* ICU-Specific Vitals */}
      <div className="report-section" style={{ borderColor: borderColor }}>
        <SectionHeader icon={Wind} title="ICU-Specific Vitals" isExpanded={expandedSections.icuVitals} 
          onToggle={() => toggleSection('icuVitals')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
        {expandedSections.icuVitals && (
          <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Vent Mode/Settings" value={report.icuVitals.ventMode} onChange={(v) => handleInputChange('icuVitals', 'ventMode', v)} placeholder="e.g., AC/VC 450x14" {...styleProps} />
            <InputField label="FiO2" value={report.icuVitals.fio2} onChange={(v) => handleInputChange('icuVitals', 'fio2', v)} placeholder="e.g., 40%" {...styleProps} />
            <InputField label="PEEP" value={report.icuVitals.peep} onChange={(v) => handleInputChange('icuVitals', 'peep', v)} placeholder="e.g., 8" {...styleProps} />
            <InputField label="Tidal Vol" value={report.icuVitals.tidalVol} onChange={(v) => handleInputChange('icuVitals', 'tidalVol', v)} placeholder="e.g., 450mL" {...styleProps} />
            <InputField label="RR" value={report.icuVitals.rr} onChange={(v) => handleInputChange('icuVitals', 'rr', v)} placeholder="e.g., 14" {...styleProps} />
          </div>
        )}
      </div>

      {/* Continuous Infusions */}
      <div className="report-section" style={{ borderColor: borderColor }}>
        <SectionHeader icon={Syringe} title="Continuous Infusions" isExpanded={expandedSections.continuousInfusions} 
          onToggle={() => toggleSection('continuousInfusions')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
        {expandedSections.continuousInfusions && (
          <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Vasopressors" value={report.continuousInfusions.vasopressors} onChange={(v) => handleInputChange('continuousInfusions', 'vasopressors', v)} placeholder="e.g., Norepinephrine, Vasopressin" {...styleProps} />
            <InputField label="Sedation/Analgesia" value={report.continuousInfusions.sedationAnalgesia} onChange={(v) => handleInputChange('continuousInfusions', 'sedationAnalgesia', v)} placeholder="e.g., Propofol, Fentanyl" {...styleProps} />
            <InputField label="Dose" value={report.continuousInfusions.dose} onChange={(v) => handleInputChange('continuousInfusions', 'dose', v)} placeholder="e.g., 0.1 mcg/kg/min" {...styleProps} />
            <InputField label="Rate" value={report.continuousInfusions.rate} onChange={(v) => handleInputChange('continuousInfusions', 'rate', v)} placeholder="e.g., 10 mL/hr" {...styleProps} />
            <InputField label="Titration Goals" value={report.continuousInfusions.titrationGoals} onChange={(v) => handleInputChange('continuousInfusions', 'titrationGoals', v)} placeholder="e.g., MAP > 65" {...styleProps} />
          </div>
        )}
      </div>

      {/* Hemodynamics */}
      <div className="report-section" style={{ borderColor: borderColor }}>
        <SectionHeader icon={Heart} title="Hemodynamics" isExpanded={expandedSections.hemodynamics} 
          onToggle={() => toggleSection('hemodynamics')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
        {expandedSections.hemodynamics && (
          <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Arterial Line" value={report.hemodynamics.arterialLine} onChange={(v) => handleInputChange('hemodynamics', 'arterialLine', v)} placeholder="e.g., Right radial" {...styleProps} />
            <InputField label="CVP" value={report.hemodynamics.cvp} onChange={(v) => handleInputChange('hemodynamics', 'cvp', v)} placeholder="e.g., 12 mmHg" {...styleProps} />
            <InputField label="MAP" value={report.hemodynamics.map} onChange={(v) => handleInputChange('hemodynamics', 'map', v)} placeholder="e.g., 72 mmHg" {...styleProps} />
            <InputField label="Cardiac Output" value={report.hemodynamics.cardiacOutput} onChange={(v) => handleInputChange('hemodynamics', 'cardiacOutput', v)} placeholder="e.g., 5.2 L/min" {...styleProps} />
          </div>
        )}
      </div>

      {/* Neuro */}
      <div className="report-section" style={{ borderColor: borderColor }}>
        <SectionHeader icon={Brain} title="Neuro" isExpanded={expandedSections.neuro} 
          onToggle={() => toggleSection('neuro')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
        {expandedSections.neuro && (
          <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Sedation Scale (RASS/GCS)" value={report.neuro.sedationScale} onChange={(v) => handleInputChange('neuro', 'sedationScale', v)} placeholder="e.g., RASS -2, GCS 10T" {...styleProps} />
            <InputField label="Pupils" value={report.neuro.pupils} onChange={(v) => handleInputChange('neuro', 'pupils', v)} placeholder="e.g., PERRL 3mm" {...styleProps} />
            <InputField label="Delirium Assess" value={report.neuro.deliriumAssess} onChange={(v) => handleInputChange('neuro', 'deliriumAssess', v)} placeholder="e.g., CAM-ICU negative" {...styleProps} />
          </div>
        )}
      </div>

      {/* Respiratory */}
      <div className="report-section" style={{ borderColor: borderColor }}>
        <SectionHeader icon={Stethoscope} title="Respiratory" isExpanded={expandedSections.respiratory} 
          onToggle={() => toggleSection('respiratory')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
        {expandedSections.respiratory && (
          <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Airway Type" value={report.respiratory.airwayType} onChange={(v) => handleInputChange('respiratory', 'airwayType', v)} placeholder="e.g., ETT 7.5, 22cm at lip" {...styleProps} />
            <InputField label="Secretions" value={report.respiratory.secretions} onChange={(v) => handleInputChange('respiratory', 'secretions', v)} placeholder="e.g., Moderate, tan" {...styleProps} />
            <InputField label="Lung Sounds" value={report.respiratory.lungSounds} onChange={(v) => handleInputChange('respiratory', 'lungSounds', v)} placeholder="e.g., Diminished bases bilat" {...styleProps} />
          </div>
        )}
      </div>

      {/* Lines/Tubes */}
      <div className="report-section" style={{ borderColor: borderColor }}>
        <SectionHeader icon={FileText} title="Lines/Tubes" isExpanded={expandedSections.linesTubes} 
          onToggle={() => toggleSection('linesTubes')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
        {expandedSections.linesTubes && (
          <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Central/Arterial" value={report.linesTubes.centralArterial} onChange={(v) => handleInputChange('linesTubes', 'centralArterial', v)} placeholder="e.g., R IJ triple lumen, R radial art" {...styleProps} />
            <InputField label="Chest Tubes" value={report.linesTubes.chestTubes} onChange={(v) => handleInputChange('linesTubes', 'chestTubes', v)} placeholder="e.g., None" {...styleProps} />
            <InputField label="Dialysis Catheter" value={report.linesTubes.dialysisCatheter} onChange={(v) => handleInputChange('linesTubes', 'dialysisCatheter', v)} placeholder="e.g., R femoral Quinton" {...styleProps} />
          </div>
        )}
      </div>

      {/* Labs + ABGs */}
      <div className="report-section" style={{ borderColor: borderColor }}>
        <SectionHeader icon={TestTube} title="Labs + ABGs" isExpanded={expandedSections.labsAbgs} 
          onToggle={() => toggleSection('labsAbgs')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
        {expandedSections.labsAbgs && (
          <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Latest ABG" value={report.labsAbgs.latestAbg} onChange={(v) => handleInputChange('labsAbgs', 'latestAbg', v)} placeholder="e.g., 7.35/42/85/24" {...styleProps} />
            <InputField label="Electrolyte Trends" value={report.labsAbgs.electrolyteTrends} onChange={(v) => handleInputChange('labsAbgs', 'electrolyteTrends', v)} placeholder="e.g., K 3.8, Na 138" {...styleProps} />
            <InputField label="Lactate" value={report.labsAbgs.lactate} onChange={(v) => handleInputChange('labsAbgs', 'lactate', v)} placeholder="e.g., 1.8 (down from 4.2)" {...styleProps} />
          </div>
        )}
      </div>

      {/* Plan/Goals */}
      <div className="report-section" style={{ borderColor: borderColor }}>
        <SectionHeader icon={Target} title="Plan/Goals" isExpanded={expandedSections.planGoals} 
          onToggle={() => toggleSection('planGoals')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
        {expandedSections.planGoals && (
          <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Vent Weaning Plan" value={report.planGoals.ventWeaningPlan} onChange={(v) => handleInputChange('planGoals', 'ventWeaningPlan', v)} placeholder="e.g., SBT in AM if stable" {...styleProps} />
            <InputField label="Pressor Wean" value={report.planGoals.pressorWean} onChange={(v) => handleInputChange('planGoals', 'pressorWean', v)} placeholder="e.g., Wean norepi for MAP > 65" {...styleProps} />
            <InputField label="Daily Goals" value={report.planGoals.dailyGoals} onChange={(v) => handleInputChange('planGoals', 'dailyGoals', v)} placeholder="e.g., Mobilize, PT eval" {...styleProps} />
          </div>
        )}
      </div>

      {/* Additional Notes */}
      <div className="report-section" style={{ borderColor: borderColor }}>
        <SectionHeader icon={MessageSquare} title="Additional Notes" isExpanded={expandedSections.additionalNotes} 
          onToggle={() => toggleSection('additionalNotes')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
        {expandedSections.additionalNotes && (
          <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Family/Consults" value={report.additionalNotes.familyConsults} onChange={(v) => handleInputChange('additionalNotes', 'familyConsults', v)} placeholder="e.g., Family updated, palliative consult pending" {...styleProps} />
            <InputField label="Pending Studies" value={report.additionalNotes.pendingStudies} onChange={(v) => handleInputChange('additionalNotes', 'pendingStudies', v)} placeholder="e.g., Echo, CT chest" {...styleProps} />
          </div>
        )}
      </div>

      {/* Other Notes */}
      <div className="report-section" style={{ borderColor: borderColor }}>
        <SectionHeader icon={FileText} title="Other Notes" isExpanded={expandedSections.otherNotes} 
          onToggle={() => toggleSection('otherNotes')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
        {expandedSections.otherNotes && (
          <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <div className="report-field">
              <label style={{ color: labelColor }}>Additional Information</label>
              <textarea
                value={report.otherNotes}
                onChange={(e) => handleInputChange('otherNotes', '', e.target.value)}
                placeholder="Any other information not captured in the fields above..."
                className="report-other-notes-textarea" style={{ width: '100%', minHeight: '100px', padding: '8px 12px', borderRadius: '6px', color: textColor, resize: 'vertical' }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Save Button */}
      <div className="report-save-container" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <button className="report-save-btn" onClick={handleSave}>Save Report</button>
        {saveStatus && <div className="report-save-status" style={{ color: saveStatus.includes('Error') ? '#ef4444' : '#10b981', marginTop: '8px' }}>{saveStatus}</div>}
      </div>
    </div>
    </>
  );
};

// ============================================
// PACU Report Component
// ============================================
const PACUReport: React.FC<{
  patientData: Patient;
  savedReport: PACUReportData | undefined;
  onSaveReport: (mrn: string, report: PACUReportData) => void;
  loggedInUser: { first_name: string; last_name: string; email: string } | null;
  onReportSubmitted?: () => void;
}> = ({ patientData, savedReport, onSaveReport, loggedInUser, onReportSubmitted }) => {
  const { theme } = useTheme();
  // Merge savedReport with empty template to ensure all nested objects exist
  const [report, setReport] = useState<PACUReportData>(() => savedReport ? {
    ...emptyPACUReport,
    ...savedReport,
    patientOverview: { ...emptyPACUReport.patientOverview, ...savedReport.patientOverview },
    immediatePostOp: { ...emptyPACUReport.immediatePostOp, ...savedReport.immediatePostOp },
    airwayBreathing: { ...emptyPACUReport.airwayBreathing, ...savedReport.airwayBreathing },
    circulation: { ...emptyPACUReport.circulation, ...savedReport.circulation },
    neurologic: { ...emptyPACUReport.neurologic, ...savedReport.neurologic },
    painComfort: { ...emptyPACUReport.painComfort, ...savedReport.painComfort },
    nauseaGI: { ...emptyPACUReport.nauseaGI, ...savedReport.nauseaGI },
    surgicalSiteDrains: { ...emptyPACUReport.surgicalSiteDrains, ...savedReport.surgicalSiteDrains },
    urinaryStatus: { ...emptyPACUReport.urinaryStatus, ...savedReport.urinaryStatus },
    safetyRecoveryScores: { ...emptyPACUReport.safetyRecoveryScores, ...savedReport.safetyRecoveryScores },
    medicationsGiven: { ...emptyPACUReport.medicationsGiven, ...savedReport.medicationsGiven },
    dischargeReadiness: { ...emptyPACUReport.dischargeReadiness, ...savedReport.dischargeReadiness },
    additionalNotes: { ...emptyPACUReport.additionalNotes, ...savedReport.additionalNotes },
  } : emptyPACUReport);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    patientOverview: true, immediatePostOp: true, airwayBreathing: true, circulation: true,
    neurologic: true, painComfort: true, nauseaGI: true, surgicalSiteDrains: true,
    urinaryStatus: true, safetyRecoveryScores: true, medicationsGiven: true, dischargeReadiness: true, additionalNotes: true, otherNotes: true
  });
  const [saveStatus, setSaveStatus] = useState<string>('');
  const [previousReport, setPreviousReport] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Load previous report when patient changes
  // Skip DB fetch if we already have savedReport in parent state
  useEffect(() => {
    if (!patientData?.MRN) return;
    
    // If we already have savedReport from parent, merge with empty template to ensure all nested objects exist
    if (savedReport) {
      setReport({
        ...emptyPACUReport,
        ...savedReport,
        patientOverview: { ...emptyPACUReport.patientOverview, ...savedReport.patientOverview },
        immediatePostOp: { ...emptyPACUReport.immediatePostOp, ...savedReport.immediatePostOp },
        airwayBreathing: { ...emptyPACUReport.airwayBreathing, ...savedReport.airwayBreathing },
        circulation: { ...emptyPACUReport.circulation, ...savedReport.circulation },
        neurologic: { ...emptyPACUReport.neurologic, ...savedReport.neurologic },
        painComfort: { ...emptyPACUReport.painComfort, ...savedReport.painComfort },
        nauseaGI: { ...emptyPACUReport.nauseaGI, ...savedReport.nauseaGI },
        surgicalSiteDrains: { ...emptyPACUReport.surgicalSiteDrains, ...savedReport.surgicalSiteDrains },
        urinaryStatus: { ...emptyPACUReport.urinaryStatus, ...savedReport.urinaryStatus },
        safetyRecoveryScores: { ...emptyPACUReport.safetyRecoveryScores, ...savedReport.safetyRecoveryScores },
        medicationsGiven: { ...emptyPACUReport.medicationsGiven, ...savedReport.medicationsGiven },
        dischargeReadiness: { ...emptyPACUReport.dischargeReadiness, ...savedReport.dischargeReadiness },
        additionalNotes: { ...emptyPACUReport.additionalNotes, ...savedReport.additionalNotes },
      });
      fetch(`${BACKEND_URL}/report/pacu/${patientData.MRN}`)
        .then(response => response.json())
        .then(data => {
          if (data.success && data.found && data.report) {
            setPreviousReport(data.report);
          }
        })
        .catch(() => {});
      return;
    }
    
    setIsLoading(true);
    fetch(`${BACKEND_URL}/report/pacu/${patientData.MRN}`)
      .then(response => response.json())
      .then(data => {
        if (data.success && data.found && data.report) {
          setPreviousReport(data.report);
          setReport(data.report.report_data || emptyPACUReport);
        } else {
          setPreviousReport(null);
          setReport(emptyPACUReport);
        }
      })
      .catch(error => {
        console.error('Error loading PACU report:', error);
        setPreviousReport(null);
        setReport(emptyPACUReport);
      })
      .finally(() => setIsLoading(false));
  }, [patientData?.MRN, savedReport]);

  // Update from local savedReport if it changes (e.g., from activity sync)
  useEffect(() => {
    if (savedReport) {
      setReport({
        ...emptyPACUReport,
        ...savedReport,
        patientOverview: { ...emptyPACUReport.patientOverview, ...savedReport.patientOverview },
        immediatePostOp: { ...emptyPACUReport.immediatePostOp, ...savedReport.immediatePostOp },
        airwayBreathing: { ...emptyPACUReport.airwayBreathing, ...savedReport.airwayBreathing },
        circulation: { ...emptyPACUReport.circulation, ...savedReport.circulation },
        neurologic: { ...emptyPACUReport.neurologic, ...savedReport.neurologic },
        painComfort: { ...emptyPACUReport.painComfort, ...savedReport.painComfort },
        nauseaGI: { ...emptyPACUReport.nauseaGI, ...savedReport.nauseaGI },
        surgicalSiteDrains: { ...emptyPACUReport.surgicalSiteDrains, ...savedReport.surgicalSiteDrains },
        urinaryStatus: { ...emptyPACUReport.urinaryStatus, ...savedReport.urinaryStatus },
        safetyRecoveryScores: { ...emptyPACUReport.safetyRecoveryScores, ...savedReport.safetyRecoveryScores },
        medicationsGiven: { ...emptyPACUReport.medicationsGiven, ...savedReport.medicationsGiven },
        dischargeReadiness: { ...emptyPACUReport.dischargeReadiness, ...savedReport.dischargeReadiness },
        additionalNotes: { ...emptyPACUReport.additionalNotes, ...savedReport.additionalNotes },
      });
    }
  }, [savedReport]);

  const cardBgColor = theme === 'dark' ? '#1e293b' : '#ffffff';
  const borderColor = theme === 'dark' ? '#334155' : '#e2e8f0';
  const headerBgColor = theme === 'dark' ? '#0f172a' : '#f1f5f9';
  const textColor = theme === 'dark' ? '#f1f5f9' : '#1e293b';
  const labelColor = theme === 'dark' ? '#94a3b8' : '#64748b';

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const handleInputChange = (section: keyof PACUReportData, field: string, value: string) => {
    setReport(prev => {
      let newReport;
      if (section === 'otherNotes') {
        newReport = { ...prev, otherNotes: value };
      } else {
        newReport = {
          ...prev,
          [section]: { ...(prev[section] as Record<string, string>), [field]: value }
        };
      }
      onSaveReport(patientData.MRN, newReport);
      return newReport;
    });
  };

  const handleSave = async () => {
    if (!loggedInUser) {
      setSaveStatus('Error: User not logged in');
      setTimeout(() => setSaveStatus(''), 3000);
      return;
    }

    setSaveStatus('Saving...');
    
    try {
      const response = await fetch('http://localhost:8080/report/pacu/save', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          patient_name: patientData.Name,
          mrn: patientData.MRN,
          submitted_by: `${loggedInUser.first_name} ${loggedInUser.last_name}`,
          email: loggedInUser.email,
          report_data: report
        })
      });

      const data = await response.json();
      
      if (data.success) {
        onSaveReport(patientData.MRN, report);
        setSaveStatus('Report saved successfully!');
        if (onReportSubmitted) onReportSubmitted();
        
        // Reload the report to show updated submission info
        const updatedResponse = await fetch(`http://localhost:8080/report/pacu/${patientData.MRN}`);
        const updatedData = await updatedResponse.json();
        if (updatedData.success && updatedData.found && updatedData.report) {
          setPreviousReport(updatedData.report);
        }
      } else {
        setSaveStatus(`Error: ${data.message}`);
      }
    } catch (error) {
      console.error('Error saving PACU report:', error);
      setSaveStatus('Error saving report');
    }
    
    setTimeout(() => setSaveStatus(''), 3000);
  };

  const styleProps = { cardBgColor, borderColor, textColor, labelColor };

  const formatTimestamp = (timestamp: string) => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
    } catch { return timestamp; }
  };

  return (
    <>
      <div className="report-header-container" style={{ 
        backgroundColor: '#3b82f6', 
        borderRadius: '12px', 
        padding: '20px 24px',
        marginBottom: '20px'
      }}>
        <h2 style={{ color: 'white', margin: 0 }}>
          Report For: {patientData.Name} | {patientData.MRN} | Room {patientData.Room || 'N/A'}
        </h2>
        {isLoading && (
          <p style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '14px', margin: '8px 0 0 0' }}>
            Loading previous report...
          </p>
        )}
        {!isLoading && previousReport && (
          <p style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '14px', margin: '8px 0 0 0' }}>
            Previous report by: <strong>{previousReport.submitted_by}</strong> • {formatTimestamp(previousReport.timestamp)}
          </p>
        )}
        {!isLoading && !previousReport && (
          <p style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '14px', margin: '8px 0 0 0' }}>
            No previous report found for this patient
          </p>
        )}
      </div>
      <div className="patient-report-container" style={{ backgroundColor: cardBgColor }}>
        {/* Patient Overview */}
      <div className="report-section" style={{ borderColor: borderColor }}>
        <SectionHeader icon={Info} title="Patient Overview" isExpanded={expandedSections.patientOverview} 
          onToggle={() => toggleSection('patientOverview')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
        {expandedSections.patientOverview && (
          <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Procedure" value={report.patientOverview.procedure || patientData.Diagnosis} onChange={(v) => handleInputChange('patientOverview', 'procedure', v)} placeholder="e.g., Laparoscopic cholecystectomy" {...styleProps} />
            <InputField label="Surgeon" value={report.patientOverview.surgeon} onChange={(v) => handleInputChange('patientOverview', 'surgeon', v)} placeholder="e.g., Dr. Smith" {...styleProps} />
            <InputField label="Anesthesia Type" value={report.patientOverview.anesthesiaType} onChange={(v) => handleInputChange('patientOverview', 'anesthesiaType', v)} placeholder="e.g., General, Spinal, MAC" {...styleProps} />
            <InputField label="Code Status" value={report.patientOverview.codeStatus} onChange={(v) => handleInputChange('patientOverview', 'codeStatus', v)} placeholder="e.g., Full Code, DNR" {...styleProps} />
          </div>
        )}
      </div>

      {/* Immediate Post-Op Status */}
      <div className="report-section" style={{ borderColor: borderColor }}>
        <SectionHeader icon={Clock} title="Immediate Post-Op Status" isExpanded={expandedSections.immediatePostOp} 
          onToggle={() => toggleSection('immediatePostOp')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
        {expandedSections.immediatePostOp && (
          <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Arrival Time" value={report.immediatePostOp.arrivalTime} onChange={(v) => handleInputChange('immediatePostOp', 'arrivalTime', v)} placeholder="e.g., 14:30" {...styleProps} />
            <InputField label="Phase (I / II)" value={report.immediatePostOp.phase} onChange={(v) => handleInputChange('immediatePostOp', 'phase', v)} placeholder="e.g., Phase I, Phase II" {...styleProps} />
            <InputField label="PACU Bay" value={report.immediatePostOp.pacuBay} onChange={(v) => handleInputChange('immediatePostOp', 'pacuBay', v)} placeholder="e.g., Bay 3" {...styleProps} />
            <InputField label="Level of Consciousness" value={report.immediatePostOp.levelOfConsciousness} onChange={(v) => handleInputChange('immediatePostOp', 'levelOfConsciousness', v)} placeholder="e.g., Alert, Drowsy, Sedated" {...styleProps} />
            <InputField label="Pain Score" value={report.immediatePostOp.painScore} onChange={(v) => handleInputChange('immediatePostOp', 'painScore', v)} placeholder="e.g., 0-10 scale" {...styleProps} />
            <InputField label="Nausea/Vomiting" value={report.immediatePostOp.nauseaVomiting} onChange={(v) => handleInputChange('immediatePostOp', 'nauseaVomiting', v)} placeholder="e.g., None, Mild nausea, Vomited x1" {...styleProps} />
          </div>
        )}
      </div>

      {/* Airway & Breathing */}
      <div className="report-section" style={{ borderColor: borderColor }}>
        <SectionHeader icon={Wind} title="Airway & Breathing" isExpanded={expandedSections.airwayBreathing} 
          onToggle={() => toggleSection('airwayBreathing')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
        {expandedSections.airwayBreathing && (
          <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Airway Type (ETT / LMA / Natural)" value={report.airwayBreathing.airwayType} onChange={(v) => handleInputChange('airwayBreathing', 'airwayType', v)} placeholder="e.g., ETT, LMA, Natural airway" {...styleProps} />
            <InputField label="O2 Delivery" value={report.airwayBreathing.o2Delivery} onChange={(v) => handleInputChange('airwayBreathing', 'o2Delivery', v)} placeholder="e.g., Room air, NC 2L, Face mask" {...styleProps} />
            <InputField label="SpO2" value={report.airwayBreathing.spo2} onChange={(v) => handleInputChange('airwayBreathing', 'spo2', v)} placeholder="e.g., 98%" {...styleProps} />
            <InputField label="Respiratory Rate" value={report.airwayBreathing.respiratoryRate} onChange={(v) => handleInputChange('airwayBreathing', 'respiratoryRate', v)} placeholder="e.g., 16/min" {...styleProps} />
            <InputField label="Breath Sounds" value={report.airwayBreathing.breathSounds} onChange={(v) => handleInputChange('airwayBreathing', 'breathSounds', v)} placeholder="e.g., Clear bilaterally, Diminished" {...styleProps} />
            <InputField label="Airway Events" value={report.airwayBreathing.airwayEvents} onChange={(v) => handleInputChange('airwayBreathing', 'airwayEvents', v)} placeholder="e.g., None, Coughing, Laryngospasm" {...styleProps} />
          </div>
        )}
      </div>

      {/* Circulation */}
      <div className="report-section" style={{ borderColor: borderColor }}>
        <SectionHeader icon={Heart} title="Circulation" isExpanded={expandedSections.circulation} 
          onToggle={() => toggleSection('circulation')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
        {expandedSections.circulation && (
          <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="BP" value={report.circulation.bp} onChange={(v) => handleInputChange('circulation', 'bp', v)} placeholder="e.g., 120/80" {...styleProps} />
            <InputField label="HR" value={report.circulation.hr} onChange={(v) => handleInputChange('circulation', 'hr', v)} placeholder="e.g., 72 bpm" {...styleProps} />
            <InputField label="Rhythm" value={report.circulation.rhythm} onChange={(v) => handleInputChange('circulation', 'rhythm', v)} placeholder="e.g., NSR, Sinus tachycardia" {...styleProps} />
            <InputField label="IV Access" value={report.circulation.ivAccess} onChange={(v) => handleInputChange('circulation', 'ivAccess', v)} placeholder="e.g., 18G x2, PIV left hand" {...styleProps} />
            <InputField label="Fluids Given" value={report.circulation.fluidsGiven} onChange={(v) => handleInputChange('circulation', 'fluidsGiven', v)} placeholder="e.g., LR 500ml, NS 250ml" {...styleProps} />
            <InputField label="Estimated Blood Loss" value={report.circulation.estimatedBloodLoss} onChange={(v) => handleInputChange('circulation', 'estimatedBloodLoss', v)} placeholder="e.g., <50ml, 200ml" {...styleProps} />
          </div>
        )}
      </div>

      {/* Neurologic */}
      <div className="report-section" style={{ borderColor: borderColor }}>
        <SectionHeader icon={Brain} title="Neurologic" isExpanded={expandedSections.neurologic} 
          onToggle={() => toggleSection('neurologic')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
        {expandedSections.neurologic && (
          <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="LOC" value={report.neurologic.loc} onChange={(v) => handleInputChange('neurologic', 'loc', v)} placeholder="e.g., Alert, Lethargic, Obtunded" {...styleProps} />
            <InputField label="Orientation" value={report.neurologic.orientation} onChange={(v) => handleInputChange('neurologic', 'orientation', v)} placeholder="e.g., A&O x3, Confused" {...styleProps} />
            <InputField label="Motor/Sensory (if regional block)" value={report.neurologic.motorSensory} onChange={(v) => handleInputChange('neurologic', 'motorSensory', v)} placeholder="e.g., Bilateral lower extremity numbness" {...styleProps} />
          </div>
        )}
      </div>

      {/* Pain & Comfort */}
      <div className="report-section" style={{ borderColor: borderColor }}>
        <SectionHeader icon={AlertCircle} title="Pain & Comfort" isExpanded={expandedSections.painComfort} 
          onToggle={() => toggleSection('painComfort')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
        {expandedSections.painComfort && (
          <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Pain Location" value={report.painComfort.painLocation} onChange={(v) => handleInputChange('painComfort', 'painLocation', v)} placeholder="e.g., Incisional, Throat, Generalized" {...styleProps} />
            <InputField label="Pain Score" value={report.painComfort.painScore2} onChange={(v) => handleInputChange('painComfort', 'painScore2', v)} placeholder="e.g., 0-10 scale" {...styleProps} />
            <InputField label="Analgesia Given" value={report.painComfort.analgesiaGiven} onChange={(v) => handleInputChange('painComfort', 'analgesiaGiven', v)} placeholder="e.g., Morphine 2mg IV, Tylenol 1g PO" {...styleProps} />
            <InputField label="Effectiveness" value={report.painComfort.effectiveness} onChange={(v) => handleInputChange('painComfort', 'effectiveness', v)} placeholder="e.g., Good relief, Minimal improvement" {...styleProps} />
          </div>
        )}
      </div>

      {/* Nausea & GI */}
      <div className="report-section" style={{ borderColor: borderColor }}>
        <SectionHeader icon={Pill} title="Nausea & GI" isExpanded={expandedSections.nauseaGI} 
          onToggle={() => toggleSection('nauseaGI')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
        {expandedSections.nauseaGI && (
          <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="N/V Status" value={report.nauseaGI.nvStatus} onChange={(v) => handleInputChange('nauseaGI', 'nvStatus', v)} placeholder="e.g., None, Mild nausea, Vomited x2" {...styleProps} />
            <InputField label="Antiemetics Given" value={report.nauseaGI.antiemeticsGiven} onChange={(v) => handleInputChange('nauseaGI', 'antiemeticsGiven', v)} placeholder="e.g., Zofran 4mg IV, Reglan 10mg IV" {...styleProps} />
            <InputField label="Tolerance" value={report.nauseaGI.tolerance} onChange={(v) => handleInputChange('nauseaGI', 'tolerance', v)} placeholder="e.g., Tolerating ice chips, NPO" {...styleProps} />
          </div>
        )}
      </div>

      {/* Surgical Site / Drains */}
      <div className="report-section" style={{ borderColor: borderColor }}>
        <SectionHeader icon={Scissors} title="Surgical Site / Drains" isExpanded={expandedSections.surgicalSiteDrains} 
          onToggle={() => toggleSection('surgicalSiteDrains')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
        {expandedSections.surgicalSiteDrains && (
          <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Incision Location" value={report.surgicalSiteDrains.incisionLocation} onChange={(v) => handleInputChange('surgicalSiteDrains', 'incisionLocation', v)} placeholder="e.g., RUQ laparoscopic ports, Midline" {...styleProps} />
            <InputField label="Dressing Status" value={report.surgicalSiteDrains.dressingStatus} onChange={(v) => handleInputChange('surgicalSiteDrains', 'dressingStatus', v)} placeholder="e.g., Clean, dry, intact" {...styleProps} />
            <InputField label="Drain Type" value={report.surgicalSiteDrains.drainType} onChange={(v) => handleInputChange('surgicalSiteDrains', 'drainType', v)} placeholder="e.g., JP drain, Penrose, None" {...styleProps} />
            <InputField label="Output" value={report.surgicalSiteDrains.output} onChange={(v) => handleInputChange('surgicalSiteDrains', 'output', v)} placeholder="e.g., 30ml serosanguinous" {...styleProps} />
          </div>
        )}
      </div>

      {/* Urinary Status */}
      <div className="report-section" style={{ borderColor: borderColor }}>
        <SectionHeader icon={Droplets} title="Urinary Status" isExpanded={expandedSections.urinaryStatus} 
          onToggle={() => toggleSection('urinaryStatus')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
        {expandedSections.urinaryStatus && (
          <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Voided (Y/N)" value={report.urinaryStatus.voided} onChange={(v) => handleInputChange('urinaryStatus', 'voided', v)} placeholder="e.g., Yes, No, Unable to assess" {...styleProps} />
            <InputField label="Bladder Scan" value={report.urinaryStatus.bladderScan} onChange={(v) => handleInputChange('urinaryStatus', 'bladderScan', v)} placeholder="e.g., 150ml, Not done" {...styleProps} />
            <InputField label="Catheter (if present)" value={report.urinaryStatus.catheter} onChange={(v) => handleInputChange('urinaryStatus', 'catheter', v)} placeholder="e.g., Foley 16Fr, None" {...styleProps} />
          </div>
        )}
      </div>

      {/* Safety & Recovery Scores */}
      <div className="report-section" style={{ borderColor: borderColor }}>
        <SectionHeader icon={Shield} title="Safety & Recovery Scores" isExpanded={expandedSections.safetyRecoveryScores} 
          onToggle={() => toggleSection('safetyRecoveryScores')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
        {expandedSections.safetyRecoveryScores && (
          <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Aldrete Score" value={report.safetyRecoveryScores.aldreteScore} onChange={(v) => handleInputChange('safetyRecoveryScores', 'aldreteScore', v)} placeholder="e.g., 9/10, 10/10" {...styleProps} />
            <InputField label="Fall Risk" value={report.safetyRecoveryScores.fallRisk} onChange={(v) => handleInputChange('safetyRecoveryScores', 'fallRisk', v)} placeholder="e.g., Low, Moderate, High" {...styleProps} />
            <InputField label="Temperature" value={report.safetyRecoveryScores.temperature} onChange={(v) => handleInputChange('safetyRecoveryScores', 'temperature', v)} placeholder="e.g., 98.6°F, 36.8°C" {...styleProps} />
          </div>
        )}
      </div>

      {/* Medications Given in PACU */}
      <div className="report-section" style={{ borderColor: borderColor }}>
        <SectionHeader icon={Pill} title="Medications Given in PACU" isExpanded={expandedSections.medicationsGiven} 
          onToggle={() => toggleSection('medicationsGiven')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
        {expandedSections.medicationsGiven && (
          <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Analgesics" value={report.medicationsGiven.analgesics} onChange={(v) => handleInputChange('medicationsGiven', 'analgesics', v)} placeholder="e.g., Morphine 4mg IV, Fentanyl 50mcg IV" {...styleProps} />
            <InputField label="Antiemetics" value={report.medicationsGiven.antiemetics} onChange={(v) => handleInputChange('medicationsGiven', 'antiemetics', v)} placeholder="e.g., Zofran 4mg IV, Phenergan 12.5mg IV" {...styleProps} />
            <InputField label="Reversals" value={report.medicationsGiven.reversals} onChange={(v) => handleInputChange('medicationsGiven', 'reversals', v)} placeholder="e.g., Narcan 0.4mg IV, Flumazenil 0.2mg IV" {...styleProps} />
          </div>
        )}
      </div>

      {/* Discharge Readiness */}
      <div className="report-section" style={{ borderColor: borderColor }}>
        <SectionHeader icon={CheckCircle} title="Discharge Readiness" isExpanded={expandedSections.dischargeReadiness} 
          onToggle={() => toggleSection('dischargeReadiness')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
        {expandedSections.dischargeReadiness && (
          <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Criteria Met (Y/N)" value={report.dischargeReadiness.criteriaMet} onChange={(v) => handleInputChange('dischargeReadiness', 'criteriaMet', v)} placeholder="e.g., Yes, No - pain control needed" {...styleProps} />
            <InputField label="Disposition (Home / Floor / ICU)" value={report.dischargeReadiness.disposition} onChange={(v) => handleInputChange('dischargeReadiness', 'disposition', v)} placeholder="e.g., Home, Floor, ICU, Step-down" {...styleProps} />
            <InputField label="Receiving Unit / RN" value={report.dischargeReadiness.receivingUnit} onChange={(v) => handleInputChange('dischargeReadiness', 'receivingUnit', v)} placeholder="e.g., 3West / Jane Smith RN" {...styleProps} />
          </div>
        )}
      </div>

      {/* Additional Notes */}
      <div className="report-section" style={{ borderColor: borderColor }}>
        <SectionHeader icon={FileText} title="Additional Notes" isExpanded={expandedSections.additionalNotes} 
          onToggle={() => toggleSection('additionalNotes')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
        {expandedSections.additionalNotes && (
          <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Events" value={report.additionalNotes.events} onChange={(v) => handleInputChange('additionalNotes', 'events', v)} placeholder="e.g., Uneventful recovery, Brief hypotension" {...styleProps} />
            <InputField label="Complications" value={report.additionalNotes.complications} onChange={(v) => handleInputChange('additionalNotes', 'complications', v)} placeholder="e.g., None, PONV requiring treatment" {...styleProps} />
            <InputField label="Follow-Ups" value={report.additionalNotes.followUps} onChange={(v) => handleInputChange('additionalNotes', 'followUps', v)} placeholder="e.g., Pain reassessment in 30 min" {...styleProps} />
          </div>
        )}
      </div>

      {/* Other Notes */}
      <div className="report-section" style={{ borderColor: borderColor }}>
        <SectionHeader icon={FileText} title="Other Notes" isExpanded={expandedSections.otherNotes} 
          onToggle={() => toggleSection('otherNotes')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
        {expandedSections.otherNotes && (
          <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <div className="report-field">
              <label style={{ color: labelColor }}>Additional Information</label>
              <textarea value={report.otherNotes} onChange={(e) => handleInputChange('otherNotes', '', e.target.value)} placeholder="Any other information not captured in the fields above..." className="report-other-notes-textarea" style={{ width: '100%', minHeight: '100px', padding: '8px 12px', borderRadius: '6px', color: textColor, resize: 'vertical' }} />
            </div>
          </div>
        )}
      </div>

      {/* Save Button */}
      <div className="report-actions">
        <button 
          onClick={handleSave}
          style={{ 
            backgroundColor: '#3b82f6', 
            color: 'white',
            border: 'none',
            padding: '12px 24px',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
          onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#2563eb'}
          onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#3b82f6'}
        >
          Save Report
        </button>
        {saveStatus && (
          <p style={{ 
            color: saveStatus.includes('Error') ? '#ef4444' : '#10b981', 
            marginTop: '8px',
            fontSize: '14px'
          }}>
            {saveStatus}
          </p>
        )}
      </div>
      </div>
    </>
  );
};

// ============================================
// ER Report Component
// ============================================
const ERReport: React.FC<{
  patientData: Patient;
  savedReport: ERReportData | undefined;
  onSaveReport: (mrn: string, report: ERReportData) => void;
  loggedInUser: { first_name: string; last_name: string; email: string } | null;
  onReportSubmitted?: () => void;
}> = ({ patientData, savedReport, onSaveReport, loggedInUser, onReportSubmitted }) => {
  const { theme } = useTheme();
  // Merge savedReport with empty template to ensure all nested objects exist
  const [report, setReport] = useState<ERReportData>(() => savedReport ? {
    ...emptyERReport,
    ...savedReport,
    patientOverview: { ...emptyERReport.patientOverview, ...savedReport.patientOverview },
    triageSafety: { ...emptyERReport.triageSafety, ...savedReport.triageSafety },
    primaryAssessment: { ...emptyERReport.primaryAssessment, ...savedReport.primaryAssessment },
    focusedHistory: { ...emptyERReport.focusedHistory, ...savedReport.focusedHistory },
    interventionsPerformed: { ...emptyERReport.interventionsPerformed, ...savedReport.interventionsPerformed },
    diagnostics: { ...emptyERReport.diagnostics, ...savedReport.diagnostics },
    responseToTreatment: { ...emptyERReport.responseToTreatment, ...savedReport.responseToTreatment },
    linesDevices: { ...emptyERReport.linesDevices, ...savedReport.linesDevices },
    consults: { ...emptyERReport.consults, ...savedReport.consults },
    dispositionPlanning: { ...emptyERReport.dispositionPlanning, ...savedReport.dispositionPlanning },
    patientEducationBelongings: { ...emptyERReport.patientEducationBelongings, ...savedReport.patientEducationBelongings },
    additionalNotes: { ...emptyERReport.additionalNotes, ...savedReport.additionalNotes },
  } : emptyERReport);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    patientOverview: true, triageSafety: true, primaryAssessment: true, focusedHistory: true,
    interventionsPerformed: true, diagnostics: true, responseToTreatment: true, linesDevices: true,
    consults: true, dispositionPlanning: true, patientEducationBelongings: true, additionalNotes: true, otherNotes: true
  });
  const [saveStatus, setSaveStatus] = useState<string>('');
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [previousReport, setPreviousReport] = useState<{ submitted_by: string; timestamp: string } | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Auto-populate age from patient DOB
  useEffect(() => {
    if (patientData?.DOB && !report.patientOverview.age) {
      const calculatedAge = calculateAgeFromDOB(patientData.DOB);
      if (calculatedAge) {
        setReport(prev => ({
          ...prev,
          patientOverview: { ...prev.patientOverview, age: calculatedAge }
        }));
      }
    }
  }, [patientData?.DOB]);

  // Fetch the latest saved report from the database when patient changes
  // Skip DB fetch if we already have savedReport in parent state
  useEffect(() => {
    if (!patientData?.MRN) {
      setIsLoading(false);
      return;
    }
    
    // If we already have savedReport from parent, merge with empty template to ensure all nested objects exist
    if (savedReport) {
      setReport({
        ...emptyERReport,
        ...savedReport,
        patientOverview: { ...emptyERReport.patientOverview, ...savedReport.patientOverview },
        triageSafety: { ...emptyERReport.triageSafety, ...savedReport.triageSafety },
        primaryAssessment: { ...emptyERReport.primaryAssessment, ...savedReport.primaryAssessment },
        focusedHistory: { ...emptyERReport.focusedHistory, ...savedReport.focusedHistory },
        interventionsPerformed: { ...emptyERReport.interventionsPerformed, ...savedReport.interventionsPerformed },
        diagnostics: { ...emptyERReport.diagnostics, ...savedReport.diagnostics },
        responseToTreatment: { ...emptyERReport.responseToTreatment, ...savedReport.responseToTreatment },
        linesDevices: { ...emptyERReport.linesDevices, ...savedReport.linesDevices },
        consults: { ...emptyERReport.consults, ...savedReport.consults },
        dispositionPlanning: { ...emptyERReport.dispositionPlanning, ...savedReport.dispositionPlanning },
        patientEducationBelongings: { ...emptyERReport.patientEducationBelongings, ...savedReport.patientEducationBelongings },
        additionalNotes: { ...emptyERReport.additionalNotes, ...savedReport.additionalNotes },
      });
      setIsLoading(false);
      fetch(`${BACKEND_URL}/report/er/${patientData.MRN}`)
        .then(response => response.json())
        .then(data => {
          if (data.success && data.found && data.report) {
            setPreviousReport(data.report);
          }
        })
        .catch(() => {});
      return;
    }
    
    setIsLoading(true);
    fetch(`${BACKEND_URL}/report/er/${patientData.MRN}`)
      .then(response => response.json())
      .then(data => {
        if (data.success && data.found && data.report) {
          setPreviousReport(data.report);
          setReport(data.report.report_data || emptyERReport);
        } else {
          setPreviousReport(null);
          setReport(emptyERReport);
        }
      })
      .catch(error => {
        console.error('Error loading ER report:', error);
        setPreviousReport(null);
        setReport(emptyERReport);
      })
      .finally(() => setIsLoading(false));
  }, [patientData?.MRN, savedReport]);

  // Update from local savedReport if it changes (e.g., from activity sync)
  useEffect(() => {
    if (savedReport) {
      setReport({
        ...emptyERReport,
        ...savedReport,
        patientOverview: { ...emptyERReport.patientOverview, ...savedReport.patientOverview },
        triageSafety: { ...emptyERReport.triageSafety, ...savedReport.triageSafety },
        primaryAssessment: { ...emptyERReport.primaryAssessment, ...savedReport.primaryAssessment },
        focusedHistory: { ...emptyERReport.focusedHistory, ...savedReport.focusedHistory },
        interventionsPerformed: { ...emptyERReport.interventionsPerformed, ...savedReport.interventionsPerformed },
        diagnostics: { ...emptyERReport.diagnostics, ...savedReport.diagnostics },
        responseToTreatment: { ...emptyERReport.responseToTreatment, ...savedReport.responseToTreatment },
        linesDevices: { ...emptyERReport.linesDevices, ...savedReport.linesDevices },
        consults: { ...emptyERReport.consults, ...savedReport.consults },
        dispositionPlanning: { ...emptyERReport.dispositionPlanning, ...savedReport.dispositionPlanning },
        patientEducationBelongings: { ...emptyERReport.patientEducationBelongings, ...savedReport.patientEducationBelongings },
        additionalNotes: { ...emptyERReport.additionalNotes, ...savedReport.additionalNotes },
      });
    }
  }, [savedReport]);

  const cardBgColor = theme === 'dark' ? '#1e293b' : '#ffffff';
  const borderColor = theme === 'dark' ? '#334155' : '#e2e8f0';
  const textColor = theme === 'dark' ? '#f1f5f9' : '#1e293b';
  const labelColor = theme === 'dark' ? '#94a3b8' : '#64748b';
  const headerBgColor = theme === 'dark' ? '#0f172a' : '#f1f5f9';

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const handleInputChange = (section: keyof ERReportData, field: string, value: string) => {
    setReport(prev => {
      let newReport;
      if (section === 'otherNotes') {
        newReport = { ...prev, otherNotes: value };
      } else {
        newReport = {
          ...prev,
          [section]: { ...(prev[section] as Record<string, string>), [field]: value }
        };
      }
      onSaveReport(patientData.MRN, newReport);
      return newReport;
    });
  };

  const styleProps = { textColor, labelColor, borderColor, cardBgColor };

  const handleSave = async () => {
    if (!loggedInUser) {
      setSaveStatus('Error: User not logged in');
      return;
    }

    setIsSaving(true);
    setSaveStatus('Saving...');

    try {
      const response = await fetch(`${BACKEND_URL}/report/er/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          patient_name: patientData.Name,
          mrn: patientData.MRN,
          submitted_by: `${loggedInUser.first_name} ${loggedInUser.last_name}`,
          email: loggedInUser.email,
          report_data: report
        })
      });

      const data = await response.json();
      if (data.success) {
        setSaveStatus('Report saved successfully!');
        onSaveReport(patientData.MRN, report);
        setPreviousReport({ submitted_by: `${loggedInUser.first_name} ${loggedInUser.last_name}`, timestamp: new Date().toISOString() });
        if (onReportSubmitted) onReportSubmitted();
      } else {
        setSaveStatus(`Error: ${data.message}`);
      }
    } catch (error) {
      console.error('Error saving ER report:', error);
      setSaveStatus('Error saving report');
    }
    
    setIsSaving(false);
    setTimeout(() => setSaveStatus(''), 3000);
  };

  const formatTimestamp = (timestamp: string) => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
    } catch { return timestamp; }
  };

  return (
    <>
      <div className="report-header-container" style={{ 
        backgroundColor: '#3b82f6', 
        borderRadius: '12px', 
        padding: '20px 24px',
        marginBottom: '20px'
      }}>
        <h2 style={{ color: 'white', margin: 0 }}>
          Report For: {patientData.Name} | {patientData.MRN} | Room {patientData.Room || 'N/A'}
        </h2>
        {isLoading && (
          <p style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '14px', margin: '8px 0 0 0' }}>
            Loading previous report...
          </p>
        )}
        {!isLoading && previousReport && (
          <p style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '14px', margin: '8px 0 0 0' }}>
            Previous report by: <strong>{previousReport.submitted_by}</strong> • {formatTimestamp(previousReport.timestamp)}
          </p>
        )}
        {!isLoading && !previousReport && (
          <p style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '14px', margin: '8px 0 0 0' }}>
            No previous report found for this patient
          </p>
        )}
      </div>
      <div className="patient-report-container" style={{ backgroundColor: cardBgColor }}>
        {/* Patient Overview */}
      <div className="report-section" style={{ borderColor: borderColor }}>
        <SectionHeader icon={User} title="Patient Overview" isExpanded={expandedSections.patientOverview} 
          onToggle={() => toggleSection('patientOverview')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
        {expandedSections.patientOverview && (
          <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Age" value={report.patientOverview.age} onChange={(v) => handleInputChange('patientOverview', 'age', v)} placeholder="e.g., 45" {...styleProps} />
            <InputField label="Sex" value={report.patientOverview.sex} onChange={(v) => handleInputChange('patientOverview', 'sex', v)} placeholder="e.g., M, F" {...styleProps} />
            <InputField label="Chief Complaint" value={report.patientOverview.chiefComplaint || patientData.Diagnosis} onChange={(v) => handleInputChange('patientOverview', 'chiefComplaint', v)} placeholder="e.g., Chest pain, Shortness of breath" {...styleProps} />
            <InputField label="Arrival Time" value={report.patientOverview.arrivalTime} onChange={(v) => handleInputChange('patientOverview', 'arrivalTime', v)} placeholder="e.g., 14:30" {...styleProps} />
            <InputField label="Acuity" value={report.patientOverview.acuity} onChange={(v) => handleInputChange('patientOverview', 'acuity', v)} placeholder="e.g., ESI 2, ESI 3" {...styleProps} />
          </div>
        )}
      </div>

      {/* Triage & Safety */}
      <div className="report-section" style={{ borderColor: borderColor }}>
        <SectionHeader icon={Shield} title="Triage & Safety" isExpanded={expandedSections.triageSafety} 
          onToggle={() => toggleSection('triageSafety')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
        {expandedSections.triageSafety && (
          <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Initial Vitals" value={report.triageSafety.initialVitals} onChange={(v) => handleInputChange('triageSafety', 'initialVitals', v)} placeholder="e.g., BP 140/90, HR 88, T 98.6, RR 18, O2 98%" {...styleProps} />
            <InputField label="Pain Score" value={report.triageSafety.painScore} onChange={(v) => handleInputChange('triageSafety', 'painScore', v)} placeholder="e.g., 7/10 chest pain" {...styleProps} />
            <InputField label="Allergies" value={report.triageSafety.allergies} onChange={(v) => handleInputChange('triageSafety', 'allergies', v)} placeholder="e.g., NKDA, PCN" {...styleProps} />
            <InputField label="Code Status" value={report.triageSafety.codeStatus} onChange={(v) => handleInputChange('triageSafety', 'codeStatus', v)} placeholder="e.g., Full code, DNR" {...styleProps} />
            <InputField label="Isolation/Safety" value={report.triageSafety.isolationSafety} onChange={(v) => handleInputChange('triageSafety', 'isolationSafety', v)} placeholder="e.g., Standard precautions, Fall risk" {...styleProps} />
          </div>
        )}
      </div>

      {/* Primary Assessment (ABCDE) */}
      <div className="report-section" style={{ borderColor: borderColor }}>
        <SectionHeader icon={Stethoscope} title="Primary Assessment (ABCDE)" isExpanded={expandedSections.primaryAssessment} 
          onToggle={() => toggleSection('primaryAssessment')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
        {expandedSections.primaryAssessment && (
          <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Airway Status" value={report.primaryAssessment.airwayStatus} onChange={(v) => handleInputChange('primaryAssessment', 'airwayStatus', v)} placeholder="e.g., Patent, clear" {...styleProps} />
            <InputField label="Breathing" value={report.primaryAssessment.breathing} onChange={(v) => handleInputChange('primaryAssessment', 'breathing', v)} placeholder="e.g., Clear bilaterally, no distress" {...styleProps} />
            <InputField label="Circulation" value={report.primaryAssessment.circulation} onChange={(v) => handleInputChange('primaryAssessment', 'circulation', v)} placeholder="e.g., Strong pulses, no bleeding" {...styleProps} />
            <InputField label="Disability" value={report.primaryAssessment.disability} onChange={(v) => handleInputChange('primaryAssessment', 'disability', v)} placeholder="e.g., Alert, oriented x3" {...styleProps} />
            <InputField label="Exposure" value={report.primaryAssessment.exposure} onChange={(v) => handleInputChange('primaryAssessment', 'exposure', v)} placeholder="e.g., No obvious trauma, normothermic" {...styleProps} />
          </div>
        )}
      </div>

      {/* Focused History */}
      <div className="report-section" style={{ borderColor: borderColor }}>
        <SectionHeader icon={FileText} title="Focused History" isExpanded={expandedSections.focusedHistory} 
          onToggle={() => toggleSection('focusedHistory')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
        {expandedSections.focusedHistory && (
          <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="HPI Summary" value={report.focusedHistory.hpiSummary} onChange={(v) => handleInputChange('focusedHistory', 'hpiSummary', v)} placeholder="e.g., 2 hour onset crushing chest pain, 7/10, radiating to left arm" {...styleProps} />
            <InputField label="Pertinent PMH" value={report.focusedHistory.pertinentPmh} onChange={(v) => handleInputChange('focusedHistory', 'pertinentPmh', v)} placeholder="e.g., HTN, DM, prior MI 2019" {...styleProps} />
            <InputField label="Home Meds" value={report.focusedHistory.homeMeds} onChange={(v) => handleInputChange('focusedHistory', 'homeMeds', v)} placeholder="e.g., Metoprolol, Lisinopril, Metformin" {...styleProps} />
            <InputField label="Last PO Intake" value={report.focusedHistory.lastPoIntake} onChange={(v) => handleInputChange('focusedHistory', 'lastPoIntake', v)} placeholder="e.g., Breakfast at 8am" {...styleProps} />
          </div>
        )}
      </div>

      {/* Interventions Performed */}
      <div className="report-section" style={{ borderColor: borderColor }}>
        <SectionHeader icon={Syringe} title="Interventions Performed" isExpanded={expandedSections.interventionsPerformed} 
          onToggle={() => toggleSection('interventionsPerformed')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
        {expandedSections.interventionsPerformed && (
          <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="IVs" value={report.interventionsPerformed.ivs} onChange={(v) => handleInputChange('interventionsPerformed', 'ivs', v)} placeholder="e.g., 18g L AC, 20g R hand" {...styleProps} />
            <InputField label="O2" value={report.interventionsPerformed.o2} onChange={(v) => handleInputChange('interventionsPerformed', 'o2', v)} placeholder="e.g., 2L NC, NRB 15L" {...styleProps} />
            <InputField label="Fluids" value={report.interventionsPerformed.fluids} onChange={(v) => handleInputChange('interventionsPerformed', 'fluids', v)} placeholder="e.g., NS 1L bolus, LR 500ml" {...styleProps} />
            <InputField label="Medications Given" value={report.interventionsPerformed.medicationsGiven} onChange={(v) => handleInputChange('interventionsPerformed', 'medicationsGiven', v)} placeholder="e.g., ASA 325mg, Morphine 4mg IV" {...styleProps} />
            <InputField label="Procedures" value={report.interventionsPerformed.procedures} onChange={(v) => handleInputChange('interventionsPerformed', 'procedures', v)} placeholder="e.g., Foley catheter, NG tube" {...styleProps} />
          </div>
        )}
      </div>

      {/* Diagnostics */}
      <div className="report-section" style={{ borderColor: borderColor }}>
        <SectionHeader icon={Activity} title="Diagnostics" isExpanded={expandedSections.diagnostics} 
          onToggle={() => toggleSection('diagnostics')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
        {expandedSections.diagnostics && (
          <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Labs Ordered" value={report.diagnostics.labsOrdered} onChange={(v) => handleInputChange('diagnostics', 'labsOrdered', v)} placeholder="e.g., CBC, BMP, Troponin, PT/INR" {...styleProps} />
            <InputField label="Imaging Ordered" value={report.diagnostics.imagingOrdered} onChange={(v) => handleInputChange('diagnostics', 'imagingOrdered', v)} placeholder="e.g., CXR, CT chest, Echo" {...styleProps} />
            <InputField label="EKG" value={report.diagnostics.ekg} onChange={(v) => handleInputChange('diagnostics', 'ekg', v)} placeholder="e.g., NSR, ST elevation V2-V4" {...styleProps} />
          </div>
        )}
      </div>

      {/* Response to Treatment */}
      <div className="report-section" style={{ borderColor: borderColor }}>
        <SectionHeader icon={TrendingUp} title="Response to Treatment" isExpanded={expandedSections.responseToTreatment} 
          onToggle={() => toggleSection('responseToTreatment')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
        {expandedSections.responseToTreatment && (
          <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Pain Reassessment" value={report.responseToTreatment.painReassessment} onChange={(v) => handleInputChange('responseToTreatment', 'painReassessment', v)} placeholder="e.g., Pain 3/10 after morphine" {...styleProps} />
            <InputField label="Vital Trends" value={report.responseToTreatment.vitalTrends} onChange={(v) => handleInputChange('responseToTreatment', 'vitalTrends', v)} placeholder="e.g., BP stable, HR decreased to 72" {...styleProps} />
            <InputField label="Clinical Changes" value={report.responseToTreatment.clinicalChanges} onChange={(v) => handleInputChange('responseToTreatment', 'clinicalChanges', v)} placeholder="e.g., Improved breathing, less diaphoretic" {...styleProps} />
          </div>
        )}
      </div>

      {/* Lines / Devices */}
      <div className="report-section" style={{ borderColor: borderColor }}>
        <SectionHeader icon={Zap} title="Lines / Devices" isExpanded={expandedSections.linesDevices} 
          onToggle={() => toggleSection('linesDevices')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
        {expandedSections.linesDevices && (
          <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="IVs/Lines" value={report.linesDevices.ivsLines} onChange={(v) => handleInputChange('linesDevices', 'ivsLines', v)} placeholder="e.g., 18g L AC patent, 20g R hand" {...styleProps} />
            <InputField label="Foley" value={report.linesDevices.foley} onChange={(v) => handleInputChange('linesDevices', 'foley', v)} placeholder="e.g., 16Fr, clear yellow urine 200ml" {...styleProps} />
            <InputField label="NG Tube" value={report.linesDevices.ngTube} onChange={(v) => handleInputChange('linesDevices', 'ngTube', v)} placeholder="e.g., 18Fr, low suction, bilious drainage" {...styleProps} />
            <InputField label="Splints" value={report.linesDevices.splints} onChange={(v) => handleInputChange('linesDevices', 'splints', v)} placeholder="e.g., R wrist splint, L ankle immobilizer" {...styleProps} />
            <InputField label="Monitors" value={report.linesDevices.monitors} onChange={(v) => handleInputChange('linesDevices', 'monitors', v)} placeholder="e.g., Tele, pulse ox, BP cuff" {...styleProps} />
          </div>
        )}
      </div>

      {/* Consults */}
      <div className="report-section" style={{ borderColor: borderColor }}>
        <SectionHeader icon={Users} title="Consults" isExpanded={expandedSections.consults} 
          onToggle={() => toggleSection('consults')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
        {expandedSections.consults && (
          <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Consulting Services" value={report.consults.consultingServices} onChange={(v) => handleInputChange('consults', 'consultingServices', v)} placeholder="e.g., Cardiology, Surgery, Psychiatry" {...styleProps} />
            <InputField label="Recommendations" value={report.consults.recommendations} onChange={(v) => handleInputChange('consults', 'recommendations', v)} placeholder="e.g., Admit to CCU, OR in AM, Psych eval" {...styleProps} />
          </div>
        )}
      </div>

      {/* Disposition Planning */}
      <div className="report-section" style={{ borderColor: borderColor }}>
        <SectionHeader icon={MapPin} title="Disposition Planning" isExpanded={expandedSections.dispositionPlanning} 
          onToggle={() => toggleSection('dispositionPlanning')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
        {expandedSections.dispositionPlanning && (
          <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Disposition" value={report.dispositionPlanning.disposition} onChange={(v) => handleInputChange('dispositionPlanning', 'disposition', v)} placeholder="e.g., Admit, Discharge, Transfer" {...styleProps} />
            <InputField label="Admitting Service" value={report.dispositionPlanning.admittingService} onChange={(v) => handleInputChange('dispositionPlanning', 'admittingService', v)} placeholder="e.g., Internal Medicine, Surgery" {...styleProps} />
            <InputField label="Bed Assignment" value={report.dispositionPlanning.bedAssignment} onChange={(v) => handleInputChange('dispositionPlanning', 'bedAssignment', v)} placeholder="e.g., ICU bed 12, Med-Surg 4A" {...styleProps} />
          </div>
        )}
      </div>

      {/* Patient Education/Belongings */}
      <div className="report-section" style={{ borderColor: borderColor }}>
        <SectionHeader icon={BookOpen} title="Patient Education/Belongings" isExpanded={expandedSections.patientEducationBelongings} 
          onToggle={() => toggleSection('patientEducationBelongings')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
        {expandedSections.patientEducationBelongings && (
          <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Discharge" value={report.patientEducationBelongings.discharge} onChange={(v) => handleInputChange('patientEducationBelongings', 'discharge', v)} placeholder="e.g., Discharge home, Follow up with PCP" {...styleProps} />
            <InputField label="Discharge Instructions" value={report.patientEducationBelongings.dischargeInstructions} onChange={(v) => handleInputChange('patientEducationBelongings', 'dischargeInstructions', v)} placeholder="e.g., Return if chest pain worsens, Take medications as prescribed" {...styleProps} />
            <InputField label="Belongings Secured" value={report.patientEducationBelongings.belongingsSecured} onChange={(v) => handleInputChange('patientEducationBelongings', 'belongingsSecured', v)} placeholder="e.g., Wallet and phone with family, Valuables in safe" {...styleProps} />
          </div>
        )}
      </div>

      {/* Additional Notes */}
      <div className="report-section" style={{ borderColor: borderColor }}>
        <SectionHeader icon={MessageSquare} title="Additional Notes" isExpanded={expandedSections.additionalNotes} 
          onToggle={() => toggleSection('additionalNotes')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
        {expandedSections.additionalNotes && (
          <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Events" value={report.additionalNotes.events} onChange={(v) => handleInputChange('additionalNotes', 'events', v)} placeholder="e.g., Code blue called, Family meeting held" {...styleProps} />
            <InputField label="Concerns" value={report.additionalNotes.concerns} onChange={(v) => handleInputChange('additionalNotes', 'concerns', v)} placeholder="e.g., Patient anxious, Family requesting updates" {...styleProps} />
          </div>
        )}
      </div>

      {/* Other Notes */}
      <div className="report-section" style={{ borderColor: borderColor }}>
        <SectionHeader icon={FileText} title="Other Notes" isExpanded={expandedSections.otherNotes} 
          onToggle={() => toggleSection('otherNotes')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
        {expandedSections.otherNotes && (
          <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <div className="report-field">
              <label style={{ color: labelColor }}>Additional Information</label>
              <textarea value={report.otherNotes} onChange={(e) => handleInputChange('otherNotes', '', e.target.value)} placeholder="Any other information not captured in the fields above..." className="report-other-notes-textarea" style={{ width: '100%', minHeight: '100px', padding: '8px 12px', borderRadius: '6px', color: textColor, resize: 'vertical' }} />
            </div>
          </div>
        )}
      </div>

      <div className="report-actions" style={{ borderColor: borderColor }}>
        <button 
          onClick={handleSave} 
          disabled={isSaving}
          style={{
            backgroundColor: '#3b82f6',
            color: 'white',
            border: 'none',
            padding: '12px 24px',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: isSaving ? 'not-allowed' : 'pointer',
            opacity: isSaving ? 0.7 : 1,
            transition: 'all 0.2s ease'
          }}
          onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#2563eb'}
          onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#3b82f6'}
        >
          Save Report
        </button>
        {saveStatus && (
          <p style={{ 
            color: saveStatus.includes('Error') ? '#ef4444' : '#10b981',
            marginTop: '8px',
            fontSize: '14px'
          }}>
            {saveStatus}
          </p>
        )}
      </div>
      </div>
    </>
  );
};

// ============================================
// NICU Report Component
// ============================================
const NICUReport: React.FC<{
  patientData: Patient;
  savedReport: NICUReportData | undefined;
  onSaveReport: (mrn: string, report: NICUReportData) => void;
  loggedInUser: { first_name: string; last_name: string; email: string } | null;
  onReportSubmitted?: () => void;
}> = ({ patientData, savedReport, onSaveReport, loggedInUser, onReportSubmitted }) => {
  const { theme } = useTheme();
  // Merge savedReport with empty template to ensure all nested objects exist
  const [report, setReport] = useState<NICUReportData>(() => savedReport ? {
    ...emptyNICUReport,
    ...savedReport,
    patientBirthInfo: { ...emptyNICUReport.patientBirthInfo, ...savedReport.patientBirthInfo },
    vitals: { ...emptyNICUReport.vitals, ...savedReport.vitals },
    respiratorySupport: { ...emptyNICUReport.respiratorySupport, ...savedReport.respiratorySupport },
    cardiovascular: { ...emptyNICUReport.cardiovascular, ...savedReport.cardiovascular },
    linesAccess: { ...emptyNICUReport.linesAccess, ...savedReport.linesAccess },
    fluidsNutrition: { ...emptyNICUReport.fluidsNutrition, ...savedReport.fluidsNutrition },
    medications: { ...emptyNICUReport.medications, ...savedReport.medications },
    neuroComfort: { ...emptyNICUReport.neuroComfort, ...savedReport.neuroComfort },
    labsMonitoring: { ...emptyNICUReport.labsMonitoring, ...savedReport.labsMonitoring },
    skinDevelopment: { ...emptyNICUReport.skinDevelopment, ...savedReport.skinDevelopment },
    familySocial: { ...emptyNICUReport.familySocial, ...savedReport.familySocial },
    planDailyGoals: { ...emptyNICUReport.planDailyGoals, ...savedReport.planDailyGoals },
    additionalNotes: { ...emptyNICUReport.additionalNotes, ...savedReport.additionalNotes },
  } : emptyNICUReport);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    patientBirthInfo: true, vitals: true, respiratorySupport: true, cardiovascular: true,
    linesAccess: true, fluidsNutrition: true, medications: true, neuroComfort: true,
    labsMonitoring: true, skinDevelopment: true, familySocial: true, planDailyGoals: true, additionalNotes: true, otherNotes: true
  });
  const [saveStatus, setSaveStatus] = useState<string>('');
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [previousReport, setPreviousReport] = useState<{ submitted_by: string; timestamp: string } | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Fetch the latest saved report from the database when patient changes
  // Skip DB fetch if we already have savedReport in parent state
  useEffect(() => {
    const fetchSavedReport = async () => {
      if (!patientData?.MRN) {
        setIsLoading(false);
        return;
      }
      
      // If we already have savedReport from parent, use it and skip DB fetch
      if (savedReport) {
        setReport({
          ...emptyNICUReport,
          ...savedReport,
          patientBirthInfo: { ...emptyNICUReport.patientBirthInfo, ...savedReport.patientBirthInfo },
          vitals: { ...emptyNICUReport.vitals, ...savedReport.vitals },
          respiratorySupport: { ...emptyNICUReport.respiratorySupport, ...savedReport.respiratorySupport },
          cardiovascular: { ...emptyNICUReport.cardiovascular, ...savedReport.cardiovascular },
          linesAccess: { ...emptyNICUReport.linesAccess, ...savedReport.linesAccess },
          fluidsNutrition: { ...emptyNICUReport.fluidsNutrition, ...savedReport.fluidsNutrition },
          medications: { ...emptyNICUReport.medications, ...savedReport.medications },
          neuroComfort: { ...emptyNICUReport.neuroComfort, ...savedReport.neuroComfort },
          labsMonitoring: { ...emptyNICUReport.labsMonitoring, ...savedReport.labsMonitoring },
          skinDevelopment: { ...emptyNICUReport.skinDevelopment, ...savedReport.skinDevelopment },
          familySocial: { ...emptyNICUReport.familySocial, ...savedReport.familySocial },
          planDailyGoals: { ...emptyNICUReport.planDailyGoals, ...savedReport.planDailyGoals },
          additionalNotes: { ...emptyNICUReport.additionalNotes, ...savedReport.additionalNotes },
        });
        setIsLoading(false);
        try {
          const response = await axios.get<{
            success: boolean;
            report?: { submitted_by: string; timestamp: string; };
          }>(`${BACKEND_URL}/report/nicu/${patientData.MRN}`);
          if (response.data.success && response.data.report) {
            setPreviousReport({
              submitted_by: response.data.report.submitted_by,
              timestamp: response.data.report.timestamp
            });
          }
        } catch { /* ignore */ }
        return;
      }
      
      setIsLoading(true);
      try {
        const response = await axios.get<{
          success: boolean;
          report?: {
            submitted_by: string;
            timestamp: string;
            report_data: NICUReportData;
          };
        }>(`${BACKEND_URL}/report/nicu/${patientData.MRN}`);
        if (response.data.success && response.data.report) {
          const dbReport = response.data.report;
          setReport(dbReport.report_data as NICUReportData);
          onSaveReport(patientData.MRN, dbReport.report_data as NICUReportData);
          setPreviousReport({
            submitted_by: dbReport.submitted_by,
            timestamp: dbReport.timestamp
          });
        } else {
          setReport(emptyNICUReport);
          setPreviousReport(null);
        }
      } catch (error) {
        setReport(emptyNICUReport);
        setPreviousReport(null);
      }
      setIsLoading(false);
    };

    fetchSavedReport();
  }, [patientData?.MRN, savedReport]);

  // Update from local savedReport if it changes (e.g., from activity sync)
  useEffect(() => {
    if (savedReport) {
      setReport({
        ...emptyNICUReport,
        ...savedReport,
        patientBirthInfo: { ...emptyNICUReport.patientBirthInfo, ...savedReport.patientBirthInfo },
        vitals: { ...emptyNICUReport.vitals, ...savedReport.vitals },
        respiratorySupport: { ...emptyNICUReport.respiratorySupport, ...savedReport.respiratorySupport },
        cardiovascular: { ...emptyNICUReport.cardiovascular, ...savedReport.cardiovascular },
        linesAccess: { ...emptyNICUReport.linesAccess, ...savedReport.linesAccess },
        fluidsNutrition: { ...emptyNICUReport.fluidsNutrition, ...savedReport.fluidsNutrition },
        medications: { ...emptyNICUReport.medications, ...savedReport.medications },
        neuroComfort: { ...emptyNICUReport.neuroComfort, ...savedReport.neuroComfort },
        labsMonitoring: { ...emptyNICUReport.labsMonitoring, ...savedReport.labsMonitoring },
        skinDevelopment: { ...emptyNICUReport.skinDevelopment, ...savedReport.skinDevelopment },
        familySocial: { ...emptyNICUReport.familySocial, ...savedReport.familySocial },
        planDailyGoals: { ...emptyNICUReport.planDailyGoals, ...savedReport.planDailyGoals },
        additionalNotes: { ...emptyNICUReport.additionalNotes, ...savedReport.additionalNotes },
      });
    }
  }, [savedReport]);

  const cardBgColor = theme === 'dark' ? '#1e293b' : '#ffffff';
  const borderColor = theme === 'dark' ? '#334155' : '#e2e8f0';
  const textColor = theme === 'dark' ? '#f1f5f9' : '#1e293b';
  const labelColor = theme === 'dark' ? '#94a3b8' : '#64748b';
  const headerBgColor = theme === 'dark' ? '#0f172a' : '#f1f5f9';

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const handleInputChange = (section: keyof NICUReportData, field: string, value: string) => {
    setReport(prev => {
      let newReport;
      if (section === 'otherNotes') {
        newReport = { ...prev, otherNotes: value };
      } else {
        newReport = {
          ...prev,
          [section]: { ...(prev[section] as Record<string, string>), [field]: value }
        };
      }
      onSaveReport(patientData.MRN, newReport);
      return newReport;
    });
  };

  const styleProps = { textColor, labelColor, borderColor, cardBgColor };

  const handleSave = async () => {
    if (!loggedInUser) {
      setSaveStatus('Error: User not logged in');
      return;
    }

    setIsSaving(true);
    setSaveStatus('Saving...');

    try {
      const response = await fetch(`${BACKEND_URL}/report/nicu/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          patient_name: patientData.Name,
          mrn: patientData.MRN,
          submitted_by: `${loggedInUser.first_name} ${loggedInUser.last_name}`,
          email: loggedInUser.email,
          report_data: report
        })
      });

      const data = await response.json();
      if (data.success) {
        setSaveStatus('Report saved successfully!');
        onSaveReport(patientData.MRN, report);
        setPreviousReport({
          submitted_by: `${loggedInUser.first_name} ${loggedInUser.last_name}`,
          timestamp: new Date().toISOString()
        });
        if (onReportSubmitted) onReportSubmitted();
      } else {
        setSaveStatus(`Error: ${data.message}`);
      }
    } catch (error) {
      setSaveStatus('Error saving report. Please try again.');
    }
    setIsSaving(false);
  };

  const formatTimestamp = (timestamp: string) => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
    } catch { return timestamp; }
  };

  return (
    <>
      <div className="report-header-container" style={{ 
        backgroundColor: '#3b82f6', 
        borderRadius: '12px', 
        padding: '20px 24px',
        marginBottom: '20px'
      }}>
        <h2 style={{ color: 'white', margin: 0 }}>
          Report For: {patientData.Name} | {patientData.MRN} | Room {patientData.Room || 'N/A'}
        </h2>
        {isLoading && (
          <p style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '14px', margin: '8px 0 0 0' }}>
            Loading previous report...
          </p>
        )}
        {!isLoading && previousReport && (
          <p style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '14px', margin: '8px 0 0 0' }}>
            Previous report by: <strong>{previousReport.submitted_by}</strong> • {formatTimestamp(previousReport.timestamp)}
          </p>
        )}
        {!isLoading && !previousReport && (
          <p style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '14px', margin: '8px 0 0 0' }}>
            No previous report found for this patient
          </p>
        )}
      </div>
      <div className="patient-report-container" style={{ backgroundColor: cardBgColor }}>
        {/* Patient & Birth Info */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={User} title="Patient & Birth Info" isExpanded={expandedSections.patientBirthInfo} 
            onToggle={() => toggleSection('patientBirthInfo')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.patientBirthInfo && (
            <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
              <InputField label="Baby Name" value={report.patientBirthInfo.babyName} onChange={(v) => handleInputChange('patientBirthInfo', 'babyName', v)} placeholder="e.g., Baby Smith" {...styleProps} />
              <InputField label="Gestational Age" value={report.patientBirthInfo.gestationalAge} onChange={(v) => handleInputChange('patientBirthInfo', 'gestationalAge', v)} placeholder="e.g., 32 weeks" {...styleProps} />
              <InputField label="Post Menstrual Age" value={report.patientBirthInfo.postMenstrualAge} onChange={(v) => handleInputChange('patientBirthInfo', 'postMenstrualAge', v)} placeholder="e.g., 34 weeks" {...styleProps} />
              <InputField label="Day of Life" value={report.patientBirthInfo.dayOfLife} onChange={(v) => handleInputChange('patientBirthInfo', 'dayOfLife', v)} placeholder="e.g., DOL 14" {...styleProps} />
              <InputField label="Birth Weight" value={report.patientBirthInfo.birthWeight} onChange={(v) => handleInputChange('patientBirthInfo', 'birthWeight', v)} placeholder="e.g., 1500g" {...styleProps} />
              <InputField label="Current Weight" value={report.patientBirthInfo.currentWeight} onChange={(v) => handleInputChange('patientBirthInfo', 'currentWeight', v)} placeholder="e.g., 1650g" {...styleProps} />
              <InputField label="Admit Dx" value={report.patientBirthInfo.admitDx || patientData.Diagnosis} onChange={(v) => handleInputChange('patientBirthInfo', 'admitDx', v)} placeholder="e.g., Prematurity, RDS" {...styleProps} />
              <InputField label="Code Status" value={report.patientBirthInfo.codeStatus} onChange={(v) => handleInputChange('patientBirthInfo', 'codeStatus', v)} placeholder="e.g., Full Code" {...styleProps} />
            </div>
          )}
        </div>

        {/* Vitals */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={Heart} title="Vitals" isExpanded={expandedSections.vitals} 
            onToggle={() => toggleSection('vitals')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.vitals && (
            <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
              <InputField label="HR" value={report.vitals.hr} onChange={(v) => handleInputChange('vitals', 'hr', v)} placeholder="e.g., 150 bpm" {...styleProps} />
              <InputField label="RR" value={report.vitals.rr} onChange={(v) => handleInputChange('vitals', 'rr', v)} placeholder="e.g., 45/min" {...styleProps} />
              <InputField label="BP" value={report.vitals.bp} onChange={(v) => handleInputChange('vitals', 'bp', v)} placeholder="e.g., 55/35 (42)" {...styleProps} />
              <InputField label="SpO2" value={report.vitals.spo2} onChange={(v) => handleInputChange('vitals', 'spo2', v)} placeholder="e.g., 95%" {...styleProps} />
              <InputField label="Temp" value={report.vitals.temp} onChange={(v) => handleInputChange('vitals', 'temp', v)} placeholder="e.g., 36.8°C" {...styleProps} />
            </div>
          )}
        </div>

        {/* Respiratory Support */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={Wind} title="Respiratory Support" isExpanded={expandedSections.respiratorySupport} 
            onToggle={() => toggleSection('respiratorySupport')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.respiratorySupport && (
            <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
              <InputField label="Support Type" value={report.respiratorySupport.supportType} onChange={(v) => handleInputChange('respiratorySupport', 'supportType', v)} placeholder="e.g., CPAP, Vent, NC" {...styleProps} />
              <InputField label="Vent Mode" value={report.respiratorySupport.ventMode} onChange={(v) => handleInputChange('respiratorySupport', 'ventMode', v)} placeholder="e.g., SIMV, AC/PC" {...styleProps} />
              <InputField label="FiO2" value={report.respiratorySupport.fio2} onChange={(v) => handleInputChange('respiratorySupport', 'fio2', v)} placeholder="e.g., 30%" {...styleProps} />
              <InputField label="PEEP" value={report.respiratorySupport.peep} onChange={(v) => handleInputChange('respiratorySupport', 'peep', v)} placeholder="e.g., 5 cmH2O" {...styleProps} />
              <InputField label="CPAP" value={report.respiratorySupport.cpap} onChange={(v) => handleInputChange('respiratorySupport', 'cpap', v)} placeholder="e.g., 6 cmH2O" {...styleProps} />
              <InputField label="Rate" value={report.respiratorySupport.rate} onChange={(v) => handleInputChange('respiratorySupport', 'rate', v)} placeholder="e.g., 40/min" {...styleProps} />
              <InputField label="Apnea/Brady/Desat Events" value={report.respiratorySupport.apneaBradyDesatEvents} onChange={(v) => handleInputChange('respiratorySupport', 'apneaBradyDesatEvents', v)} placeholder="e.g., 2 episodes, self-resolved" {...styleProps} />
            </div>
          )}
        </div>

        {/* Cardiovascular */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={Activity} title="Cardiovascular" isExpanded={expandedSections.cardiovascular} 
            onToggle={() => toggleSection('cardiovascular')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.cardiovascular && (
            <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
              <InputField label="MAP" value={report.cardiovascular.map} onChange={(v) => handleInputChange('cardiovascular', 'map', v)} placeholder="e.g., 42 mmHg" {...styleProps} />
              <InputField label="Murmur" value={report.cardiovascular.murmur} onChange={(v) => handleInputChange('cardiovascular', 'murmur', v)} placeholder="e.g., Grade II/VI systolic" {...styleProps} />
              <InputField label="Echo Findings" value={report.cardiovascular.echoFindings} onChange={(v) => handleInputChange('cardiovascular', 'echoFindings', v)} placeholder="e.g., Small PDA" {...styleProps} />
              <InputField label="Perfusion" value={report.cardiovascular.perfusion} onChange={(v) => handleInputChange('cardiovascular', 'perfusion', v)} placeholder="e.g., Pink, well perfused" {...styleProps} />
              <InputField label="Cap Refill" value={report.cardiovascular.capRefill} onChange={(v) => handleInputChange('cardiovascular', 'capRefill', v)} placeholder="e.g., <3 sec" {...styleProps} />
            </div>
          )}
        </div>

        {/* Lines & Access */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={Syringe} title="Lines & Access" isExpanded={expandedSections.linesAccess} 
            onToggle={() => toggleSection('linesAccess')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.linesAccess && (
            <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
              <InputField label="UVC" value={report.linesAccess.uvc} onChange={(v) => handleInputChange('linesAccess', 'uvc', v)} placeholder="e.g., Present, DOL 3" {...styleProps} />
              <InputField label="UAC" value={report.linesAccess.uac} onChange={(v) => handleInputChange('linesAccess', 'uac', v)} placeholder="e.g., Present, DOL 3" {...styleProps} />
              <InputField label="PICC" value={report.linesAccess.picc} onChange={(v) => handleInputChange('linesAccess', 'picc', v)} placeholder="e.g., R arm, day 5" {...styleProps} />
              <InputField label="PIVs" value={report.linesAccess.pivs} onChange={(v) => handleInputChange('linesAccess', 'pivs', v)} placeholder="e.g., L hand 24g" {...styleProps} />
              <InputField label="ETT" value={report.linesAccess.ett} onChange={(v) => handleInputChange('linesAccess', 'ett', v)} placeholder="e.g., 2.5 at 7cm" {...styleProps} />
            </div>
          )}
        </div>

        {/* Fluids & Nutrition */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={Droplets} title="Fluids & Nutrition" isExpanded={expandedSections.fluidsNutrition} 
            onToggle={() => toggleSection('fluidsNutrition')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.fluidsNutrition && (
            <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
              <InputField label="IV Fluids" value={report.fluidsNutrition.ivFluids} onChange={(v) => handleInputChange('fluidsNutrition', 'ivFluids', v)} placeholder="e.g., D10W at 80 mL/kg/day" {...styleProps} />
              <InputField label="TPN" value={report.fluidsNutrition.tpn} onChange={(v) => handleInputChange('fluidsNutrition', 'tpn', v)} placeholder="e.g., TPN at 100 mL/kg/day" {...styleProps} />
              <InputField label="Lipids" value={report.fluidsNutrition.lipids} onChange={(v) => handleInputChange('fluidsNutrition', 'lipids', v)} placeholder="e.g., 3 g/kg/day" {...styleProps} />
              <InputField label="Enteral Feeds" value={report.fluidsNutrition.enteralFeeds} onChange={(v) => handleInputChange('fluidsNutrition', 'enteralFeeds', v)} placeholder="e.g., EBM" {...styleProps} />
              <InputField label="Feed Type" value={report.fluidsNutrition.feedType} onChange={(v) => handleInputChange('fluidsNutrition', 'feedType', v)} placeholder="e.g., Fortified EBM" {...styleProps} />
              <InputField label="Route" value={report.fluidsNutrition.route} onChange={(v) => handleInputChange('fluidsNutrition', 'route', v)} placeholder="e.g., OG tube" {...styleProps} />
              <InputField label="Volume" value={report.fluidsNutrition.volume} onChange={(v) => handleInputChange('fluidsNutrition', 'volume', v)} placeholder="e.g., 20 mL q3h" {...styleProps} />
              <InputField label="Feeding Tolerance" value={report.fluidsNutrition.feedingTolerance} onChange={(v) => handleInputChange('fluidsNutrition', 'feedingTolerance', v)} placeholder="e.g., Tolerating well, no residuals" {...styleProps} />
            </div>
          )}
        </div>

        {/* Medications */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={Pill} title="Medications" isExpanded={expandedSections.medications} 
            onToggle={() => toggleSection('medications')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.medications && (
            <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
              <InputField label="Scheduled Meds" value={report.medications.scheduledMeds} onChange={(v) => handleInputChange('medications', 'scheduledMeds', v)} placeholder="e.g., Caffeine citrate 10mg daily" {...styleProps} />
              <InputField label="Continuous Infusions" value={report.medications.continuousInfusions} onChange={(v) => handleInputChange('medications', 'continuousInfusions', v)} placeholder="e.g., Dopamine 5 mcg/kg/min" {...styleProps} />
              <InputField label="Antibiotics" value={report.medications.antibiotics} onChange={(v) => handleInputChange('medications', 'antibiotics', v)} placeholder="e.g., Ampicillin, Gentamicin day 3/7" {...styleProps} />
            </div>
          )}
        </div>

        {/* Neuro & Comfort */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={Brain} title="Neuro & Comfort" isExpanded={expandedSections.neuroComfort} 
            onToggle={() => toggleSection('neuroComfort')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.neuroComfort && (
            <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
              <InputField label="Tone" value={report.neuroComfort.tone} onChange={(v) => handleInputChange('neuroComfort', 'tone', v)} placeholder="e.g., Appropriate for GA" {...styleProps} />
              <InputField label="Activity" value={report.neuroComfort.activity} onChange={(v) => handleInputChange('neuroComfort', 'activity', v)} placeholder="e.g., Active, responsive" {...styleProps} />
              <InputField label="Pain/Sedation Scale" value={report.neuroComfort.painSedationScale} onChange={(v) => handleInputChange('neuroComfort', 'painSedationScale', v)} placeholder="e.g., N-PASS 2" {...styleProps} />
              <InputField label="Comfort Measures" value={report.neuroComfort.comfortMeasures} onChange={(v) => handleInputChange('neuroComfort', 'comfortMeasures', v)} placeholder="e.g., Swaddling, sucrose PRN" {...styleProps} />
            </div>
          )}
        </div>

        {/* Labs & Monitoring */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={TestTube} title="Labs & Monitoring" isExpanded={expandedSections.labsMonitoring} 
            onToggle={() => toggleSection('labsMonitoring')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.labsMonitoring && (
            <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
              <InputField label="ABGs" value={report.labsMonitoring.abgs} onChange={(v) => handleInputChange('labsMonitoring', 'abgs', v)} placeholder="e.g., 7.35/42/65/22" {...styleProps} />
              <InputField label="Glucose" value={report.labsMonitoring.glucose} onChange={(v) => handleInputChange('labsMonitoring', 'glucose', v)} placeholder="e.g., 85 mg/dL" {...styleProps} />
              <InputField label="Electrolytes" value={report.labsMonitoring.electrolytes} onChange={(v) => handleInputChange('labsMonitoring', 'electrolytes', v)} placeholder="e.g., Na 138, K 4.2" {...styleProps} />
              <InputField label="Bilirubin" value={report.labsMonitoring.bilirubin} onChange={(v) => handleInputChange('labsMonitoring', 'bilirubin', v)} placeholder="e.g., Total 8.5, on phototherapy" {...styleProps} />
              <InputField label="Cultures" value={report.labsMonitoring.cultures} onChange={(v) => handleInputChange('labsMonitoring', 'cultures', v)} placeholder="e.g., Blood cx pending" {...styleProps} />
            </div>
          )}
        </div>

        {/* Skin & Development */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={Shield} title="Skin & Development" isExpanded={expandedSections.skinDevelopment} 
            onToggle={() => toggleSection('skinDevelopment')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.skinDevelopment && (
            <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
              <InputField label="Skin Integrity" value={report.skinDevelopment.skinIntegrity} onChange={(v) => handleInputChange('skinDevelopment', 'skinIntegrity', v)} placeholder="e.g., Intact, no breakdown" {...styleProps} />
              <InputField label="Positioning" value={report.skinDevelopment.positioning} onChange={(v) => handleInputChange('skinDevelopment', 'positioning', v)} placeholder="e.g., Prone, nested" {...styleProps} />
              <InputField label="Developmental Care" value={report.skinDevelopment.developmentalCare} onChange={(v) => handleInputChange('skinDevelopment', 'developmentalCare', v)} placeholder="e.g., Clustered care, dim lights" {...styleProps} />
            </div>
          )}
        </div>

        {/* Family & Social */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={Users} title="Family & Social" isExpanded={expandedSections.familySocial} 
            onToggle={() => toggleSection('familySocial')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.familySocial && (
            <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
              <InputField label="Parents Updated" value={report.familySocial.parentsUpdated} onChange={(v) => handleInputChange('familySocial', 'parentsUpdated', v)} placeholder="e.g., Yes, spoke with mom at 1400" {...styleProps} />
              <InputField label="Kangaroo Care" value={report.familySocial.kangarooCare} onChange={(v) => handleInputChange('familySocial', 'kangarooCare', v)} placeholder="e.g., 2 hours with mom" {...styleProps} />
              <InputField label="Social Work" value={report.familySocial.socialWork} onChange={(v) => handleInputChange('familySocial', 'socialWork', v)} placeholder="e.g., Consult placed" {...styleProps} />
              <InputField label="Lactation" value={report.familySocial.lactation} onChange={(v) => handleInputChange('familySocial', 'lactation', v)} placeholder="e.g., Pumping, good supply" {...styleProps} />
            </div>
          )}
        </div>

        {/* Plan & Daily Goals */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={Target} title="Plan & Daily Goals" isExpanded={expandedSections.planDailyGoals} 
            onToggle={() => toggleSection('planDailyGoals')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.planDailyGoals && (
            <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
              <InputField label="Respiratory Plan" value={report.planDailyGoals.respiratoryPlan} onChange={(v) => handleInputChange('planDailyGoals', 'respiratoryPlan', v)} placeholder="e.g., Wean CPAP as tolerated" {...styleProps} />
              <InputField label="Feeding Advancement" value={report.planDailyGoals.feedingAdvancement} onChange={(v) => handleInputChange('planDailyGoals', 'feedingAdvancement', v)} placeholder="e.g., Advance by 10 mL/kg/day" {...styleProps} />
              <InputField label="Antibiotic Plan" value={report.planDailyGoals.antibioticPlan} onChange={(v) => handleInputChange('planDailyGoals', 'antibioticPlan', v)} placeholder="e.g., Complete 7 day course" {...styleProps} />
              <InputField label="Discharge Barriers" value={report.planDailyGoals.dischargeBarriers} onChange={(v) => handleInputChange('planDailyGoals', 'dischargeBarriers', v)} placeholder="e.g., Feeding, thermoregulation" {...styleProps} />
            </div>
          )}
        </div>

        {/* Additional Notes */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={FileText} title="Additional Notes" isExpanded={expandedSections.additionalNotes} 
            onToggle={() => toggleSection('additionalNotes')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.additionalNotes && (
            <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
              <InputField label="Events" value={report.additionalNotes.events} onChange={(v) => handleInputChange('additionalNotes', 'events', v)} placeholder="e.g., Significant events this shift" {...styleProps} />
              <InputField label="Changes from Prior Shift" value={report.additionalNotes.changesFromPriorShift} onChange={(v) => handleInputChange('additionalNotes', 'changesFromPriorShift', v)} placeholder="e.g., Notable changes" {...styleProps} />
            </div>
          )}
        </div>

        {/* Other Notes */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={FileText} title="Other Notes" isExpanded={expandedSections.otherNotes} 
            onToggle={() => toggleSection('otherNotes')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.otherNotes && (
            <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
              <div className="report-field">
                <label style={{ color: labelColor }}>Additional Information</label>
                <textarea value={report.otherNotes} onChange={(e) => handleInputChange('otherNotes', '', e.target.value)} placeholder="Any other information not captured in the fields above..." className="report-other-notes-textarea" style={{ width: '100%', minHeight: '100px', padding: '8px 12px', borderRadius: '6px', color: textColor, resize: 'vertical' }} />
              </div>
            </div>
          )}
        </div>

        {/* Save Button */}
        <div className="report-actions" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '20px' }}>
          <button
            onClick={handleSave}
            disabled={isSaving}
            style={{
              backgroundColor: '#3b82f6',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: isSaving ? 'not-allowed' : 'pointer',
              opacity: isSaving ? 0.7 : 1,
              transition: 'all 0.2s ease'
            }}
            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#2563eb'}
            onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#3b82f6'}
          >
            Save Report
          </button>
          {saveStatus && (
            <p style={{ 
              color: saveStatus.includes('Error') ? '#ef4444' : '#10b981',
              marginTop: '8px',
              fontSize: '14px'
            }}>
              {saveStatus}
            </p>
          )}
        </div>
      </div>
    </>
  );
};

// ============================================
// Pediatrics Report Component
// ============================================
const PediatricsReport: React.FC<{
  patientData: Patient;
  savedReport: PediatricsReportData | undefined;
  onSaveReport: (mrn: string, report: PediatricsReportData) => void;
  loggedInUser: { first_name: string; last_name: string; email: string } | null;
  onReportSubmitted?: () => void;
}> = ({ patientData, savedReport, onSaveReport, loggedInUser, onReportSubmitted }) => {
  const { theme } = useTheme();
  // Merge savedReport with empty template to ensure all nested objects exist
  const [report, setReport] = useState<PediatricsReportData>(() => savedReport ? {
    ...emptyPediatricsReport,
    ...savedReport,
    patientOverview: { ...emptyPediatricsReport.patientOverview, ...savedReport.patientOverview },
    vitals: { ...emptyPediatricsReport.vitals, ...savedReport.vitals },
    growthNutrition: { ...emptyPediatricsReport.growthNutrition, ...savedReport.growthNutrition },
    respiratory: { ...emptyPediatricsReport.respiratory, ...savedReport.respiratory },
    cardiac: { ...emptyPediatricsReport.cardiac, ...savedReport.cardiac },
    neuroBehavior: { ...emptyPediatricsReport.neuroBehavior, ...savedReport.neuroBehavior },
    painComfort: { ...emptyPediatricsReport.painComfort, ...savedReport.painComfort },
    medications: { ...emptyPediatricsReport.medications, ...savedReport.medications },
    giElimination: { ...emptyPediatricsReport.giElimination, ...savedReport.giElimination },
    skinSafety: { ...emptyPediatricsReport.skinSafety, ...savedReport.skinSafety },
    familyPsychosocial: { ...emptyPediatricsReport.familyPsychosocial, ...savedReport.familyPsychosocial },
    schoolDevelopment: { ...emptyPediatricsReport.schoolDevelopment, ...savedReport.schoolDevelopment },
    planDischarge: { ...emptyPediatricsReport.planDischarge, ...savedReport.planDischarge },
    additionalNotes: { ...emptyPediatricsReport.additionalNotes, ...savedReport.additionalNotes },
  } : emptyPediatricsReport);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    patientOverview: true, vitals: true, growthNutrition: true, respiratory: true,
    cardiac: true, neuroBehavior: true, painComfort: true, medications: true,
    giElimination: true, skinSafety: true, familyPsychosocial: true,
    schoolDevelopment: true, planDischarge: true, additionalNotes: true, otherNotes: true
  });
  const [saveStatus, setSaveStatus] = useState<string>('');
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [previousSubmitter, setPreviousSubmitter] = useState<{ name: string; timestamp: string } | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Auto-populate age from patient DOB
  useEffect(() => {
    if (patientData?.DOB && !report.patientOverview.age) {
      const calculatedAge = calculateAgeFromDOB(patientData.DOB);
      if (calculatedAge) {
        setReport(prev => ({
          ...prev,
          patientOverview: { ...prev.patientOverview, age: calculatedAge }
        }));
      }
    }
  }, [patientData?.DOB]);

  // Skip DB fetch if we already have savedReport in parent state
  useEffect(() => {
    const fetchSavedReport = async () => {
      if (!patientData?.MRN) {
        setIsLoading(false);
        return;
      }
      
      // If we already have savedReport from parent, use it and skip DB fetch
      if (savedReport) {
        setReport({
          ...emptyPediatricsReport,
          ...savedReport,
          patientOverview: { ...emptyPediatricsReport.patientOverview, ...savedReport.patientOverview },
          vitals: { ...emptyPediatricsReport.vitals, ...savedReport.vitals },
          growthNutrition: { ...emptyPediatricsReport.growthNutrition, ...savedReport.growthNutrition },
          respiratory: { ...emptyPediatricsReport.respiratory, ...savedReport.respiratory },
          cardiac: { ...emptyPediatricsReport.cardiac, ...savedReport.cardiac },
          neuroBehavior: { ...emptyPediatricsReport.neuroBehavior, ...savedReport.neuroBehavior },
          painComfort: { ...emptyPediatricsReport.painComfort, ...savedReport.painComfort },
          medications: { ...emptyPediatricsReport.medications, ...savedReport.medications },
          giElimination: { ...emptyPediatricsReport.giElimination, ...savedReport.giElimination },
          skinSafety: { ...emptyPediatricsReport.skinSafety, ...savedReport.skinSafety },
          familyPsychosocial: { ...emptyPediatricsReport.familyPsychosocial, ...savedReport.familyPsychosocial },
          schoolDevelopment: { ...emptyPediatricsReport.schoolDevelopment, ...savedReport.schoolDevelopment },
          planDischarge: { ...emptyPediatricsReport.planDischarge, ...savedReport.planDischarge },
          additionalNotes: { ...emptyPediatricsReport.additionalNotes, ...savedReport.additionalNotes },
        });
        setIsLoading(false);
        try {
          const response = await axios.get<{
            success: boolean;
            report?: { submitted_by: string; timestamp: string; };
          }>(`${BACKEND_URL}/report/pediatrics/${patientData.MRN}`);
          if (response.data.success && response.data.report) {
            setPreviousSubmitter({ name: response.data.report.submitted_by, timestamp: response.data.report.timestamp });
          }
        } catch { /* ignore */ }
        return;
      }
      
      setIsLoading(true);
      try {
        const response = await axios.get<{
          success: boolean;
          report?: {
            submitted_by: string;
            timestamp: string;
            report_data: PediatricsReportData;
          };
        }>(`${BACKEND_URL}/report/pediatrics/${patientData.MRN}`);
        if (response.data.success && response.data.report) {
          const dbReport = response.data.report;
          setReport(dbReport.report_data as PediatricsReportData);
          onSaveReport(patientData.MRN, dbReport.report_data as PediatricsReportData);
          setPreviousSubmitter({
            name: dbReport.submitted_by,
            timestamp: dbReport.timestamp
          });
        } else {
          setReport(emptyPediatricsReport);
          setPreviousSubmitter(null);
        }
      } catch (error) {
        setReport(emptyPediatricsReport);
        setPreviousSubmitter(null);
      }
      setIsLoading(false);
    };

    fetchSavedReport();
  }, [patientData?.MRN, savedReport]);

  // Update from local savedReport if it changes (e.g., from activity sync)
  useEffect(() => {
    if (savedReport) {
      setReport({
        ...emptyPediatricsReport,
        ...savedReport,
        patientOverview: { ...emptyPediatricsReport.patientOverview, ...savedReport.patientOverview },
        vitals: { ...emptyPediatricsReport.vitals, ...savedReport.vitals },
        growthNutrition: { ...emptyPediatricsReport.growthNutrition, ...savedReport.growthNutrition },
        respiratory: { ...emptyPediatricsReport.respiratory, ...savedReport.respiratory },
        cardiac: { ...emptyPediatricsReport.cardiac, ...savedReport.cardiac },
        neuroBehavior: { ...emptyPediatricsReport.neuroBehavior, ...savedReport.neuroBehavior },
        painComfort: { ...emptyPediatricsReport.painComfort, ...savedReport.painComfort },
        medications: { ...emptyPediatricsReport.medications, ...savedReport.medications },
        giElimination: { ...emptyPediatricsReport.giElimination, ...savedReport.giElimination },
        skinSafety: { ...emptyPediatricsReport.skinSafety, ...savedReport.skinSafety },
        familyPsychosocial: { ...emptyPediatricsReport.familyPsychosocial, ...savedReport.familyPsychosocial },
        schoolDevelopment: { ...emptyPediatricsReport.schoolDevelopment, ...savedReport.schoolDevelopment },
        planDischarge: { ...emptyPediatricsReport.planDischarge, ...savedReport.planDischarge },
        additionalNotes: { ...emptyPediatricsReport.additionalNotes, ...savedReport.additionalNotes },
      });
    }
  }, [savedReport]);

  const cardBgColor = theme === 'dark' ? '#1e293b' : '#ffffff';
  const borderColor = theme === 'dark' ? '#334155' : '#e2e8f0';
  const headerBgColor = theme === 'dark' ? '#0f172a' : '#f1f5f9';
  const textColor = theme === 'dark' ? '#f1f5f9' : '#1e293b';
  const labelColor = theme === 'dark' ? '#94a3b8' : '#64748b';

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const handleInputChange = (section: keyof PediatricsReportData, field: string, value: string) => {
    setReport(prev => {
      let newReport;
      if (section === 'otherNotes') {
        newReport = { ...prev, otherNotes: value };
      } else {
        newReport = {
          ...prev,
          [section]: { ...(prev[section] as Record<string, string>), [field]: value }
        };
      }
      onSaveReport(patientData.MRN, newReport);
      return newReport;
    });
  };

  const handleSave = async () => {
    onSaveReport(patientData.MRN, report);
    
    if (loggedInUser) {
      setIsSaving(true);
      try {
        const submittedBy = `${loggedInUser.first_name} ${loggedInUser.last_name}`;
        await axios.post(`${BACKEND_URL}/report/pediatrics/save`, {
          patient_name: patientData.Name,
          mrn: patientData.MRN,
          submitted_by: submittedBy,
          email: loggedInUser.email,
          report_data: report
        });
        setPreviousSubmitter({
          name: submittedBy,
          timestamp: new Date().toISOString()
        });
        setSaveStatus('Report saved successfully!');
        onReportSubmitted?.();
      } catch (error) {
        console.error('Error saving report to database:', error);
        setSaveStatus('Saved locally (database error)');
      }
      setIsSaving(false);
    } else {
      setSaveStatus('Report saved locally!');
    }
    setTimeout(() => setSaveStatus(''), 3000);
  };

  const styleProps = { cardBgColor, borderColor, textColor, labelColor };

  const formatTimestamp = (timestamp: string) => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return timestamp;
    }
  };

  if (isLoading) {
    return (
      <div className="patient-report-container" style={{ backgroundColor: cardBgColor, padding: '40px', textAlign: 'center' }}>
        <div style={{ color: labelColor }}>Loading report...</div>
      </div>
    );
  }

  return (
    <>
      <div className="report-header-container" style={{ 
        backgroundColor: '#3b82f6', 
        borderRadius: '12px', 
        padding: '20px 24px',
        marginBottom: '20px'
      }}>
        <h2 style={{ color: 'white', margin: 0 }}>
          Report For: {patientData.Name} | {patientData.MRN} | Room {patientData.Room || 'N/A'}
        </h2>
        {previousSubmitter && (
          <p style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '14px', margin: '8px 0 0 0' }}>
            Previous report by: <strong>{previousSubmitter.name}</strong> • {formatTimestamp(previousSubmitter.timestamp)}
          </p>
        )}
        {!previousSubmitter && (
          <p style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '14px', margin: '8px 0 0 0' }}>
            No previous report found for this patient
          </p>
        )}
      </div>
      <div className="patient-report-container" style={{ backgroundColor: cardBgColor }}>
        {/* Patient Overview */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={Info} title="Patient Overview" isExpanded={expandedSections.patientOverview} 
            onToggle={() => toggleSection('patientOverview')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.patientOverview && (
            <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
              <InputField label="Age" value={report.patientOverview.age} onChange={(v) => handleInputChange('patientOverview', 'age', v)} placeholder="e.g., 5 years" {...styleProps} />
              <InputField label="Weight" value={report.patientOverview.weight} onChange={(v) => handleInputChange('patientOverview', 'weight', v)} placeholder="e.g., 20 kg" {...styleProps} />
              <InputField label="Admit Dx" value={report.patientOverview.admitDx || patientData.Diagnosis} onChange={(v) => handleInputChange('patientOverview', 'admitDx', v)} placeholder="e.g., Pneumonia" {...styleProps} />
              <InputField label="Code Status" value={report.patientOverview.codeStatus} onChange={(v) => handleInputChange('patientOverview', 'codeStatus', v)} placeholder="e.g., Full Code" {...styleProps} />
              <InputField label="Allergies" value={report.patientOverview.allergies} onChange={(v) => handleInputChange('patientOverview', 'allergies', v)} placeholder="e.g., Penicillin, Peanuts" {...styleProps} />
            </div>
          )}
        </div>

        {/* Vitals */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={Heart} title="Vitals" isExpanded={expandedSections.vitals} 
            onToggle={() => toggleSection('vitals')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.vitals && (
            <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
              <InputField label="HR" value={report.vitals.hr} onChange={(v) => handleInputChange('vitals', 'hr', v)} placeholder="e.g., 100 bpm" {...styleProps} />
              <InputField label="RR" value={report.vitals.rr} onChange={(v) => handleInputChange('vitals', 'rr', v)} placeholder="e.g., 22/min" {...styleProps} />
              <InputField label="BP" value={report.vitals.bp} onChange={(v) => handleInputChange('vitals', 'bp', v)} placeholder="e.g., 95/60" {...styleProps} />
              <InputField label="SpO2" value={report.vitals.spo2} onChange={(v) => handleInputChange('vitals', 'spo2', v)} placeholder="e.g., 98% RA" {...styleProps} />
              <InputField label="Temp" value={report.vitals.temp} onChange={(v) => handleInputChange('vitals', 'temp', v)} placeholder="e.g., 37.2°C" {...styleProps} />
              <InputField label="Pain Score" value={report.vitals.painScore} onChange={(v) => handleInputChange('vitals', 'painScore', v)} placeholder="e.g., 2/10 FLACC" {...styleProps} />
            </div>
          )}
        </div>

        {/* Growth & Nutrition */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={Utensils} title="Growth & Nutrition" isExpanded={expandedSections.growthNutrition} 
            onToggle={() => toggleSection('growthNutrition')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.growthNutrition && (
            <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
              <InputField label="Diet Type" value={report.growthNutrition.dietType} onChange={(v) => handleInputChange('growthNutrition', 'dietType', v)} placeholder="e.g., Regular, soft" {...styleProps} />
              <InputField label="Intake" value={report.growthNutrition.intake} onChange={(v) => handleInputChange('growthNutrition', 'intake', v)} placeholder="e.g., 75% of meals" {...styleProps} />
              <InputField label="IV Fluids" value={report.growthNutrition.ivFluids} onChange={(v) => handleInputChange('growthNutrition', 'ivFluids', v)} placeholder="e.g., D5 1/2NS at 50 mL/hr" {...styleProps} />
              <InputField label="Daily Weight" value={report.growthNutrition.dailyWeight} onChange={(v) => handleInputChange('growthNutrition', 'dailyWeight', v)} placeholder="e.g., 20.2 kg (+0.1)" {...styleProps} />
            </div>
          )}
        </div>

        {/* Respiratory */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={Wind} title="Respiratory" isExpanded={expandedSections.respiratory} 
            onToggle={() => toggleSection('respiratory')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.respiratory && (
            <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
              <InputField label="O2 Delivery" value={report.respiratory.o2Delivery} onChange={(v) => handleInputChange('respiratory', 'o2Delivery', v)} placeholder="e.g., 2L NC, RA" {...styleProps} />
              <InputField label="SpO2 Trend" value={report.respiratory.spo2Trend} onChange={(v) => handleInputChange('respiratory', 'spo2Trend', v)} placeholder="e.g., Stable 96-98%" {...styleProps} />
              <InputField label="Breath Sounds" value={report.respiratory.breathSounds} onChange={(v) => handleInputChange('respiratory', 'breathSounds', v)} placeholder="e.g., CTA bilaterally" {...styleProps} />
              <InputField label="Work of Breathing" value={report.respiratory.workOfBreathing} onChange={(v) => handleInputChange('respiratory', 'workOfBreathing', v)} placeholder="e.g., Mild retractions" {...styleProps} />
              <InputField label="Cough/Secretions" value={report.respiratory.coughSecretions} onChange={(v) => handleInputChange('respiratory', 'coughSecretions', v)} placeholder="e.g., Productive, yellow" {...styleProps} />
            </div>
          )}
        </div>

        {/* Cardiac */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={Activity} title="Cardiac" isExpanded={expandedSections.cardiac} 
            onToggle={() => toggleSection('cardiac')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.cardiac && (
            <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
              <InputField label="Heart Sounds" value={report.cardiac.heartSounds} onChange={(v) => handleInputChange('cardiac', 'heartSounds', v)} placeholder="e.g., S1S2 regular, no murmur" {...styleProps} />
              <InputField label="Cap Refill" value={report.cardiac.capRefill} onChange={(v) => handleInputChange('cardiac', 'capRefill', v)} placeholder="e.g., <2 sec" {...styleProps} />
              <InputField label="Edema" value={report.cardiac.edema} onChange={(v) => handleInputChange('cardiac', 'edema', v)} placeholder="e.g., None" {...styleProps} />
            </div>
          )}
        </div>

        {/* Neuro & Behavior */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={Brain} title="Neuro & Behavior" isExpanded={expandedSections.neuroBehavior} 
            onToggle={() => toggleSection('neuroBehavior')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.neuroBehavior && (
            <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
              <InputField label="LOC" value={report.neuroBehavior.loc} onChange={(v) => handleInputChange('neuroBehavior', 'loc', v)} placeholder="e.g., Alert, interactive" {...styleProps} />
              <InputField label="Orientation" value={report.neuroBehavior.orientation} onChange={(v) => handleInputChange('neuroBehavior', 'orientation', v)} placeholder="e.g., Age-appropriate" {...styleProps} />
              <InputField label="Behavior/Mood" value={report.neuroBehavior.behaviorMood} onChange={(v) => handleInputChange('neuroBehavior', 'behaviorMood', v)} placeholder="e.g., Cooperative, playful" {...styleProps} />
              <InputField label="Developmental Level" value={report.neuroBehavior.developmentalLevel} onChange={(v) => handleInputChange('neuroBehavior', 'developmentalLevel', v)} placeholder="e.g., Meeting milestones" {...styleProps} />
            </div>
          )}
        </div>

        {/* Pain & Comfort */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={Thermometer} title="Pain & Comfort" isExpanded={expandedSections.painComfort} 
            onToggle={() => toggleSection('painComfort')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.painComfort && (
            <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
              <InputField label="Pain Location" value={report.painComfort.painLocation} onChange={(v) => handleInputChange('painComfort', 'painLocation', v)} placeholder="e.g., Abdomen, throat" {...styleProps} />
              <InputField label="Pain Scale Used" value={report.painComfort.painScaleUsed} onChange={(v) => handleInputChange('painComfort', 'painScaleUsed', v)} placeholder="e.g., FLACC, Wong-Baker" {...styleProps} />
              <InputField label="Pain Interventions" value={report.painComfort.painInterventions} onChange={(v) => handleInputChange('painComfort', 'painInterventions', v)} placeholder="e.g., Tylenol, distraction" {...styleProps} />
              <InputField label="Effectiveness" value={report.painComfort.effectiveness} onChange={(v) => handleInputChange('painComfort', 'effectiveness', v)} placeholder="e.g., Pain reduced to 1/10" {...styleProps} />
            </div>
          )}
        </div>

        {/* Medications */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={Pill} title="Medications" isExpanded={expandedSections.medications} 
            onToggle={() => toggleSection('medications')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.medications && (
            <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
              <InputField label="Scheduled Meds" value={report.medications.scheduledMeds} onChange={(v) => handleInputChange('medications', 'scheduledMeds', v)} placeholder="e.g., Amoxicillin 250mg TID" {...styleProps} />
              <InputField label="PRNs Given" value={report.medications.prnsGiven} onChange={(v) => handleInputChange('medications', 'prnsGiven', v)} placeholder="e.g., Tylenol x1, effective" {...styleProps} />
              <InputField label="Antibiotics" value={report.medications.antibiotics} onChange={(v) => handleInputChange('medications', 'antibiotics', v)} placeholder="e.g., Ceftriaxone day 2/5" {...styleProps} />
            </div>
          )}
        </div>

        {/* GI & Elimination */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={Stethoscope} title="GI & Elimination" isExpanded={expandedSections.giElimination} 
            onToggle={() => toggleSection('giElimination')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.giElimination && (
            <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
              <InputField label="Abdomen" value={report.giElimination.abdomen} onChange={(v) => handleInputChange('giElimination', 'abdomen', v)} placeholder="e.g., Soft, non-tender" {...styleProps} />
              <InputField label="Bowel Sounds" value={report.giElimination.bowelSounds} onChange={(v) => handleInputChange('giElimination', 'bowelSounds', v)} placeholder="e.g., Active x4 quadrants" {...styleProps} />
              <InputField label="Stool" value={report.giElimination.stool} onChange={(v) => handleInputChange('giElimination', 'stool', v)} placeholder="e.g., BM x1, formed" {...styleProps} />
              <InputField label="Urine Output" value={report.giElimination.urineOutput} onChange={(v) => handleInputChange('giElimination', 'urineOutput', v)} placeholder="e.g., Voiding well, clear" {...styleProps} />
            </div>
          )}
        </div>

        {/* Skin & Safety */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={Shield} title="Skin & Safety" isExpanded={expandedSections.skinSafety} 
            onToggle={() => toggleSection('skinSafety')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.skinSafety && (
            <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
              <InputField label="Skin Integrity" value={report.skinSafety.skinIntegrity} onChange={(v) => handleInputChange('skinSafety', 'skinIntegrity', v)} placeholder="e.g., Intact, no rashes" {...styleProps} />
              <InputField label="IV Site Checks" value={report.skinSafety.ivSiteChecks} onChange={(v) => handleInputChange('skinSafety', 'ivSiteChecks', v)} placeholder="e.g., R hand, no redness" {...styleProps} />
              <InputField label="Fall Risk" value={report.skinSafety.fallRisk} onChange={(v) => handleInputChange('skinSafety', 'fallRisk', v)} placeholder="e.g., Low, crib rails up" {...styleProps} />
              <InputField label="Isolation Precautions" value={report.skinSafety.isolationPrecautions} onChange={(v) => handleInputChange('skinSafety', 'isolationPrecautions', v)} placeholder="e.g., Contact, droplet" {...styleProps} />
            </div>
          )}
        </div>

        {/* Family & Psychosocial */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={Users} title="Family & Psychosocial" isExpanded={expandedSections.familyPsychosocial} 
            onToggle={() => toggleSection('familyPsychosocial')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.familyPsychosocial && (
            <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
              <InputField label="Parent/Caregiver Present" value={report.familyPsychosocial.parentCaregiverPresent} onChange={(v) => handleInputChange('familyPsychosocial', 'parentCaregiverPresent', v)} placeholder="e.g., Mom at bedside" {...styleProps} />
              <InputField label="Education Provided" value={report.familyPsychosocial.educationProvided} onChange={(v) => handleInputChange('familyPsychosocial', 'educationProvided', v)} placeholder="e.g., Asthma management" {...styleProps} />
              <InputField label="Coping/Concerns" value={report.familyPsychosocial.copingConcerns} onChange={(v) => handleInputChange('familyPsychosocial', 'copingConcerns', v)} placeholder="e.g., Anxious about discharge" {...styleProps} />
            </div>
          )}
        </div>

        {/* School / Development */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={BookOpen} title="School / Development" isExpanded={expandedSections.schoolDevelopment} 
            onToggle={() => toggleSection('schoolDevelopment')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.schoolDevelopment && (
            <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
              <InputField label="School Needs" value={report.schoolDevelopment.schoolNeeds} onChange={(v) => handleInputChange('schoolDevelopment', 'schoolNeeds', v)} placeholder="e.g., Homework brought in" {...styleProps} />
              <InputField label="Play/Child Life Involvement" value={report.schoolDevelopment.playChildLifeInvolvement} onChange={(v) => handleInputChange('schoolDevelopment', 'playChildLifeInvolvement', v)} placeholder="e.g., Child life visited" {...styleProps} />
            </div>
          )}
        </div>

        {/* Plan & Discharge */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={Target} title="Plan & Discharge" isExpanded={expandedSections.planDischarge} 
            onToggle={() => toggleSection('planDischarge')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.planDischarge && (
            <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
              <InputField label="Plan for Shift" value={report.planDischarge.planForShift} onChange={(v) => handleInputChange('planDischarge', 'planForShift', v)} placeholder="e.g., Continue IV abx, advance diet" {...styleProps} />
              <InputField label="Pending Tests" value={report.planDischarge.pendingTests} onChange={(v) => handleInputChange('planDischarge', 'pendingTests', v)} placeholder="e.g., CXR in AM" {...styleProps} />
              <InputField label="Discharge Barriers" value={report.planDischarge.dischargeBarriers} onChange={(v) => handleInputChange('planDischarge', 'dischargeBarriers', v)} placeholder="e.g., Needs to tolerate PO" {...styleProps} />
            </div>
          )}
        </div>

        {/* Additional Notes */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={FileText} title="Additional Notes" isExpanded={expandedSections.additionalNotes} 
            onToggle={() => toggleSection('additionalNotes')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.additionalNotes && (
            <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
              <InputField label="Events" value={report.additionalNotes.events} onChange={(v) => handleInputChange('additionalNotes', 'events', v)} placeholder="e.g., Significant events this shift" {...styleProps} />
              <InputField label="Changes from Prior Shift" value={report.additionalNotes.changesFromPriorShift} onChange={(v) => handleInputChange('additionalNotes', 'changesFromPriorShift', v)} placeholder="e.g., Notable changes" {...styleProps} />
            </div>
          )}
        </div>

        {/* Other Notes */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={FileText} title="Other Notes" isExpanded={expandedSections.otherNotes} 
            onToggle={() => toggleSection('otherNotes')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.otherNotes && (
            <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
              <div className="report-field">
                <label style={{ color: labelColor }}>Additional Information</label>
                <textarea value={report.otherNotes} onChange={(e) => handleInputChange('otherNotes', '', e.target.value)} placeholder="Any other information not captured in the fields above..." className="report-other-notes-textarea" style={{ width: '100%', minHeight: '100px', padding: '8px 12px', borderRadius: '6px', color: textColor, resize: 'vertical' }} />
              </div>
            </div>
          )}
        </div>

        {/* Save Button */}
        <div className="report-actions" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '20px' }}>
          <button
            onClick={handleSave}
            disabled={isSaving}
            style={{
              backgroundColor: '#3b82f6',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: isSaving ? 'not-allowed' : 'pointer',
              opacity: isSaving ? 0.7 : 1,
              transition: 'all 0.2s ease'
            }}
            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#2563eb'}
            onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#3b82f6'}
          >
            Save Report
          </button>
          {saveStatus && (
            <p style={{ 
              color: saveStatus.includes('Error') ? '#ef4444' : '#10b981',
              marginTop: '8px',
              fontSize: '14px'
            }}>
              {saveStatus}
            </p>
          )}
        </div>
      </div>
    </>
  );
};

// ============================================
// Oncology Report Component
// ============================================
const OncologyReport: React.FC<{
  patientData: Patient;
  savedReport: OncologyReportData | undefined;
  onSaveReport: (mrn: string, report: OncologyReportData) => void;
  loggedInUser: { first_name: string; last_name: string; email: string } | null;
  onReportSubmitted?: () => void;
}> = ({ patientData, savedReport, onSaveReport, loggedInUser, onReportSubmitted }) => {
  const { theme } = useTheme();
  // Merge savedReport with empty template to ensure all nested objects exist
  const [report, setReport] = useState<OncologyReportData>(() => savedReport ? {
    ...emptyOncologyReport,
    ...savedReport,
    patientOverview: { ...emptyOncologyReport.patientOverview, ...savedReport.patientOverview },
    vitals: { ...emptyOncologyReport.vitals, ...savedReport.vitals },
    treatmentStatus: { ...emptyOncologyReport.treatmentStatus, ...savedReport.treatmentStatus },
    hematology: { ...emptyOncologyReport.hematology, ...savedReport.hematology },
    symptoms: { ...emptyOncologyReport.symptoms, ...savedReport.symptoms },
    infectionRisk: { ...emptyOncologyReport.infectionRisk, ...savedReport.infectionRisk },
    giNutrition: { ...emptyOncologyReport.giNutrition, ...savedReport.giNutrition },
    skinLines: { ...emptyOncologyReport.skinLines, ...savedReport.skinLines },
    psychosocial: { ...emptyOncologyReport.psychosocial, ...savedReport.psychosocial },
    plan: { ...emptyOncologyReport.plan, ...savedReport.plan },
    notes: { ...emptyOncologyReport.notes, ...savedReport.notes },
  } : emptyOncologyReport);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    patientOverview: true, vitals: true, treatmentStatus: true, hematology: true,
    symptoms: true, infectionRisk: true, giNutrition: true, skinLines: true,
    psychosocial: true, plan: true, notes: true, otherNotes: true
  });
  const [saveStatus, setSaveStatus] = useState<string>('');
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [previousSubmitter, setPreviousSubmitter] = useState<{ name: string; timestamp: string } | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Auto-populate age from patient DOB
  useEffect(() => {
    if (patientData?.DOB && !report.patientOverview.age) {
      const calculatedAge = calculateAgeFromDOB(patientData.DOB);
      if (calculatedAge) {
        setReport(prev => ({
          ...prev,
          patientOverview: { ...prev.patientOverview, age: calculatedAge }
        }));
      }
    }
  }, [patientData?.DOB]);

  useEffect(() => {
    const fetchSavedReport = async () => {
      if (!patientData?.MRN) { setIsLoading(false); return; }
      if (savedReport) {
        setReport({
          ...emptyOncologyReport,
          ...savedReport,
          patientOverview: { ...emptyOncologyReport.patientOverview, ...savedReport.patientOverview },
          vitals: { ...emptyOncologyReport.vitals, ...savedReport.vitals },
          treatmentStatus: { ...emptyOncologyReport.treatmentStatus, ...savedReport.treatmentStatus },
          hematology: { ...emptyOncologyReport.hematology, ...savedReport.hematology },
          symptoms: { ...emptyOncologyReport.symptoms, ...savedReport.symptoms },
          infectionRisk: { ...emptyOncologyReport.infectionRisk, ...savedReport.infectionRisk },
          giNutrition: { ...emptyOncologyReport.giNutrition, ...savedReport.giNutrition },
          skinLines: { ...emptyOncologyReport.skinLines, ...savedReport.skinLines },
          psychosocial: { ...emptyOncologyReport.psychosocial, ...savedReport.psychosocial },
          plan: { ...emptyOncologyReport.plan, ...savedReport.plan },
          notes: { ...emptyOncologyReport.notes, ...savedReport.notes },
        });
        setIsLoading(false);
        try {
          const response = await axios.get<{ success: boolean; report?: { submitted_by: string; timestamp: string; }; }>(`${BACKEND_URL}/report/oncology/${patientData.MRN}`);
          if (response.data.success && response.data.report) {
            setPreviousSubmitter({ name: response.data.report.submitted_by, timestamp: response.data.report.timestamp });
          }
        } catch { /* ignore */ }
        return;
      }
      setIsLoading(true);
      try {
        const response = await axios.get<{ success: boolean; report?: { submitted_by: string; timestamp: string; report_data: OncologyReportData; }; }>(`${BACKEND_URL}/report/oncology/${patientData.MRN}`);
        if (response.data.success && response.data.report) {
          const dbReport = response.data.report;
          setReport(dbReport.report_data as OncologyReportData);
          onSaveReport(patientData.MRN, dbReport.report_data as OncologyReportData);
          setPreviousSubmitter({ name: dbReport.submitted_by, timestamp: dbReport.timestamp });
        } else {
          setReport(emptyOncologyReport);
          setPreviousSubmitter(null);
        }
      } catch (error) {
        setReport(emptyOncologyReport);
        setPreviousSubmitter(null);
      }
      setIsLoading(false);
    };
    fetchSavedReport();
  }, [patientData?.MRN, savedReport]);

  useEffect(() => {
    if (savedReport) {
      setReport({
        ...emptyOncologyReport,
        ...savedReport,
        patientOverview: { ...emptyOncologyReport.patientOverview, ...savedReport.patientOverview },
        vitals: { ...emptyOncologyReport.vitals, ...savedReport.vitals },
        treatmentStatus: { ...emptyOncologyReport.treatmentStatus, ...savedReport.treatmentStatus },
        hematology: { ...emptyOncologyReport.hematology, ...savedReport.hematology },
        symptoms: { ...emptyOncologyReport.symptoms, ...savedReport.symptoms },
        infectionRisk: { ...emptyOncologyReport.infectionRisk, ...savedReport.infectionRisk },
        giNutrition: { ...emptyOncologyReport.giNutrition, ...savedReport.giNutrition },
        skinLines: { ...emptyOncologyReport.skinLines, ...savedReport.skinLines },
        psychosocial: { ...emptyOncologyReport.psychosocial, ...savedReport.psychosocial },
        plan: { ...emptyOncologyReport.plan, ...savedReport.plan },
        notes: { ...emptyOncologyReport.notes, ...savedReport.notes },
      });
    }
  }, [savedReport]);

  const cardBgColor = theme === 'dark' ? '#1e293b' : '#ffffff';
  const borderColor = theme === 'dark' ? '#334155' : '#e2e8f0';
  const headerBgColor = theme === 'dark' ? '#0f172a' : '#f1f5f9';
  const textColor = theme === 'dark' ? '#f1f5f9' : '#1e293b';
  const labelColor = theme === 'dark' ? '#94a3b8' : '#64748b';

  const toggleSection = (section: string) => { setExpandedSections(prev => ({ ...prev, [section]: !prev[section] })); };
  const handleInputChange = (section: keyof OncologyReportData, field: string, value: string) => {
    setReport(prev => {
      const newReport = section === 'otherNotes' ? { ...prev, otherNotes: value } : { ...prev, [section]: { ...(prev[section] as Record<string, string>), [field]: value } };
      onSaveReport(patientData.MRN, newReport);
      return newReport;
    });
  };

  const handleSave = async () => {
    onSaveReport(patientData.MRN, report);
    if (loggedInUser) {
      setIsSaving(true);
      try {
        const submittedBy = `${loggedInUser.first_name} ${loggedInUser.last_name}`;
        await axios.post(`${BACKEND_URL}/report/oncology/save`, {
          patient_name: patientData.Name, mrn: patientData.MRN, submitted_by: submittedBy, email: loggedInUser.email, report_data: report
        });
        setPreviousSubmitter({ name: submittedBy, timestamp: new Date().toISOString() });
        setSaveStatus('Report saved successfully!');
        onReportSubmitted?.();
      } catch (error) {
        console.error('Error saving report to database:', error);
        setSaveStatus('Saved locally (database error)');
      }
      setIsSaving(false);
    } else { setSaveStatus('Report saved locally!'); }
    setTimeout(() => setSaveStatus(''), 3000);
  };

  const styleProps = { cardBgColor, borderColor, textColor, labelColor };
  const formatTimestamp = (timestamp: string) => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
    } catch { return timestamp; }
  };

  if (isLoading) {
    return (<div className="patient-report-container" style={{ backgroundColor: cardBgColor, padding: '40px', textAlign: 'center' }}><div style={{ color: labelColor }}>Loading report...</div></div>);
  }

  return (
    <>
      <div className="report-header-container" style={{ backgroundColor: '#3b82f6', borderRadius: '12px', padding: '20px 24px', marginBottom: '20px' }}>
        <h2 style={{ color: 'white', margin: 0 }}>Report For: {patientData.Name} | {patientData.MRN} | Room {patientData.Room || 'N/A'}</h2>
        {previousSubmitter && (<p style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '14px', margin: '8px 0 0 0' }}>Previous report by: <strong>{previousSubmitter.name}</strong> • {formatTimestamp(previousSubmitter.timestamp)}</p>)}
        {!previousSubmitter && (<p style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '14px', margin: '8px 0 0 0' }}>No previous report found for this patient</p>)}
      </div>
      <div className="patient-report-container" style={{ backgroundColor: cardBgColor, borderRadius: '12px', padding: '24px' }}>
        {/* Patient Overview */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={User} title="Patient Overview" isExpanded={expandedSections.patientOverview} onToggle={() => toggleSection('patientOverview')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.patientOverview && (<div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Age" value={report.patientOverview.age} onChange={(v) => handleInputChange('patientOverview', 'age', v)} placeholder="e.g., 58 y/o" {...styleProps} />
            <InputField label="Cancer Type" value={report.patientOverview.cancerType || patientData.Diagnosis} onChange={(v) => handleInputChange('patientOverview', 'cancerType', v)} placeholder="e.g., AML, Breast CA" {...styleProps} />
            <InputField label="Stage" value={report.patientOverview.stage} onChange={(v) => handleInputChange('patientOverview', 'stage', v)} placeholder="e.g., Stage IV" {...styleProps} />
            <InputField label="Code Status" value={report.patientOverview.codeStatus} onChange={(v) => handleInputChange('patientOverview', 'codeStatus', v)} placeholder="e.g., Full Code" {...styleProps} />
            <InputField label="Allergies" value={report.patientOverview.allergies} onChange={(v) => handleInputChange('patientOverview', 'allergies', v)} placeholder="e.g., NKDA" {...styleProps} />
          </div>)}
        </div>
        {/* Vitals */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={Activity} title="Vitals" isExpanded={expandedSections.vitals} onToggle={() => toggleSection('vitals')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.vitals && (<div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="HR" value={report.vitals.hr} onChange={(v) => handleInputChange('vitals', 'hr', v)} placeholder="e.g., 88" {...styleProps} />
            <InputField label="BP" value={report.vitals.bp} onChange={(v) => handleInputChange('vitals', 'bp', v)} placeholder="e.g., 118/72" {...styleProps} />
            <InputField label="RR" value={report.vitals.rr} onChange={(v) => handleInputChange('vitals', 'rr', v)} placeholder="e.g., 18" {...styleProps} />
            <InputField label="Temp" value={report.vitals.temp} onChange={(v) => handleInputChange('vitals', 'temp', v)} placeholder="e.g., 98.6°F" {...styleProps} />
            <InputField label="SpO2" value={report.vitals.spo2} onChange={(v) => handleInputChange('vitals', 'spo2', v)} placeholder="e.g., 97% RA" {...styleProps} />
            <InputField label="Pain Score" value={report.vitals.painScore} onChange={(v) => handleInputChange('vitals', 'painScore', v)} placeholder="e.g., 4/10" {...styleProps} />
          </div>)}
        </div>
        {/* Treatment Status */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={Pill} title="Treatment Status" isExpanded={expandedSections.treatmentStatus} onToggle={() => toggleSection('treatmentStatus')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.treatmentStatus && (<div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Current Chemo/Radiation" value={report.treatmentStatus.currentChemoRadiation} onChange={(v) => handleInputChange('treatmentStatus', 'currentChemoRadiation', v)} placeholder="e.g., FOLFOX Day 2" {...styleProps} />
            <InputField label="Cycle Number" value={report.treatmentStatus.cycleNumber} onChange={(v) => handleInputChange('treatmentStatus', 'cycleNumber', v)} placeholder="e.g., Cycle 3" {...styleProps} />
            <InputField label="Day Number" value={report.treatmentStatus.dayNumber} onChange={(v) => handleInputChange('treatmentStatus', 'dayNumber', v)} placeholder="e.g., Day 2 of 3" {...styleProps} />
            <InputField label="Central Line Type" value={report.treatmentStatus.centralLineType} onChange={(v) => handleInputChange('treatmentStatus', 'centralLineType', v)} placeholder="e.g., Port, PICC" {...styleProps} />
          </div>)}
        </div>
        {/* Hematology */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={Droplets} title="Hematology" isExpanded={expandedSections.hematology} onToggle={() => toggleSection('hematology')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.hematology && (<div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="WBC" value={report.hematology.wbc} onChange={(v) => handleInputChange('hematology', 'wbc', v)} placeholder="e.g., 2.1" {...styleProps} />
            <InputField label="ANC" value={report.hematology.anc} onChange={(v) => handleInputChange('hematology', 'anc', v)} placeholder="e.g., 800" {...styleProps} />
            <InputField label="Hgb" value={report.hematology.hgb} onChange={(v) => handleInputChange('hematology', 'hgb', v)} placeholder="e.g., 9.2" {...styleProps} />
            <InputField label="Platelets" value={report.hematology.platelets} onChange={(v) => handleInputChange('hematology', 'platelets', v)} placeholder="e.g., 45K" {...styleProps} />
            <InputField label="Neutropenic Precautions" value={report.hematology.neutropenicPrecautions} onChange={(v) => handleInputChange('hematology', 'neutropenicPrecautions', v)} placeholder="e.g., Yes, ANC < 500" {...styleProps} />
          </div>)}
        </div>
        {/* Symptoms */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={AlertCircle} title="Symptoms" isExpanded={expandedSections.symptoms} onToggle={() => toggleSection('symptoms')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.symptoms && (<div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Pain" value={report.symptoms.pain} onChange={(v) => handleInputChange('symptoms', 'pain', v)} placeholder="e.g., Bone pain, managed" {...styleProps} />
            <InputField label="Nausea" value={report.symptoms.nausea} onChange={(v) => handleInputChange('symptoms', 'nausea', v)} placeholder="e.g., Mild, Zofran PRN" {...styleProps} />
            <InputField label="Fatigue" value={report.symptoms.fatigue} onChange={(v) => handleInputChange('symptoms', 'fatigue', v)} placeholder="e.g., Moderate" {...styleProps} />
            <InputField label="Mucositis" value={report.symptoms.mucositis} onChange={(v) => handleInputChange('symptoms', 'mucositis', v)} placeholder="e.g., Grade 2" {...styleProps} />
            <InputField label="Neuropathy" value={report.symptoms.neuropathy} onChange={(v) => handleInputChange('symptoms', 'neuropathy', v)} placeholder="e.g., Mild tingling" {...styleProps} />
          </div>)}
        </div>
        {/* Infection Risk */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={Thermometer} title="Infection Risk" isExpanded={expandedSections.infectionRisk} onToggle={() => toggleSection('infectionRisk')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.infectionRisk && (<div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Temp Trends" value={report.infectionRisk.tempTrends} onChange={(v) => handleInputChange('infectionRisk', 'tempTrends', v)} placeholder="e.g., Afebrile x24h" {...styleProps} />
            <InputField label="Cultures Pending" value={report.infectionRisk.culturesPending} onChange={(v) => handleInputChange('infectionRisk', 'culturesPending', v)} placeholder="e.g., Blood cx pending" {...styleProps} />
            <InputField label="Antibiotics/Antivirals" value={report.infectionRisk.antibioticsAntivirals} onChange={(v) => handleInputChange('infectionRisk', 'antibioticsAntivirals', v)} placeholder="e.g., Cefepime, Acyclovir" {...styleProps} />
          </div>)}
        </div>
        {/* GI/Nutrition */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={Utensils} title="GI / Nutrition" isExpanded={expandedSections.giNutrition} onToggle={() => toggleSection('giNutrition')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.giNutrition && (<div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Diet" value={report.giNutrition.diet} onChange={(v) => handleInputChange('giNutrition', 'diet', v)} placeholder="e.g., Neutropenic diet" {...styleProps} />
            <InputField label="Appetite" value={report.giNutrition.appetite} onChange={(v) => handleInputChange('giNutrition', 'appetite', v)} placeholder="e.g., Poor, 25% intake" {...styleProps} />
            <InputField label="N/V" value={report.giNutrition.nv} onChange={(v) => handleInputChange('giNutrition', 'nv', v)} placeholder="e.g., Controlled" {...styleProps} />
            <InputField label="Bowel Pattern" value={report.giNutrition.bowelPattern} onChange={(v) => handleInputChange('giNutrition', 'bowelPattern', v)} placeholder="e.g., Constipated, Senna" {...styleProps} />
          </div>)}
        </div>
        {/* Skin/Lines */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={Shield} title="Skin / Lines" isExpanded={expandedSections.skinLines} onToggle={() => toggleSection('skinLines')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.skinLines && (<div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Port/PICC Condition" value={report.skinLines.portPiccCondition} onChange={(v) => handleInputChange('skinLines', 'portPiccCondition', v)} placeholder="e.g., PICC intact, no redness" {...styleProps} />
            <InputField label="Skin Integrity" value={report.skinLines.skinIntegrity} onChange={(v) => handleInputChange('skinLines', 'skinIntegrity', v)} placeholder="e.g., Intact, no breakdown" {...styleProps} />
          </div>)}
        </div>
        {/* Psychosocial */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={Heart} title="Psychosocial" isExpanded={expandedSections.psychosocial} onToggle={() => toggleSection('psychosocial')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.psychosocial && (<div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Emotional Status" value={report.psychosocial.emotionalStatus} onChange={(v) => handleInputChange('psychosocial', 'emotionalStatus', v)} placeholder="e.g., Anxious about results" {...styleProps} />
            <InputField label="Support System" value={report.psychosocial.supportSystem} onChange={(v) => handleInputChange('psychosocial', 'supportSystem', v)} placeholder="e.g., Spouse at bedside" {...styleProps} />
          </div>)}
        </div>
        {/* Plan */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={Target} title="Plan" isExpanded={expandedSections.plan} onToggle={() => toggleSection('plan')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.plan && (<div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Next Treatment" value={report.plan.nextTreatment} onChange={(v) => handleInputChange('plan', 'nextTreatment', v)} placeholder="e.g., Day 3 chemo tomorrow" {...styleProps} />
            <InputField label="Labs Pending" value={report.plan.labsPending} onChange={(v) => handleInputChange('plan', 'labsPending', v)} placeholder="e.g., CBC in AM" {...styleProps} />
            <InputField label="Discharge Barriers" value={report.plan.dischargeBarriers} onChange={(v) => handleInputChange('plan', 'dischargeBarriers', v)} placeholder="e.g., ANC recovery" {...styleProps} />
          </div>)}
        </div>
        {/* Notes */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={FileText} title="Notes" isExpanded={expandedSections.notes} onToggle={() => toggleSection('notes')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.notes && (<div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Events This Shift" value={report.notes.events} onChange={(v) => handleInputChange('notes', 'events', v)} placeholder="e.g., Transfusion given" {...styleProps} />
            <InputField label="Provider Updates" value={report.notes.providerUpdates} onChange={(v) => handleInputChange('notes', 'providerUpdates', v)} placeholder="e.g., Onc team rounded" {...styleProps} />
          </div>)}
        </div>
        {/* Other Notes */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={FileText} title="Other Notes" isExpanded={expandedSections.otherNotes} onToggle={() => toggleSection('otherNotes')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.otherNotes && (<div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <div className="report-field"><label style={{ color: labelColor }}>Additional Information</label>
            <textarea value={report.otherNotes} onChange={(e) => handleInputChange('otherNotes', '', e.target.value)} placeholder="Any other information not captured in the fields above..." className="report-other-notes-textarea" style={{ width: '100%', minHeight: '100px', padding: '8px 12px', borderRadius: '6px', color: textColor, resize: 'vertical' }} /></div>
          </div>)}
        </div>
        {/* Save Button */}
        <div style={{ marginTop: '24px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <button onClick={handleSave} disabled={isSaving} style={{ backgroundColor: '#3b82f6', color: 'white', border: 'none', padding: '12px 24px', borderRadius: '8px', fontSize: '16px', fontWeight: '600', cursor: isSaving ? 'not-allowed' : 'pointer', opacity: isSaving ? 0.7 : 1, transition: 'all 0.2s ease' }} onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#2563eb'} onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#3b82f6'}>Save Report</button>
          {saveStatus && (<p style={{ color: saveStatus.includes('Error') ? '#ef4444' : '#10b981', marginTop: '8px', fontSize: '14px' }}>{saveStatus}</p>)}
        </div>
      </div>
    </>
  );
};

// ============================================
// Neurology Report Component
// ============================================
const NeurologyReport: React.FC<{
  patientData: Patient;
  savedReport: NeurologyReportData | undefined;
  onSaveReport: (mrn: string, report: NeurologyReportData) => void;
  loggedInUser: { first_name: string; last_name: string; email: string } | null;
  onReportSubmitted?: () => void;
}> = ({ patientData, savedReport, onSaveReport, loggedInUser, onReportSubmitted }) => {
  const { theme } = useTheme();
  // Merge savedReport with empty template to ensure all nested objects exist
  const [report, setReport] = useState<NeurologyReportData>(() => savedReport ? {
    ...emptyNeurologyReport,
    ...savedReport,
    patientOverview: { ...emptyNeurologyReport.patientOverview, ...savedReport.patientOverview },
    vitals: { ...emptyNeurologyReport.vitals, ...savedReport.vitals },
    neuroAssessment: { ...emptyNeurologyReport.neuroAssessment, ...savedReport.neuroAssessment },
    seizureManagement: { ...emptyNeurologyReport.seizureManagement, ...savedReport.seizureManagement },
    mobilitySafety: { ...emptyNeurologyReport.mobilitySafety, ...savedReport.mobilitySafety },
    icpMonitoring: { ...emptyNeurologyReport.icpMonitoring, ...savedReport.icpMonitoring },
    swallowSpeech: { ...emptyNeurologyReport.swallowSpeech, ...savedReport.swallowSpeech },
    elimination: { ...emptyNeurologyReport.elimination, ...savedReport.elimination },
    plan: { ...emptyNeurologyReport.plan, ...savedReport.plan },
    notes: { ...emptyNeurologyReport.notes, ...savedReport.notes },
  } : emptyNeurologyReport);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    patientOverview: true, vitals: true, neuroAssessment: true, seizureManagement: true,
    mobilitySafety: true, icpMonitoring: true, swallowSpeech: true, elimination: true,
    plan: true, notes: true, otherNotes: true
  });
  const [saveStatus, setSaveStatus] = useState<string>('');
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [previousSubmitter, setPreviousSubmitter] = useState<{ name: string; timestamp: string } | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Auto-populate age from patient DOB
  useEffect(() => {
    if (patientData?.DOB && !report.patientOverview.age) {
      const calculatedAge = calculateAgeFromDOB(patientData.DOB);
      if (calculatedAge) {
        setReport(prev => ({
          ...prev,
          patientOverview: { ...prev.patientOverview, age: calculatedAge }
        }));
      }
    }
  }, [patientData?.DOB]);

  useEffect(() => {
    const fetchSavedReport = async () => {
      if (!patientData?.MRN) { setIsLoading(false); return; }
      if (savedReport) {
        setReport({
          ...emptyNeurologyReport,
          ...savedReport,
          patientOverview: { ...emptyNeurologyReport.patientOverview, ...savedReport.patientOverview },
          vitals: { ...emptyNeurologyReport.vitals, ...savedReport.vitals },
          neuroAssessment: { ...emptyNeurologyReport.neuroAssessment, ...savedReport.neuroAssessment },
          seizureManagement: { ...emptyNeurologyReport.seizureManagement, ...savedReport.seizureManagement },
          mobilitySafety: { ...emptyNeurologyReport.mobilitySafety, ...savedReport.mobilitySafety },
          icpMonitoring: { ...emptyNeurologyReport.icpMonitoring, ...savedReport.icpMonitoring },
          swallowSpeech: { ...emptyNeurologyReport.swallowSpeech, ...savedReport.swallowSpeech },
          elimination: { ...emptyNeurologyReport.elimination, ...savedReport.elimination },
          plan: { ...emptyNeurologyReport.plan, ...savedReport.plan },
          notes: { ...emptyNeurologyReport.notes, ...savedReport.notes },
        });
        setIsLoading(false);
        try {
          const response = await axios.get<{ success: boolean; report?: { submitted_by: string; timestamp: string; }; }>(`${BACKEND_URL}/report/neurology/${patientData.MRN}`);
          if (response.data.success && response.data.report) {
            setPreviousSubmitter({ name: response.data.report.submitted_by, timestamp: response.data.report.timestamp });
          }
        } catch { /* ignore */ }
        return;
      }
      setIsLoading(true);
      try {
        const response = await axios.get<{ success: boolean; report?: { submitted_by: string; timestamp: string; report_data: NeurologyReportData; }; }>(`${BACKEND_URL}/report/neurology/${patientData.MRN}`);
        if (response.data.success && response.data.report) {
          const dbReport = response.data.report;
          setReport(dbReport.report_data as NeurologyReportData);
          onSaveReport(patientData.MRN, dbReport.report_data as NeurologyReportData);
          setPreviousSubmitter({ name: dbReport.submitted_by, timestamp: dbReport.timestamp });
        } else {
          setReport(emptyNeurologyReport);
          setPreviousSubmitter(null);
        }
      } catch (error) {
        setReport(emptyNeurologyReport);
        setPreviousSubmitter(null);
      }
      setIsLoading(false);
    };
    fetchSavedReport();
  }, [patientData?.MRN, savedReport]);

  useEffect(() => {
    if (savedReport) {
      setReport({
        ...emptyNeurologyReport,
        ...savedReport,
        patientOverview: { ...emptyNeurologyReport.patientOverview, ...savedReport.patientOverview },
        vitals: { ...emptyNeurologyReport.vitals, ...savedReport.vitals },
        neuroAssessment: { ...emptyNeurologyReport.neuroAssessment, ...savedReport.neuroAssessment },
        seizureManagement: { ...emptyNeurologyReport.seizureManagement, ...savedReport.seizureManagement },
        mobilitySafety: { ...emptyNeurologyReport.mobilitySafety, ...savedReport.mobilitySafety },
        icpMonitoring: { ...emptyNeurologyReport.icpMonitoring, ...savedReport.icpMonitoring },
        swallowSpeech: { ...emptyNeurologyReport.swallowSpeech, ...savedReport.swallowSpeech },
        elimination: { ...emptyNeurologyReport.elimination, ...savedReport.elimination },
        plan: { ...emptyNeurologyReport.plan, ...savedReport.plan },
        notes: { ...emptyNeurologyReport.notes, ...savedReport.notes },
      });
    }
  }, [savedReport]);

  const cardBgColor = theme === 'dark' ? '#1e293b' : '#ffffff';
  const borderColor = theme === 'dark' ? '#334155' : '#e2e8f0';
  const headerBgColor = theme === 'dark' ? '#0f172a' : '#f1f5f9';
  const textColor = theme === 'dark' ? '#f1f5f9' : '#1e293b';
  const labelColor = theme === 'dark' ? '#94a3b8' : '#64748b';

  const toggleSection = (section: string) => { setExpandedSections(prev => ({ ...prev, [section]: !prev[section] })); };
  const handleInputChange = (section: keyof NeurologyReportData, field: string, value: string) => {
    setReport(prev => {
      const newReport = section === 'otherNotes' ? { ...prev, otherNotes: value } : { ...prev, [section]: { ...(prev[section] as Record<string, string>), [field]: value } };
      onSaveReport(patientData.MRN, newReport);
      return newReport;
    });
  };

  const handleSave = async () => {
    onSaveReport(patientData.MRN, report);
    if (loggedInUser) {
      setIsSaving(true);
      try {
        const submittedBy = `${loggedInUser.first_name} ${loggedInUser.last_name}`;
        await axios.post(`${BACKEND_URL}/report/neurology/save`, {
          patient_name: patientData.Name, mrn: patientData.MRN, submitted_by: submittedBy, email: loggedInUser.email, report_data: report
        });
        setPreviousSubmitter({ name: submittedBy, timestamp: new Date().toISOString() });
        setSaveStatus('Report saved successfully!');
        onReportSubmitted?.();
      } catch (error) {
        console.error('Error saving report to database:', error);
        setSaveStatus('Saved locally (database error)');
      }
      setIsSaving(false);
    } else { setSaveStatus('Report saved locally!'); }
    setTimeout(() => setSaveStatus(''), 3000);
  };

  const styleProps = { cardBgColor, borderColor, textColor, labelColor };
  const formatTimestamp = (timestamp: string) => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
    } catch { return timestamp; }
  };

  if (isLoading) {
    return (<div className="patient-report-container" style={{ backgroundColor: cardBgColor, padding: '40px', textAlign: 'center' }}><div style={{ color: labelColor }}>Loading report...</div></div>);
  }

  return (
    <>
      <div className="report-header-container" style={{ backgroundColor: '#3b82f6', borderRadius: '12px', padding: '20px 24px', marginBottom: '20px' }}>
        <h2 style={{ color: 'white', margin: 0 }}>Report For: {patientData.Name} | {patientData.MRN} | Room {patientData.Room || 'N/A'}</h2>
        {previousSubmitter && (<p style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '14px', margin: '8px 0 0 0' }}>Previous report by: <strong>{previousSubmitter.name}</strong> • {formatTimestamp(previousSubmitter.timestamp)}</p>)}
        {!previousSubmitter && (<p style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '14px', margin: '8px 0 0 0' }}>No previous report found for this patient</p>)}
      </div>
      <div className="patient-report-container" style={{ backgroundColor: cardBgColor, borderRadius: '12px', padding: '24px' }}>
        {/* Patient Overview */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={User} title="Patient Overview" isExpanded={expandedSections.patientOverview} onToggle={() => toggleSection('patientOverview')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.patientOverview && (<div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Age" value={report.patientOverview.age} onChange={(v) => handleInputChange('patientOverview', 'age', v)} placeholder="e.g., 65 y/o" {...styleProps} />
            <InputField label="Primary Neuro Dx" value={report.patientOverview.primaryNeuroDx || patientData.Diagnosis} onChange={(v) => handleInputChange('patientOverview', 'primaryNeuroDx', v)} placeholder="e.g., CVA, Seizure disorder" {...styleProps} />
            <InputField label="Code Status" value={report.patientOverview.codeStatus} onChange={(v) => handleInputChange('patientOverview', 'codeStatus', v)} placeholder="e.g., Full Code" {...styleProps} />
            <InputField label="Allergies" value={report.patientOverview.allergies} onChange={(v) => handleInputChange('patientOverview', 'allergies', v)} placeholder="e.g., NKDA" {...styleProps} />
          </div>)}
        </div>
        {/* Vitals */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={Activity} title="Vitals" isExpanded={expandedSections.vitals} onToggle={() => toggleSection('vitals')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.vitals && (<div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="HR" value={report.vitals.hr} onChange={(v) => handleInputChange('vitals', 'hr', v)} placeholder="e.g., 72" {...styleProps} />
            <InputField label="BP" value={report.vitals.bp} onChange={(v) => handleInputChange('vitals', 'bp', v)} placeholder="e.g., 140/85" {...styleProps} />
            <InputField label="RR" value={report.vitals.rr} onChange={(v) => handleInputChange('vitals', 'rr', v)} placeholder="e.g., 16" {...styleProps} />
            <InputField label="Temp" value={report.vitals.temp} onChange={(v) => handleInputChange('vitals', 'temp', v)} placeholder="e.g., 98.2°F" {...styleProps} />
            <InputField label="SpO2" value={report.vitals.spo2} onChange={(v) => handleInputChange('vitals', 'spo2', v)} placeholder="e.g., 98% RA" {...styleProps} />
          </div>)}
        </div>
        {/* Neuro Assessment */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={Brain} title="Neuro Assessment" isExpanded={expandedSections.neuroAssessment} onToggle={() => toggleSection('neuroAssessment')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.neuroAssessment && (<div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="LOC" value={report.neuroAssessment.loc} onChange={(v) => handleInputChange('neuroAssessment', 'loc', v)} placeholder="e.g., Alert, Drowsy" {...styleProps} />
            <InputField label="Orientation" value={report.neuroAssessment.orientation} onChange={(v) => handleInputChange('neuroAssessment', 'orientation', v)} placeholder="e.g., A&Ox3" {...styleProps} />
            <InputField label="GCS/NIHSS" value={report.neuroAssessment.gcsNihss} onChange={(v) => handleInputChange('neuroAssessment', 'gcsNihss', v)} placeholder="e.g., GCS 15, NIHSS 4" {...styleProps} />
            <InputField label="Pupils" value={report.neuroAssessment.pupils} onChange={(v) => handleInputChange('neuroAssessment', 'pupils', v)} placeholder="e.g., PERRLA 3mm" {...styleProps} />
            <InputField label="Motor" value={report.neuroAssessment.motor} onChange={(v) => handleInputChange('neuroAssessment', 'motor', v)} placeholder="e.g., L sided weakness 3/5" {...styleProps} />
            <InputField label="Sensation" value={report.neuroAssessment.sensation} onChange={(v) => handleInputChange('neuroAssessment', 'sensation', v)} placeholder="e.g., Intact bilaterally" {...styleProps} />
          </div>)}
        </div>
        {/* Seizure Management */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={Zap} title="Seizure Management" isExpanded={expandedSections.seizureManagement} onToggle={() => toggleSection('seizureManagement')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.seizureManagement && (<div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Seizure Precautions" value={report.seizureManagement.seizurePrecautions} onChange={(v) => handleInputChange('seizureManagement', 'seizurePrecautions', v)} placeholder="e.g., Yes, bed low, padded rails" {...styleProps} />
            <InputField label="Last Seizure" value={report.seizureManagement.lastSeizure} onChange={(v) => handleInputChange('seizureManagement', 'lastSeizure', v)} placeholder="e.g., 2 days ago, GTC" {...styleProps} />
            <InputField label="AEDs" value={report.seizureManagement.aeds} onChange={(v) => handleInputChange('seizureManagement', 'aeds', v)} placeholder="e.g., Keppra 1000mg BID" {...styleProps} />
          </div>)}
        </div>
        {/* Mobility/Safety */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={Accessibility} title="Mobility / Safety" isExpanded={expandedSections.mobilitySafety} onToggle={() => toggleSection('mobilitySafety')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.mobilitySafety && (<div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Assist Level" value={report.mobilitySafety.assistLevel} onChange={(v) => handleInputChange('mobilitySafety', 'assistLevel', v)} placeholder="e.g., Max assist x2" {...styleProps} />
            <InputField label="Fall Risk" value={report.mobilitySafety.fallRisk} onChange={(v) => handleInputChange('mobilitySafety', 'fallRisk', v)} placeholder="e.g., High, bed alarm on" {...styleProps} />
            <InputField label="Aspiration Risk" value={report.mobilitySafety.aspirationRisk} onChange={(v) => handleInputChange('mobilitySafety', 'aspirationRisk', v)} placeholder="e.g., Yes, HOB elevated" {...styleProps} />
          </div>)}
        </div>
        {/* ICP Monitoring */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={TrendingUp} title="ICP Monitoring" isExpanded={expandedSections.icpMonitoring} onToggle={() => toggleSection('icpMonitoring')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.icpMonitoring && (<div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="ICP Monitor" value={report.icpMonitoring.icpMonitor} onChange={(v) => handleInputChange('icpMonitoring', 'icpMonitor', v)} placeholder="e.g., EVD in place, ICP 12" {...styleProps} />
            <InputField label="Neuro Checks Frequency" value={report.icpMonitoring.neuroChecksFrequency} onChange={(v) => handleInputChange('icpMonitoring', 'neuroChecksFrequency', v)} placeholder="e.g., Q1h" {...styleProps} />
          </div>)}
        </div>
        {/* Swallow/Speech */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={MessageSquare} title="Swallow / Speech" isExpanded={expandedSections.swallowSpeech} onToggle={() => toggleSection('swallowSpeech')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.swallowSpeech && (<div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Swallow Eval" value={report.swallowSpeech.swallowEval} onChange={(v) => handleInputChange('swallowSpeech', 'swallowEval', v)} placeholder="e.g., Passed bedside eval" {...styleProps} />
            <InputField label="Diet Consistency" value={report.swallowSpeech.dietConsistency} onChange={(v) => handleInputChange('swallowSpeech', 'dietConsistency', v)} placeholder="e.g., Mechanical soft, thin liquids" {...styleProps} />
          </div>)}
        </div>
        {/* Elimination */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={Droplets} title="Elimination" isExpanded={expandedSections.elimination} onToggle={() => toggleSection('elimination')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.elimination && (<div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Bladder" value={report.elimination.bladder} onChange={(v) => handleInputChange('elimination', 'bladder', v)} placeholder="e.g., Foley, voiding independently" {...styleProps} />
            <InputField label="Bowel Function" value={report.elimination.bowelFunction} onChange={(v) => handleInputChange('elimination', 'bowelFunction', v)} placeholder="e.g., Regular, constipated" {...styleProps} />
          </div>)}
        </div>
        {/* Plan */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={Target} title="Plan" isExpanded={expandedSections.plan} onToggle={() => toggleSection('plan')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.plan && (<div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Imaging Pending" value={report.plan.imagingPending} onChange={(v) => handleInputChange('plan', 'imagingPending', v)} placeholder="e.g., MRI brain tomorrow" {...styleProps} />
            <InputField label="Neuro Consults" value={report.plan.neuroConsults} onChange={(v) => handleInputChange('plan', 'neuroConsults', v)} placeholder="e.g., Stroke team following" {...styleProps} />
            <InputField label="Therapy Orders" value={report.plan.therapyOrders} onChange={(v) => handleInputChange('plan', 'therapyOrders', v)} placeholder="e.g., PT/OT/SLP daily" {...styleProps} />
          </div>)}
        </div>
        {/* Notes */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={FileText} title="Notes" isExpanded={expandedSections.notes} onToggle={() => toggleSection('notes')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.notes && (<div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Changes from Baseline" value={report.notes.changesFromBaseline} onChange={(v) => handleInputChange('notes', 'changesFromBaseline', v)} placeholder="e.g., More alert today" {...styleProps} />
          </div>)}
        </div>
        {/* Other Notes */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={FileText} title="Other Notes" isExpanded={expandedSections.otherNotes} onToggle={() => toggleSection('otherNotes')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.otherNotes && (<div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <div className="report-field"><label style={{ color: labelColor }}>Additional Information</label>
            <textarea value={report.otherNotes} onChange={(e) => handleInputChange('otherNotes', '', e.target.value)} placeholder="Any other information not captured in the fields above..." className="report-other-notes-textarea" style={{ width: '100%', minHeight: '100px', padding: '8px 12px', borderRadius: '6px', color: textColor, resize: 'vertical' }} /></div>
          </div>)}
        </div>
        {/* Save Button */}
        <div style={{ marginTop: '24px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <button onClick={handleSave} disabled={isSaving} style={{ backgroundColor: '#3b82f6', color: 'white', border: 'none', padding: '12px 24px', borderRadius: '8px', fontSize: '16px', fontWeight: '600', cursor: isSaving ? 'not-allowed' : 'pointer', opacity: isSaving ? 0.7 : 1, transition: 'all 0.2s ease' }} onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#2563eb'} onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#3b82f6'}>Save Report</button>
          {saveStatus && (<p style={{ color: saveStatus.includes('Error') ? '#ef4444' : '#10b981', marginTop: '8px', fontSize: '14px' }}>{saveStatus}</p>)}
        </div>
      </div>
    </>
  );
};

// ============================================
// Cardiology Report Component
// ============================================
const CardiologyReport: React.FC<{
  patientData: Patient;
  savedReport: CardiologyReportData | undefined;
  onSaveReport: (mrn: string, report: CardiologyReportData) => void;
  loggedInUser: { first_name: string; last_name: string; email: string } | null;
  onReportSubmitted?: () => void;
}> = ({ patientData, savedReport, onSaveReport, loggedInUser, onReportSubmitted }) => {
  const { theme } = useTheme();
  // Merge savedReport with empty template to ensure all nested objects exist
  const [report, setReport] = useState<CardiologyReportData>(() => savedReport ? {
    ...emptyCardiologyReport,
    ...savedReport,
    patientOverview: { ...emptyCardiologyReport.patientOverview, ...savedReport.patientOverview },
    vitals: { ...emptyCardiologyReport.vitals, ...savedReport.vitals },
    cardiacMonitoring: { ...emptyCardiologyReport.cardiacMonitoring, ...savedReport.cardiacMonitoring },
    perfusion: { ...emptyCardiologyReport.perfusion, ...savedReport.perfusion },
    respiratory: { ...emptyCardiologyReport.respiratory, ...savedReport.respiratory },
    medications: { ...emptyCardiologyReport.medications, ...savedReport.medications },
    fluids: { ...emptyCardiologyReport.fluids, ...savedReport.fluids },
    activity: { ...emptyCardiologyReport.activity, ...savedReport.activity },
    procedures: { ...emptyCardiologyReport.procedures, ...savedReport.procedures },
    plan: { ...emptyCardiologyReport.plan, ...savedReport.plan },
    notes: { ...emptyCardiologyReport.notes, ...savedReport.notes },
  } : emptyCardiologyReport);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    patientOverview: true, vitals: true, cardiacMonitoring: true, perfusion: true,
    respiratory: true, medications: true, fluids: true, activity: true,
    procedures: true, plan: true, notes: true, otherNotes: true
  });
  const [saveStatus, setSaveStatus] = useState<string>('');
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [previousSubmitter, setPreviousSubmitter] = useState<{ name: string; timestamp: string } | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Auto-populate age from patient DOB
  useEffect(() => {
    if (patientData?.DOB && !report.patientOverview.age) {
      const calculatedAge = calculateAgeFromDOB(patientData.DOB);
      if (calculatedAge) {
        setReport(prev => ({
          ...prev,
          patientOverview: { ...prev.patientOverview, age: calculatedAge }
        }));
      }
    }
  }, [patientData?.DOB]);

  useEffect(() => {
    const fetchSavedReport = async () => {
      if (!patientData?.MRN) { setIsLoading(false); return; }
      if (savedReport) {
        setReport({
          ...emptyCardiologyReport,
          ...savedReport,
          patientOverview: { ...emptyCardiologyReport.patientOverview, ...savedReport.patientOverview },
          vitals: { ...emptyCardiologyReport.vitals, ...savedReport.vitals },
          cardiacMonitoring: { ...emptyCardiologyReport.cardiacMonitoring, ...savedReport.cardiacMonitoring },
          perfusion: { ...emptyCardiologyReport.perfusion, ...savedReport.perfusion },
          respiratory: { ...emptyCardiologyReport.respiratory, ...savedReport.respiratory },
          medications: { ...emptyCardiologyReport.medications, ...savedReport.medications },
          fluids: { ...emptyCardiologyReport.fluids, ...savedReport.fluids },
          activity: { ...emptyCardiologyReport.activity, ...savedReport.activity },
          procedures: { ...emptyCardiologyReport.procedures, ...savedReport.procedures },
          plan: { ...emptyCardiologyReport.plan, ...savedReport.plan },
          notes: { ...emptyCardiologyReport.notes, ...savedReport.notes },
        });
        setIsLoading(false);
        try {
          const response = await axios.get<{ success: boolean; report?: { submitted_by: string; timestamp: string; }; }>(`${BACKEND_URL}/report/cardiology/${patientData.MRN}`);
          if (response.data.success && response.data.report) {
            setPreviousSubmitter({ name: response.data.report.submitted_by, timestamp: response.data.report.timestamp });
          }
        } catch { /* ignore */ }
        return;
      }
      setIsLoading(true);
      try {
        const response = await axios.get<{ success: boolean; report?: { submitted_by: string; timestamp: string; report_data: CardiologyReportData; }; }>(`${BACKEND_URL}/report/cardiology/${patientData.MRN}`);
        if (response.data.success && response.data.report) {
          const dbReport = response.data.report;
          setReport(dbReport.report_data as CardiologyReportData);
          onSaveReport(patientData.MRN, dbReport.report_data as CardiologyReportData);
          setPreviousSubmitter({ name: dbReport.submitted_by, timestamp: dbReport.timestamp });
        } else {
          setReport(emptyCardiologyReport);
          setPreviousSubmitter(null);
        }
      } catch (error) {
        setReport(emptyCardiologyReport);
        setPreviousSubmitter(null);
      }
      setIsLoading(false);
    };
    fetchSavedReport();
  }, [patientData?.MRN, savedReport]);

  useEffect(() => {
    if (savedReport) {
      setReport({
        ...emptyCardiologyReport,
        ...savedReport,
        patientOverview: { ...emptyCardiologyReport.patientOverview, ...savedReport.patientOverview },
        vitals: { ...emptyCardiologyReport.vitals, ...savedReport.vitals },
        cardiacMonitoring: { ...emptyCardiologyReport.cardiacMonitoring, ...savedReport.cardiacMonitoring },
        perfusion: { ...emptyCardiologyReport.perfusion, ...savedReport.perfusion },
        respiratory: { ...emptyCardiologyReport.respiratory, ...savedReport.respiratory },
        medications: { ...emptyCardiologyReport.medications, ...savedReport.medications },
        fluids: { ...emptyCardiologyReport.fluids, ...savedReport.fluids },
        activity: { ...emptyCardiologyReport.activity, ...savedReport.activity },
        procedures: { ...emptyCardiologyReport.procedures, ...savedReport.procedures },
        plan: { ...emptyCardiologyReport.plan, ...savedReport.plan },
        notes: { ...emptyCardiologyReport.notes, ...savedReport.notes },
      });
    }
  }, [savedReport]);

  const cardBgColor = theme === 'dark' ? '#1e293b' : '#ffffff';
  const borderColor = theme === 'dark' ? '#334155' : '#e2e8f0';
  const headerBgColor = theme === 'dark' ? '#0f172a' : '#f1f5f9';
  const textColor = theme === 'dark' ? '#f1f5f9' : '#1e293b';
  const labelColor = theme === 'dark' ? '#94a3b8' : '#64748b';

  const toggleSection = (section: string) => { setExpandedSections(prev => ({ ...prev, [section]: !prev[section] })); };
  const handleInputChange = (section: keyof CardiologyReportData, field: string, value: string) => {
    setReport(prev => {
      const newReport = section === 'otherNotes' ? { ...prev, otherNotes: value } : { ...prev, [section]: { ...(prev[section] as Record<string, string>), [field]: value } };
      onSaveReport(patientData.MRN, newReport);
      return newReport;
    });
  };

  const handleSave = async () => {
    onSaveReport(patientData.MRN, report);
    if (loggedInUser) {
      setIsSaving(true);
      try {
        const submittedBy = `${loggedInUser.first_name} ${loggedInUser.last_name}`;
        await axios.post(`${BACKEND_URL}/report/cardiology/save`, {
          patient_name: patientData.Name, mrn: patientData.MRN, submitted_by: submittedBy, email: loggedInUser.email, report_data: report
        });
        setPreviousSubmitter({ name: submittedBy, timestamp: new Date().toISOString() });
        setSaveStatus('Report saved successfully!');
        onReportSubmitted?.();
      } catch (error) {
        console.error('Error saving report to database:', error);
        setSaveStatus('Saved locally (database error)');
      }
      setIsSaving(false);
    } else { setSaveStatus('Report saved locally!'); }
    setTimeout(() => setSaveStatus(''), 3000);
  };

  const styleProps = { cardBgColor, borderColor, textColor, labelColor };
  const formatTimestamp = (timestamp: string) => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
    } catch { return timestamp; }
  };

  if (isLoading) {
    return (<div className="patient-report-container" style={{ backgroundColor: cardBgColor, padding: '40px', textAlign: 'center' }}><div style={{ color: labelColor }}>Loading report...</div></div>);
  }

  return (
    <>
      <div className="report-header-container" style={{ backgroundColor: '#3b82f6', borderRadius: '12px', padding: '20px 24px', marginBottom: '20px' }}>
        <h2 style={{ color: 'white', margin: 0 }}>Report For: {patientData.Name} | {patientData.MRN} | Room {patientData.Room || 'N/A'}</h2>
        {previousSubmitter && (<p style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '14px', margin: '8px 0 0 0' }}>Previous report by: <strong>{previousSubmitter.name}</strong> • {formatTimestamp(previousSubmitter.timestamp)}</p>)}
        {!previousSubmitter && (<p style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '14px', margin: '8px 0 0 0' }}>No previous report found for this patient</p>)}
      </div>
      <div className="patient-report-container" style={{ backgroundColor: cardBgColor, borderRadius: '12px', padding: '24px' }}>
        {/* Patient Overview */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={User} title="Patient Overview" isExpanded={expandedSections.patientOverview} onToggle={() => toggleSection('patientOverview')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.patientOverview && (<div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Age" value={report.patientOverview.age} onChange={(v) => handleInputChange('patientOverview', 'age', v)} placeholder="e.g., 72 y/o" {...styleProps} />
            <InputField label="Cardiac Dx" value={report.patientOverview.cardiacDx || patientData.Diagnosis} onChange={(v) => handleInputChange('patientOverview', 'cardiacDx', v)} placeholder="e.g., CHF, STEMI" {...styleProps} />
            <InputField label="Code Status" value={report.patientOverview.codeStatus} onChange={(v) => handleInputChange('patientOverview', 'codeStatus', v)} placeholder="e.g., Full Code" {...styleProps} />
            <InputField label="Allergies" value={report.patientOverview.allergies} onChange={(v) => handleInputChange('patientOverview', 'allergies', v)} placeholder="e.g., NKDA" {...styleProps} />
          </div>)}
        </div>
        {/* Vitals */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={Activity} title="Vitals" isExpanded={expandedSections.vitals} onToggle={() => toggleSection('vitals')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.vitals && (<div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="HR" value={report.vitals.hr} onChange={(v) => handleInputChange('vitals', 'hr', v)} placeholder="e.g., 78" {...styleProps} />
            <InputField label="BP" value={report.vitals.bp} onChange={(v) => handleInputChange('vitals', 'bp', v)} placeholder="e.g., 130/80" {...styleProps} />
            <InputField label="RR" value={report.vitals.rr} onChange={(v) => handleInputChange('vitals', 'rr', v)} placeholder="e.g., 18" {...styleProps} />
            <InputField label="SpO2" value={report.vitals.spo2} onChange={(v) => handleInputChange('vitals', 'spo2', v)} placeholder="e.g., 96% 2L NC" {...styleProps} />
            <InputField label="Temp" value={report.vitals.temp} onChange={(v) => handleInputChange('vitals', 'temp', v)} placeholder="e.g., 98.4°F" {...styleProps} />
            <InputField label="Pain/Chest" value={report.vitals.painChest} onChange={(v) => handleInputChange('vitals', 'painChest', v)} placeholder="e.g., No chest pain" {...styleProps} />
          </div>)}
        </div>
        {/* Cardiac Monitoring */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={Heart} title="Cardiac Monitoring" isExpanded={expandedSections.cardiacMonitoring} onToggle={() => toggleSection('cardiacMonitoring')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.cardiacMonitoring && (<div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Rhythm" value={report.cardiacMonitoring.rhythm} onChange={(v) => handleInputChange('cardiacMonitoring', 'rhythm', v)} placeholder="e.g., NSR, AFib RVR" {...styleProps} />
            <InputField label="Telemetry Alarms" value={report.cardiacMonitoring.telemetryAlarms} onChange={(v) => handleInputChange('cardiacMonitoring', 'telemetryAlarms', v)} placeholder="e.g., None, PVCs noted" {...styleProps} />
            <InputField label="EKG Changes" value={report.cardiacMonitoring.ekgChanges} onChange={(v) => handleInputChange('cardiacMonitoring', 'ekgChanges', v)} placeholder="e.g., ST elevation V1-V4" {...styleProps} />
          </div>)}
        </div>
        {/* Perfusion */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={Droplets} title="Perfusion" isExpanded={expandedSections.perfusion} onToggle={() => toggleSection('perfusion')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.perfusion && (<div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Cap Refill" value={report.perfusion.capRefill} onChange={(v) => handleInputChange('perfusion', 'capRefill', v)} placeholder="e.g., < 2 sec" {...styleProps} />
            <InputField label="Edema" value={report.perfusion.edema} onChange={(v) => handleInputChange('perfusion', 'edema', v)} placeholder="e.g., 2+ bilateral LE" {...styleProps} />
            <InputField label="Pulses" value={report.perfusion.pulses} onChange={(v) => handleInputChange('perfusion', 'pulses', v)} placeholder="e.g., 2+ pedal pulses" {...styleProps} />
          </div>)}
        </div>
        {/* Respiratory */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={Wind} title="Respiratory" isExpanded={expandedSections.respiratory} onToggle={() => toggleSection('respiratory')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.respiratory && (<div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="O2 Support" value={report.respiratory.o2Support} onChange={(v) => handleInputChange('respiratory', 'o2Support', v)} placeholder="e.g., 2L NC" {...styleProps} />
            <InputField label="Breath Sounds" value={report.respiratory.breathSounds} onChange={(v) => handleInputChange('respiratory', 'breathSounds', v)} placeholder="e.g., Crackles bases bilat" {...styleProps} />
          </div>)}
        </div>
        {/* Medications */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={Pill} title="Medications" isExpanded={expandedSections.medications} onToggle={() => toggleSection('medications')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.medications && (<div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Cardiac Drips" value={report.medications.cardiacDrips} onChange={(v) => handleInputChange('medications', 'cardiacDrips', v)} placeholder="e.g., Heparin gtt, Amio gtt" {...styleProps} />
            <InputField label="Anticoagulants" value={report.medications.anticoagulants} onChange={(v) => handleInputChange('medications', 'anticoagulants', v)} placeholder="e.g., Heparin, Eliquis" {...styleProps} />
            <InputField label="PRNs" value={report.medications.prns} onChange={(v) => handleInputChange('medications', 'prns', v)} placeholder="e.g., NTG SL PRN" {...styleProps} />
          </div>)}
        </div>
        {/* Fluids */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={Droplets} title="Fluids" isExpanded={expandedSections.fluids} onToggle={() => toggleSection('fluids')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.fluids && (<div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="IV Fluids" value={report.fluids.ivFluids} onChange={(v) => handleInputChange('fluids', 'ivFluids', v)} placeholder="e.g., NS @ 75ml/hr" {...styleProps} />
            <InputField label="I/O Balance" value={report.fluids.ioBalance} onChange={(v) => handleInputChange('fluids', 'ioBalance', v)} placeholder="e.g., +500ml" {...styleProps} />
            <InputField label="Daily Weight" value={report.fluids.dailyWeight} onChange={(v) => handleInputChange('fluids', 'dailyWeight', v)} placeholder="e.g., 82kg (+1kg)" {...styleProps} />
          </div>)}
        </div>
        {/* Activity */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={Accessibility} title="Activity" isExpanded={expandedSections.activity} onToggle={() => toggleSection('activity')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.activity && (<div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Activity Orders" value={report.activity.activityOrders} onChange={(v) => handleInputChange('activity', 'activityOrders', v)} placeholder="e.g., Bedrest, OOB to chair" {...styleProps} />
            <InputField label="Response to Activity" value={report.activity.responseToActivity} onChange={(v) => handleInputChange('activity', 'responseToActivity', v)} placeholder="e.g., Tolerated well, SOB with exertion" {...styleProps} />
          </div>)}
        </div>
        {/* Procedures */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={Scissors} title="Procedures" isExpanded={expandedSections.procedures} onToggle={() => toggleSection('procedures')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.procedures && (<div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Recent Cath/Surgery" value={report.procedures.recentCathSurgery} onChange={(v) => handleInputChange('procedures', 'recentCathSurgery', v)} placeholder="e.g., PCI yesterday, stent x2" {...styleProps} />
            <InputField label="Site Checks" value={report.procedures.siteChecks} onChange={(v) => handleInputChange('procedures', 'siteChecks', v)} placeholder="e.g., R groin - no hematoma" {...styleProps} />
          </div>)}
        </div>
        {/* Plan */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={Target} title="Plan" isExpanded={expandedSections.plan} onToggle={() => toggleSection('plan')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.plan && (<div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Cardiology Goals" value={report.plan.cardiologyGoals} onChange={(v) => handleInputChange('plan', 'cardiologyGoals', v)} placeholder="e.g., Optimize CHF meds" {...styleProps} />
            <InputField label="Tests Pending" value={report.plan.testsPending} onChange={(v) => handleInputChange('plan', 'testsPending', v)} placeholder="e.g., Echo tomorrow" {...styleProps} />
            <InputField label="Discharge Planning" value={report.plan.dischargePlanning} onChange={(v) => handleInputChange('plan', 'dischargePlanning', v)} placeholder="e.g., Pending PT eval" {...styleProps} />
          </div>)}
        </div>
        {/* Notes */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={FileText} title="Notes" isExpanded={expandedSections.notes} onToggle={() => toggleSection('notes')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.notes && (<div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Events This Shift" value={report.notes.events} onChange={(v) => handleInputChange('notes', 'events', v)} placeholder="e.g., Converted to NSR" {...styleProps} />
            <InputField label="Rhythm Changes" value={report.notes.rhythmChanges} onChange={(v) => handleInputChange('notes', 'rhythmChanges', v)} placeholder="e.g., Brief run of VT" {...styleProps} />
          </div>)}
        </div>
        {/* Other Notes */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={FileText} title="Other Notes" isExpanded={expandedSections.otherNotes} onToggle={() => toggleSection('otherNotes')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.otherNotes && (<div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <div className="report-field"><label style={{ color: labelColor }}>Additional Information</label>
            <textarea value={report.otherNotes} onChange={(e) => handleInputChange('otherNotes', '', e.target.value)} placeholder="Any other information not captured in the fields above..." className="report-other-notes-textarea" style={{ width: '100%', minHeight: '100px', padding: '8px 12px', borderRadius: '6px', color: textColor, resize: 'vertical' }} /></div>
          </div>)}
        </div>
        {/* Save Button */}
        <div style={{ marginTop: '24px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <button onClick={handleSave} disabled={isSaving} style={{ backgroundColor: '#3b82f6', color: 'white', border: 'none', padding: '12px 24px', borderRadius: '8px', fontSize: '16px', fontWeight: '600', cursor: isSaving ? 'not-allowed' : 'pointer', opacity: isSaving ? 0.7 : 1, transition: 'all 0.2s ease' }} onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#2563eb'} onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#3b82f6'}>Save Report</button>
          {saveStatus && (<p style={{ color: saveStatus.includes('Error') ? '#ef4444' : '#10b981', marginTop: '8px', fontSize: '14px' }}>{saveStatus}</p>)}
        </div>
      </div>
    </>
  );
};

// ============================================
// Labor & Delivery Report Component
// ============================================
const LaborDeliveryReport: React.FC<{
  patientData: Patient;
  savedReport: LaborDeliveryReportData | undefined;
  onSaveReport: (mrn: string, report: LaborDeliveryReportData) => void;
  loggedInUser: { first_name: string; last_name: string; email: string } | null;
  onReportSubmitted?: () => void;
}> = ({ patientData, savedReport, onSaveReport, loggedInUser, onReportSubmitted }) => {
  const { theme } = useTheme();
  // Merge savedReport with empty template to ensure all nested objects exist
  const [report, setReport] = useState<LaborDeliveryReportData>(() => savedReport ? {
    ...emptyLaborDeliveryReport,
    ...savedReport,
    maternalOverview: { ...emptyLaborDeliveryReport.maternalOverview, ...savedReport.maternalOverview },
    vitals: { ...emptyLaborDeliveryReport.vitals, ...savedReport.vitals },
    fetalMonitoring: { ...emptyLaborDeliveryReport.fetalMonitoring, ...savedReport.fetalMonitoring },
    laborStatus: { ...emptyLaborDeliveryReport.laborStatus, ...savedReport.laborStatus },
    medications: { ...emptyLaborDeliveryReport.medications, ...savedReport.medications },
    maternalAssessment: { ...emptyLaborDeliveryReport.maternalAssessment, ...savedReport.maternalAssessment },
    riskFactors: { ...emptyLaborDeliveryReport.riskFactors, ...savedReport.riskFactors },
    deliveryPlan: { ...emptyLaborDeliveryReport.deliveryPlan, ...savedReport.deliveryPlan },
    postpartum: { ...emptyLaborDeliveryReport.postpartum, ...savedReport.postpartum },
    notes: { ...emptyLaborDeliveryReport.notes, ...savedReport.notes },
  } : emptyLaborDeliveryReport);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    maternalOverview: true, vitals: true, fetalMonitoring: true, laborStatus: true,
    medications: true, maternalAssessment: true, riskFactors: true, deliveryPlan: true,
    postpartum: true, notes: true, otherNotes: true
  });
  const [saveStatus, setSaveStatus] = useState<string>('');
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [previousSubmitter, setPreviousSubmitter] = useState<{ name: string; timestamp: string } | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchSavedReport = async () => {
      if (!patientData?.MRN) { setIsLoading(false); return; }
      if (savedReport) {
        setReport({
          ...emptyLaborDeliveryReport,
          ...savedReport,
          maternalOverview: { ...emptyLaborDeliveryReport.maternalOverview, ...savedReport.maternalOverview },
          vitals: { ...emptyLaborDeliveryReport.vitals, ...savedReport.vitals },
          fetalMonitoring: { ...emptyLaborDeliveryReport.fetalMonitoring, ...savedReport.fetalMonitoring },
          laborStatus: { ...emptyLaborDeliveryReport.laborStatus, ...savedReport.laborStatus },
          medications: { ...emptyLaborDeliveryReport.medications, ...savedReport.medications },
          maternalAssessment: { ...emptyLaborDeliveryReport.maternalAssessment, ...savedReport.maternalAssessment },
          riskFactors: { ...emptyLaborDeliveryReport.riskFactors, ...savedReport.riskFactors },
          deliveryPlan: { ...emptyLaborDeliveryReport.deliveryPlan, ...savedReport.deliveryPlan },
          postpartum: { ...emptyLaborDeliveryReport.postpartum, ...savedReport.postpartum },
          notes: { ...emptyLaborDeliveryReport.notes, ...savedReport.notes },
        });
        setIsLoading(false);
        try {
          const response = await axios.get<{ success: boolean; report?: { submitted_by: string; timestamp: string; }; }>(`${BACKEND_URL}/report/labor-delivery/${patientData.MRN}`);
          if (response.data.success && response.data.report) {
            setPreviousSubmitter({ name: response.data.report.submitted_by, timestamp: response.data.report.timestamp });
          }
        } catch { /* ignore */ }
        return;
      }
      setIsLoading(true);
      try {
        const response = await axios.get<{ success: boolean; report?: { submitted_by: string; timestamp: string; report_data: LaborDeliveryReportData; }; }>(`${BACKEND_URL}/report/labor-delivery/${patientData.MRN}`);
        if (response.data.success && response.data.report) {
          const dbReport = response.data.report;
          setReport(dbReport.report_data as LaborDeliveryReportData);
          onSaveReport(patientData.MRN, dbReport.report_data as LaborDeliveryReportData);
          setPreviousSubmitter({ name: dbReport.submitted_by, timestamp: dbReport.timestamp });
        } else {
          setReport(emptyLaborDeliveryReport);
          setPreviousSubmitter(null);
        }
      } catch (error) {
        setReport(emptyLaborDeliveryReport);
        setPreviousSubmitter(null);
      }
      setIsLoading(false);
    };
    fetchSavedReport();
  }, [patientData?.MRN, savedReport]);

  useEffect(() => {
    if (savedReport) {
      setReport({
        ...emptyLaborDeliveryReport,
        ...savedReport,
        maternalOverview: { ...emptyLaborDeliveryReport.maternalOverview, ...savedReport.maternalOverview },
        vitals: { ...emptyLaborDeliveryReport.vitals, ...savedReport.vitals },
        fetalMonitoring: { ...emptyLaborDeliveryReport.fetalMonitoring, ...savedReport.fetalMonitoring },
        laborStatus: { ...emptyLaborDeliveryReport.laborStatus, ...savedReport.laborStatus },
        medications: { ...emptyLaborDeliveryReport.medications, ...savedReport.medications },
        maternalAssessment: { ...emptyLaborDeliveryReport.maternalAssessment, ...savedReport.maternalAssessment },
        riskFactors: { ...emptyLaborDeliveryReport.riskFactors, ...savedReport.riskFactors },
        deliveryPlan: { ...emptyLaborDeliveryReport.deliveryPlan, ...savedReport.deliveryPlan },
        postpartum: { ...emptyLaborDeliveryReport.postpartum, ...savedReport.postpartum },
        notes: { ...emptyLaborDeliveryReport.notes, ...savedReport.notes },
      });
    }
  }, [savedReport]);

  const cardBgColor = theme === 'dark' ? '#1e293b' : '#ffffff';
  const borderColor = theme === 'dark' ? '#334155' : '#e2e8f0';
  const headerBgColor = theme === 'dark' ? '#0f172a' : '#f1f5f9';
  const textColor = theme === 'dark' ? '#f1f5f9' : '#1e293b';
  const labelColor = theme === 'dark' ? '#94a3b8' : '#64748b';

  const toggleSection = (section: string) => { setExpandedSections(prev => ({ ...prev, [section]: !prev[section] })); };
  const handleInputChange = (section: keyof LaborDeliveryReportData, field: string, value: string) => {
    setReport(prev => {
      const newReport = section === 'otherNotes' ? { ...prev, otherNotes: value } : { ...prev, [section]: { ...(prev[section] as Record<string, string>), [field]: value } };
      onSaveReport(patientData.MRN, newReport);
      return newReport;
    });
  };

  const handleSave = async () => {
    onSaveReport(patientData.MRN, report);
    if (loggedInUser) {
      setIsSaving(true);
      try {
        const submittedBy = `${loggedInUser.first_name} ${loggedInUser.last_name}`;
        await axios.post(`${BACKEND_URL}/report/labor-delivery/save`, {
          patient_name: patientData.Name, mrn: patientData.MRN, submitted_by: submittedBy, email: loggedInUser.email, report_data: report
        });
        setPreviousSubmitter({ name: submittedBy, timestamp: new Date().toISOString() });
        setSaveStatus('Report saved successfully!');
        onReportSubmitted?.();
      } catch (error) {
        console.error('Error saving report to database:', error);
        setSaveStatus('Saved locally (database error)');
      }
      setIsSaving(false);
    } else { setSaveStatus('Report saved locally!'); }
    setTimeout(() => setSaveStatus(''), 3000);
  };

  const styleProps = { cardBgColor, borderColor, textColor, labelColor };
  const formatTimestamp = (timestamp: string) => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
    } catch { return timestamp; }
  };

  if (isLoading) {
    return (<div className="patient-report-container" style={{ backgroundColor: cardBgColor, padding: '40px', textAlign: 'center' }}><div style={{ color: labelColor }}>Loading report...</div></div>);
  }

  return (
    <>
      <div className="report-header-container" style={{ backgroundColor: '#3b82f6', borderRadius: '12px', padding: '20px 24px', marginBottom: '20px' }}>
        <h2 style={{ color: 'white', margin: 0 }}>Report For: {patientData.Name} | {patientData.MRN} | Room {patientData.Room || 'N/A'}</h2>
        {previousSubmitter && (<p style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '14px', margin: '8px 0 0 0' }}>Previous report by: <strong>{previousSubmitter.name}</strong> • {formatTimestamp(previousSubmitter.timestamp)}</p>)}
        {!previousSubmitter && (<p style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '14px', margin: '8px 0 0 0' }}>No previous report found for this patient</p>)}
      </div>
      <div className="patient-report-container" style={{ backgroundColor: cardBgColor, borderRadius: '12px', padding: '24px' }}>
        {/* Maternal Overview */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={User} title="Maternal Overview" isExpanded={expandedSections.maternalOverview} onToggle={() => toggleSection('maternalOverview')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.maternalOverview && (<div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="G/P" value={report.maternalOverview.gp} onChange={(v) => handleInputChange('maternalOverview', 'gp', v)} placeholder="e.g., G2P1" {...styleProps} />
            <InputField label="Gestational Age" value={report.maternalOverview.gestationalAge} onChange={(v) => handleInputChange('maternalOverview', 'gestationalAge', v)} placeholder="e.g., 38w2d" {...styleProps} />
            <InputField label="Blood Type" value={report.maternalOverview.bloodType} onChange={(v) => handleInputChange('maternalOverview', 'bloodType', v)} placeholder="e.g., O+, Rh-" {...styleProps} />
            <InputField label="GBS Status" value={report.maternalOverview.gbsStatus} onChange={(v) => handleInputChange('maternalOverview', 'gbsStatus', v)} placeholder="e.g., Positive, Abx given" {...styleProps} />
          </div>)}
        </div>
        {/* Vitals */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={Activity} title="Vitals" isExpanded={expandedSections.vitals} onToggle={() => toggleSection('vitals')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.vitals && (<div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="BP" value={report.vitals.bp} onChange={(v) => handleInputChange('vitals', 'bp', v)} placeholder="e.g., 120/78" {...styleProps} />
            <InputField label="HR" value={report.vitals.hr} onChange={(v) => handleInputChange('vitals', 'hr', v)} placeholder="e.g., 88" {...styleProps} />
            <InputField label="Temp" value={report.vitals.temp} onChange={(v) => handleInputChange('vitals', 'temp', v)} placeholder="e.g., 98.6°F" {...styleProps} />
            <InputField label="SpO2" value={report.vitals.spo2} onChange={(v) => handleInputChange('vitals', 'spo2', v)} placeholder="e.g., 99% RA" {...styleProps} />
            <InputField label="Pain Score" value={report.vitals.painScore} onChange={(v) => handleInputChange('vitals', 'painScore', v)} placeholder="e.g., 6/10" {...styleProps} />
          </div>)}
        </div>
        {/* Fetal Monitoring */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={Heart} title="Fetal Monitoring" isExpanded={expandedSections.fetalMonitoring} onToggle={() => toggleSection('fetalMonitoring')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.fetalMonitoring && (<div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="FHR Baseline" value={report.fetalMonitoring.fhrBaseline} onChange={(v) => handleInputChange('fetalMonitoring', 'fhrBaseline', v)} placeholder="e.g., 140s" {...styleProps} />
            <InputField label="Variability" value={report.fetalMonitoring.variability} onChange={(v) => handleInputChange('fetalMonitoring', 'variability', v)} placeholder="e.g., Moderate" {...styleProps} />
            <InputField label="Decelerations" value={report.fetalMonitoring.decelerations} onChange={(v) => handleInputChange('fetalMonitoring', 'decelerations', v)} placeholder="e.g., None, Variable decels" {...styleProps} />
          </div>)}
        </div>
        {/* Labor Status */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={Clock} title="Labor Status" isExpanded={expandedSections.laborStatus} onToggle={() => toggleSection('laborStatus')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.laborStatus && (<div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Cervical Exam" value={report.laborStatus.cervicalExam} onChange={(v) => handleInputChange('laborStatus', 'cervicalExam', v)} placeholder="e.g., 6cm/80%/-1" {...styleProps} />
            <InputField label="Contraction Pattern" value={report.laborStatus.contractionPattern} onChange={(v) => handleInputChange('laborStatus', 'contractionPattern', v)} placeholder="e.g., Q3min x 60sec" {...styleProps} />
            <InputField label="Membrane Status" value={report.laborStatus.membraneStatus} onChange={(v) => handleInputChange('laborStatus', 'membraneStatus', v)} placeholder="e.g., SROM, clear fluid" {...styleProps} />
          </div>)}
        </div>
        {/* Medications */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={Pill} title="Medications" isExpanded={expandedSections.medications} onToggle={() => toggleSection('medications')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.medications && (<div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Oxytocin" value={report.medications.oxytocin} onChange={(v) => handleInputChange('medications', 'oxytocin', v)} placeholder="e.g., 12 mU/min" {...styleProps} />
            <InputField label="Magnesium" value={report.medications.magnesium} onChange={(v) => handleInputChange('medications', 'magnesium', v)} placeholder="e.g., 2g/hr" {...styleProps} />
            <InputField label="Pain Control" value={report.medications.painControl} onChange={(v) => handleInputChange('medications', 'painControl', v)} placeholder="e.g., Epidural in place" {...styleProps} />
          </div>)}
        </div>
        {/* Maternal Assessment */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={Stethoscope} title="Maternal Assessment" isExpanded={expandedSections.maternalAssessment} onToggle={() => toggleSection('maternalAssessment')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.maternalAssessment && (<div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Bleeding" value={report.maternalAssessment.bleeding} onChange={(v) => handleInputChange('maternalAssessment', 'bleeding', v)} placeholder="e.g., Minimal, bloody show" {...styleProps} />
            <InputField label="Edema" value={report.maternalAssessment.edema} onChange={(v) => handleInputChange('maternalAssessment', 'edema', v)} placeholder="e.g., 1+ LE" {...styleProps} />
            <InputField label="Urine Output" value={report.maternalAssessment.urineOutput} onChange={(v) => handleInputChange('maternalAssessment', 'urineOutput', v)} placeholder="e.g., Clear, adequate" {...styleProps} />
          </div>)}
        </div>
        {/* Risk Factors */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={AlertTriangle} title="Risk Factors" isExpanded={expandedSections.riskFactors} onToggle={() => toggleSection('riskFactors')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.riskFactors && (<div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Pre-eclampsia" value={report.riskFactors.preEclampsia} onChange={(v) => handleInputChange('riskFactors', 'preEclampsia', v)} placeholder="e.g., Mild, on Mag" {...styleProps} />
            <InputField label="Gestational Diabetes" value={report.riskFactors.gestationalDiabetes} onChange={(v) => handleInputChange('riskFactors', 'gestationalDiabetes', v)} placeholder="e.g., Diet controlled" {...styleProps} />
            <InputField label="Infection Risk" value={report.riskFactors.infectionRisk} onChange={(v) => handleInputChange('riskFactors', 'infectionRisk', v)} placeholder="e.g., GBS+, Abx given" {...styleProps} />
          </div>)}
        </div>
        {/* Delivery Plan */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={Target} title="Delivery Plan" isExpanded={expandedSections.deliveryPlan} onToggle={() => toggleSection('deliveryPlan')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.deliveryPlan && (<div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Vaginal/C-section" value={report.deliveryPlan.vaginalCsection} onChange={(v) => handleInputChange('deliveryPlan', 'vaginalCsection', v)} placeholder="e.g., VBAC attempt" {...styleProps} />
            <InputField label="Provider Notified" value={report.deliveryPlan.providerNotified} onChange={(v) => handleInputChange('deliveryPlan', 'providerNotified', v)} placeholder="e.g., Dr. Smith aware" {...styleProps} />
            <InputField label="NICU on Standby" value={report.deliveryPlan.nicuOnStandby} onChange={(v) => handleInputChange('deliveryPlan', 'nicuOnStandby', v)} placeholder="e.g., Yes, for preterm" {...styleProps} />
          </div>)}
        </div>
        {/* Postpartum */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={CheckCircle} title="Postpartum" isExpanded={expandedSections.postpartum} onToggle={() => toggleSection('postpartum')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.postpartum && (<div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Fundus" value={report.postpartum.fundus} onChange={(v) => handleInputChange('postpartum', 'fundus', v)} placeholder="e.g., Firm, midline" {...styleProps} />
            <InputField label="Lochia" value={report.postpartum.lochia} onChange={(v) => handleInputChange('postpartum', 'lochia', v)} placeholder="e.g., Moderate rubra" {...styleProps} />
            <InputField label="Perineum" value={report.postpartum.perineum} onChange={(v) => handleInputChange('postpartum', 'perineum', v)} placeholder="e.g., 2nd degree repair" {...styleProps} />
          </div>)}
        </div>
        {/* Notes */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={FileText} title="Notes" isExpanded={expandedSections.notes} onToggle={() => toggleSection('notes')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.notes && (<div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Events This Shift" value={report.notes.events} onChange={(v) => handleInputChange('notes', 'events', v)} placeholder="e.g., AROM performed" {...styleProps} />
            <InputField label="Provider Updates" value={report.notes.providerUpdates} onChange={(v) => handleInputChange('notes', 'providerUpdates', v)} placeholder="e.g., OB notified of decels" {...styleProps} />
          </div>)}
        </div>
        {/* Other Notes */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={FileText} title="Other Notes" isExpanded={expandedSections.otherNotes} onToggle={() => toggleSection('otherNotes')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.otherNotes && (<div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <div className="report-field"><label style={{ color: labelColor }}>Additional Information</label>
            <textarea value={report.otherNotes} onChange={(e) => handleInputChange('otherNotes', '', e.target.value)} placeholder="Any other information not captured in the fields above..." className="report-other-notes-textarea" style={{ width: '100%', minHeight: '100px', padding: '8px 12px', borderRadius: '6px', color: textColor, resize: 'vertical' }} /></div>
          </div>)}
        </div>
        {/* Save Button */}
        <div style={{ marginTop: '24px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <button onClick={handleSave} disabled={isSaving} style={{ backgroundColor: '#3b82f6', color: 'white', border: 'none', padding: '12px 24px', borderRadius: '8px', fontSize: '16px', fontWeight: '600', cursor: isSaving ? 'not-allowed' : 'pointer', opacity: isSaving ? 0.7 : 1, transition: 'all 0.2s ease' }} onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#2563eb'} onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#3b82f6'}>Save Report</button>
          {saveStatus && (<p style={{ color: saveStatus.includes('Error') ? '#ef4444' : '#10b981', marginTop: '8px', fontSize: '14px' }}>{saveStatus}</p>)}
        </div>
      </div>
    </>
  );
};

// ============================================
// Psychiatry Report Component
// ============================================
const PsychiatryReport: React.FC<{
  patientData: Patient;
  savedReport: PsychiatryReportData | undefined;
  onSaveReport: (mrn: string, report: PsychiatryReportData) => void;
  loggedInUser: { first_name: string; last_name: string; email: string } | null;
  onReportSubmitted?: () => void;
}> = ({ patientData, savedReport, onSaveReport, loggedInUser, onReportSubmitted }) => {
  const { theme } = useTheme();
  // Merge savedReport with empty template to ensure all nested objects exist
  const [report, setReport] = useState<PsychiatryReportData>(() => savedReport ? {
    ...emptyPsychiatryReport,
    ...savedReport,
    patientOverview: { ...emptyPsychiatryReport.patientOverview, ...savedReport.patientOverview },
    vitals: { ...emptyPsychiatryReport.vitals, ...savedReport.vitals },
    mentalStatus: { ...emptyPsychiatryReport.mentalStatus, ...savedReport.mentalStatus },
    safety: { ...emptyPsychiatryReport.safety, ...savedReport.safety },
    behavior: { ...emptyPsychiatryReport.behavior, ...savedReport.behavior },
    medications: { ...emptyPsychiatryReport.medications, ...savedReport.medications },
    substanceUse: { ...emptyPsychiatryReport.substanceUse, ...savedReport.substanceUse },
    therapy: { ...emptyPsychiatryReport.therapy, ...savedReport.therapy },
    familySocial: { ...emptyPsychiatryReport.familySocial, ...savedReport.familySocial },
    plan: { ...emptyPsychiatryReport.plan, ...savedReport.plan },
    notes: { ...emptyPsychiatryReport.notes, ...savedReport.notes },
  } : emptyPsychiatryReport);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    patientOverview: true, vitals: true, mentalStatus: true, safety: true,
    behavior: true, medications: true, substanceUse: true, therapy: true,
    familySocial: true, plan: true, notes: true, otherNotes: true
  });
  const [saveStatus, setSaveStatus] = useState<string>('');
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [previousSubmitter, setPreviousSubmitter] = useState<{ name: string; timestamp: string } | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Auto-populate age from patient DOB
  useEffect(() => {
    if (patientData?.DOB && !report.patientOverview.age) {
      const calculatedAge = calculateAgeFromDOB(patientData.DOB);
      if (calculatedAge) {
        setReport(prev => ({
          ...prev,
          patientOverview: { ...prev.patientOverview, age: calculatedAge }
        }));
      }
    }
  }, [patientData?.DOB]);

  useEffect(() => {
    const fetchSavedReport = async () => {
      if (!patientData?.MRN) { setIsLoading(false); return; }
      if (savedReport) {
        setReport({
          ...emptyPsychiatryReport,
          ...savedReport,
          patientOverview: { ...emptyPsychiatryReport.patientOverview, ...savedReport.patientOverview },
          vitals: { ...emptyPsychiatryReport.vitals, ...savedReport.vitals },
          mentalStatus: { ...emptyPsychiatryReport.mentalStatus, ...savedReport.mentalStatus },
          safety: { ...emptyPsychiatryReport.safety, ...savedReport.safety },
          behavior: { ...emptyPsychiatryReport.behavior, ...savedReport.behavior },
          medications: { ...emptyPsychiatryReport.medications, ...savedReport.medications },
          substanceUse: { ...emptyPsychiatryReport.substanceUse, ...savedReport.substanceUse },
          therapy: { ...emptyPsychiatryReport.therapy, ...savedReport.therapy },
          familySocial: { ...emptyPsychiatryReport.familySocial, ...savedReport.familySocial },
          plan: { ...emptyPsychiatryReport.plan, ...savedReport.plan },
          notes: { ...emptyPsychiatryReport.notes, ...savedReport.notes },
        });
        setIsLoading(false);
        try {
          const response = await axios.get<{ success: boolean; report?: { submitted_by: string; timestamp: string; }; }>(`${BACKEND_URL}/report/psychiatry/${patientData.MRN}`);
          if (response.data.success && response.data.report) {
            setPreviousSubmitter({ name: response.data.report.submitted_by, timestamp: response.data.report.timestamp });
          }
        } catch { /* ignore */ }
        return;
      }
      setIsLoading(true);
      try {
        const response = await axios.get<{ success: boolean; report?: { submitted_by: string; timestamp: string; report_data: PsychiatryReportData; }; }>(`${BACKEND_URL}/report/psychiatry/${patientData.MRN}`);
        if (response.data.success && response.data.report) {
          const dbReport = response.data.report;
          setReport(dbReport.report_data as PsychiatryReportData);
          onSaveReport(patientData.MRN, dbReport.report_data as PsychiatryReportData);
          setPreviousSubmitter({ name: dbReport.submitted_by, timestamp: dbReport.timestamp });
        } else {
          setReport(emptyPsychiatryReport);
          setPreviousSubmitter(null);
        }
      } catch (error) {
        setReport(emptyPsychiatryReport);
        setPreviousSubmitter(null);
      }
      setIsLoading(false);
    };
    fetchSavedReport();
  }, [patientData?.MRN, savedReport]);

  useEffect(() => {
    if (savedReport) {
      setReport({
        ...emptyPsychiatryReport,
        ...savedReport,
        patientOverview: { ...emptyPsychiatryReport.patientOverview, ...savedReport.patientOverview },
        vitals: { ...emptyPsychiatryReport.vitals, ...savedReport.vitals },
        mentalStatus: { ...emptyPsychiatryReport.mentalStatus, ...savedReport.mentalStatus },
        safety: { ...emptyPsychiatryReport.safety, ...savedReport.safety },
        behavior: { ...emptyPsychiatryReport.behavior, ...savedReport.behavior },
        medications: { ...emptyPsychiatryReport.medications, ...savedReport.medications },
        substanceUse: { ...emptyPsychiatryReport.substanceUse, ...savedReport.substanceUse },
        therapy: { ...emptyPsychiatryReport.therapy, ...savedReport.therapy },
        familySocial: { ...emptyPsychiatryReport.familySocial, ...savedReport.familySocial },
        plan: { ...emptyPsychiatryReport.plan, ...savedReport.plan },
        notes: { ...emptyPsychiatryReport.notes, ...savedReport.notes },
      });
    }
  }, [savedReport]);

  const cardBgColor = theme === 'dark' ? '#1e293b' : '#ffffff';
  const borderColor = theme === 'dark' ? '#334155' : '#e2e8f0';
  const headerBgColor = theme === 'dark' ? '#0f172a' : '#f1f5f9';
  const textColor = theme === 'dark' ? '#f1f5f9' : '#1e293b';
  const labelColor = theme === 'dark' ? '#94a3b8' : '#64748b';

  const toggleSection = (section: string) => { setExpandedSections(prev => ({ ...prev, [section]: !prev[section] })); };
  const handleInputChange = (section: keyof PsychiatryReportData, field: string, value: string) => {
    setReport(prev => {
      const newReport = section === 'otherNotes' ? { ...prev, otherNotes: value } : { ...prev, [section]: { ...(prev[section] as Record<string, string>), [field]: value } };
      onSaveReport(patientData.MRN, newReport);
      return newReport;
    });
  };

  const handleSave = async () => {
    onSaveReport(patientData.MRN, report);
    if (loggedInUser) {
      setIsSaving(true);
      try {
        const submittedBy = `${loggedInUser.first_name} ${loggedInUser.last_name}`;
        await axios.post(`${BACKEND_URL}/report/psychiatry/save`, {
          patient_name: patientData.Name, mrn: patientData.MRN, submitted_by: submittedBy, email: loggedInUser.email, report_data: report
        });
        setPreviousSubmitter({ name: submittedBy, timestamp: new Date().toISOString() });
        setSaveStatus('Report saved successfully!');
        onReportSubmitted?.();
      } catch (error) {
        console.error('Error saving report to database:', error);
        setSaveStatus('Saved locally (database error)');
      }
      setIsSaving(false);
    } else { setSaveStatus('Report saved locally!'); }
    setTimeout(() => setSaveStatus(''), 3000);
  };

  const styleProps = { cardBgColor, borderColor, textColor, labelColor };
  const formatTimestamp = (timestamp: string) => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
    } catch { return timestamp; }
  };

  if (isLoading) {
    return (<div className="patient-report-container" style={{ backgroundColor: cardBgColor, padding: '40px', textAlign: 'center' }}><div style={{ color: labelColor }}>Loading report...</div></div>);
  }

  return (
    <>
      <div className="report-header-container" style={{ backgroundColor: '#3b82f6', borderRadius: '12px', padding: '20px 24px', marginBottom: '20px' }}>
        <h2 style={{ color: 'white', margin: 0 }}>Report For: {patientData.Name} | {patientData.MRN} | Room {patientData.Room || 'N/A'}</h2>
        {previousSubmitter && (<p style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '14px', margin: '8px 0 0 0' }}>Previous report by: <strong>{previousSubmitter.name}</strong> • {formatTimestamp(previousSubmitter.timestamp)}</p>)}
        {!previousSubmitter && (<p style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '14px', margin: '8px 0 0 0' }}>No previous report found for this patient</p>)}
      </div>
      <div className="patient-report-container" style={{ backgroundColor: cardBgColor, borderRadius: '12px', padding: '24px' }}>
        {/* Patient Overview */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={User} title="Patient Overview" isExpanded={expandedSections.patientOverview} onToggle={() => toggleSection('patientOverview')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.patientOverview && (<div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Age" value={report.patientOverview.age} onChange={(v) => handleInputChange('patientOverview', 'age', v)} placeholder="e.g., 34 y/o" {...styleProps} />
            <InputField label="Psych Dx" value={report.patientOverview.psychDx || patientData.Diagnosis} onChange={(v) => handleInputChange('patientOverview', 'psychDx', v)} placeholder="e.g., MDD, Bipolar I" {...styleProps} />
            <InputField label="Legal Status" value={report.patientOverview.legalStatus} onChange={(v) => handleInputChange('patientOverview', 'legalStatus', v)} placeholder="e.g., Voluntary, 5150" {...styleProps} />
          </div>)}
        </div>
        {/* Vitals */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={Activity} title="Vitals" isExpanded={expandedSections.vitals} onToggle={() => toggleSection('vitals')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.vitals && (<div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="HR" value={report.vitals.hr} onChange={(v) => handleInputChange('vitals', 'hr', v)} placeholder="e.g., 78" {...styleProps} />
            <InputField label="BP" value={report.vitals.bp} onChange={(v) => handleInputChange('vitals', 'bp', v)} placeholder="e.g., 118/72" {...styleProps} />
            <InputField label="Temp" value={report.vitals.temp} onChange={(v) => handleInputChange('vitals', 'temp', v)} placeholder="e.g., 98.4°F" {...styleProps} />
            <InputField label="RR" value={report.vitals.rr} onChange={(v) => handleInputChange('vitals', 'rr', v)} placeholder="e.g., 16" {...styleProps} />
          </div>)}
        </div>
        {/* Mental Status */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={Brain} title="Mental Status" isExpanded={expandedSections.mentalStatus} onToggle={() => toggleSection('mentalStatus')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.mentalStatus && (<div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Appearance" value={report.mentalStatus.appearance} onChange={(v) => handleInputChange('mentalStatus', 'appearance', v)} placeholder="e.g., Disheveled, appropriate" {...styleProps} />
            <InputField label="Mood/Affect" value={report.mentalStatus.moodAffect} onChange={(v) => handleInputChange('mentalStatus', 'moodAffect', v)} placeholder="e.g., Depressed/flat" {...styleProps} />
            <InputField label="Thought Process" value={report.mentalStatus.thoughtProcess} onChange={(v) => handleInputChange('mentalStatus', 'thoughtProcess', v)} placeholder="e.g., Linear, disorganized" {...styleProps} />
          </div>)}
        </div>
        {/* Safety */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={ShieldCheck} title="Safety" isExpanded={expandedSections.safety} onToggle={() => toggleSection('safety')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.safety && (<div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Suicide Risk" value={report.safety.suicideRisk} onChange={(v) => handleInputChange('safety', 'suicideRisk', v)} placeholder="e.g., Low, denies SI" {...styleProps} />
            <InputField label="Homicide Risk" value={report.safety.homicideRisk} onChange={(v) => handleInputChange('safety', 'homicideRisk', v)} placeholder="e.g., None, denies HI" {...styleProps} />
            <InputField label="Observation Level" value={report.safety.observationLevel} onChange={(v) => handleInputChange('safety', 'observationLevel', v)} placeholder="e.g., Q15min, 1:1" {...styleProps} />
          </div>)}
        </div>
        {/* Behavior */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={Smile} title="Behavior" isExpanded={expandedSections.behavior} onToggle={() => toggleSection('behavior')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.behavior && (<div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Agitation" value={report.behavior.agitation} onChange={(v) => handleInputChange('behavior', 'agitation', v)} placeholder="e.g., None, pacing" {...styleProps} />
            <InputField label="Cooperation" value={report.behavior.cooperation} onChange={(v) => handleInputChange('behavior', 'cooperation', v)} placeholder="e.g., Cooperative, guarded" {...styleProps} />
            <InputField label="Sleep Pattern" value={report.behavior.sleepPattern} onChange={(v) => handleInputChange('behavior', 'sleepPattern', v)} placeholder="e.g., Poor, slept 3hrs" {...styleProps} />
          </div>)}
        </div>
        {/* Medications */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={Pill} title="Medications" isExpanded={expandedSections.medications} onToggle={() => toggleSection('medications')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.medications && (<div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Psych Meds" value={report.medications.psychMeds} onChange={(v) => handleInputChange('medications', 'psychMeds', v)} placeholder="e.g., Seroquel, Lexapro" {...styleProps} />
            <InputField label="PRNs Given" value={report.medications.prnsGiven} onChange={(v) => handleInputChange('medications', 'prnsGiven', v)} placeholder="e.g., Ativan x1 for anxiety" {...styleProps} />
            <InputField label="Side Effects" value={report.medications.sideEffects} onChange={(v) => handleInputChange('medications', 'sideEffects', v)} placeholder="e.g., Drowsiness, EPS" {...styleProps} />
          </div>)}
        </div>
        {/* Substance Use */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={AlertCircle} title="Substance Use" isExpanded={expandedSections.substanceUse} onToggle={() => toggleSection('substanceUse')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.substanceUse && (<div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Withdrawal Risk" value={report.substanceUse.withdrawalRisk} onChange={(v) => handleInputChange('substanceUse', 'withdrawalRisk', v)} placeholder="e.g., EtOH withdrawal risk" {...styleProps} />
            <InputField label="CIWA/COWS" value={report.substanceUse.ciwaCows} onChange={(v) => handleInputChange('substanceUse', 'ciwaCows', v)} placeholder="e.g., CIWA 8" {...styleProps} />
          </div>)}
        </div>
        {/* Therapy */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={Users} title="Therapy" isExpanded={expandedSections.therapy} onToggle={() => toggleSection('therapy')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.therapy && (<div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Group Participation" value={report.therapy.groupParticipation} onChange={(v) => handleInputChange('therapy', 'groupParticipation', v)} placeholder="e.g., Attended 2 groups" {...styleProps} />
            <InputField label="1:1 Therapy" value={report.therapy.oneOnOneTherapy} onChange={(v) => handleInputChange('therapy', 'oneOnOneTherapy', v)} placeholder="e.g., Met with SW" {...styleProps} />
          </div>)}
        </div>
        {/* Family/Social */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={Home} title="Family / Social" isExpanded={expandedSections.familySocial} onToggle={() => toggleSection('familySocial')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.familySocial && (<div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Support System" value={report.familySocial.supportSystem} onChange={(v) => handleInputChange('familySocial', 'supportSystem', v)} placeholder="e.g., Sister involved" {...styleProps} />
            <InputField label="Housing/Resources" value={report.familySocial.housingResources} onChange={(v) => handleInputChange('familySocial', 'housingResources', v)} placeholder="e.g., Homeless, SW involved" {...styleProps} />
          </div>)}
        </div>
        {/* Plan */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={Target} title="Plan" isExpanded={expandedSections.plan} onToggle={() => toggleSection('plan')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.plan && (<div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Psych Goals" value={report.plan.psychGoals} onChange={(v) => handleInputChange('plan', 'psychGoals', v)} placeholder="e.g., Stabilize mood" {...styleProps} />
            <InputField label="Discharge Criteria" value={report.plan.dischargeCriteria} onChange={(v) => handleInputChange('plan', 'dischargeCriteria', v)} placeholder="e.g., No SI, med compliant" {...styleProps} />
            <InputField label="Follow-up Needs" value={report.plan.followUpNeeds} onChange={(v) => handleInputChange('plan', 'followUpNeeds', v)} placeholder="e.g., Outpatient appt scheduled" {...styleProps} />
          </div>)}
        </div>
        {/* Notes */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={FileText} title="Notes" isExpanded={expandedSections.notes} onToggle={() => toggleSection('notes')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.notes && (<div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Notes" value={report.notes.notesField} onChange={(v) => handleInputChange('notes', 'notesField', v)} placeholder="e.g., Patient more engaged" {...styleProps} />
            <InputField label="Escalations" value={report.notes.escalations} onChange={(v) => handleInputChange('notes', 'escalations', v)} placeholder="e.g., None, verbal redirection" {...styleProps} />
          </div>)}
        </div>
        {/* Other Notes */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={FileText} title="Other Notes" isExpanded={expandedSections.otherNotes} onToggle={() => toggleSection('otherNotes')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.otherNotes && (<div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <div className="report-field"><label style={{ color: labelColor }}>Additional Information</label>
            <textarea value={report.otherNotes} onChange={(e) => handleInputChange('otherNotes', '', e.target.value)} placeholder="Any other information not captured in the fields above..." className="report-other-notes-textarea" style={{ width: '100%', minHeight: '100px', padding: '8px 12px', borderRadius: '6px', color: textColor, resize: 'vertical' }} /></div>
          </div>)}
        </div>
        {/* Save Button */}
        <div style={{ marginTop: '24px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <button onClick={handleSave} disabled={isSaving} style={{ backgroundColor: '#3b82f6', color: 'white', border: 'none', padding: '12px 24px', borderRadius: '8px', fontSize: '16px', fontWeight: '600', cursor: isSaving ? 'not-allowed' : 'pointer', opacity: isSaving ? 0.7 : 1, transition: 'all 0.2s ease' }} onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#2563eb'} onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#3b82f6'}>Save Report</button>
          {saveStatus && (<p style={{ color: saveStatus.includes('Error') ? '#ef4444' : '#10b981', marginTop: '8px', fontSize: '14px' }}>{saveStatus}</p>)}
        </div>
      </div>
    </>
  );
};

// ============================================
// Women's Services (OB) Report Component
// ============================================
const OBReport: React.FC<{
  patientData: Patient;
  savedReport: OBReportData | undefined;
  onSaveReport: (mrn: string, report: OBReportData) => void;
  loggedInUser: { first_name: string; last_name: string; email: string } | null;
  onReportSubmitted?: () => void;
}> = ({ patientData, savedReport, onSaveReport, loggedInUser, onReportSubmitted }) => {
  const { theme } = useTheme();
  const [report, setReport] = useState<OBReportData>(() => savedReport ? {
    ...emptyOBReport,
    ...savedReport,
    patientOverview: { ...emptyOBReport.patientOverview, ...savedReport.patientOverview },
    patientPlanPreferences: { ...emptyOBReport.patientPlanPreferences, ...savedReport.patientPlanPreferences },
    vitals: { ...emptyOBReport.vitals, ...savedReport.vitals },
    maternalAssessment: { ...emptyOBReport.maternalAssessment, ...savedReport.maternalAssessment },
    postpartumStatus: { ...emptyOBReport.postpartumStatus, ...savedReport.postpartumStatus },
    breastfeedingLactation: { ...emptyOBReport.breastfeedingLactation, ...savedReport.breastfeedingLactation },
    giGu: { ...emptyOBReport.giGu, ...savedReport.giGu },
    medications: { ...emptyOBReport.medications, ...savedReport.medications },
    labs: { ...emptyOBReport.labs, ...savedReport.labs },
    infantStatus: { ...emptyOBReport.infantStatus, ...savedReport.infantStatus },
    educationSafety: { ...emptyOBReport.educationSafety, ...savedReport.educationSafety },
    psychosocial: { ...emptyOBReport.psychosocial, ...savedReport.psychosocial },
    dischargePlanning: { ...emptyOBReport.dischargePlanning, ...savedReport.dischargePlanning },
    additionalNotes: { ...emptyOBReport.additionalNotes, ...savedReport.additionalNotes },
  } : emptyOBReport);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    patientOverview: true, patientPlanPreferences: true, vitals: true, maternalAssessment: true, postpartumStatus: true,
    breastfeedingLactation: true, giGu: true, medications: true, labs: true,
    infantStatus: true, educationSafety: true, psychosocial: true, dischargePlanning: true,
    additionalNotes: true, otherNotes: true
  });
  const [saveStatus, setSaveStatus] = useState<string>('');
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [previousSubmitter, setPreviousSubmitter] = useState<{ name: string; timestamp: string } | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Auto-populate age from patient DOB
  useEffect(() => {
    if (patientData?.DOB && !report.patientOverview.age) {
      const calculatedAge = calculateAgeFromDOB(patientData.DOB);
      if (calculatedAge) {
        setReport(prev => ({
          ...prev,
          patientOverview: { ...prev.patientOverview, age: calculatedAge }
        }));
      }
    }
  }, [patientData?.DOB]);

  useEffect(() => {
    const fetchSavedReport = async () => {
      if (!patientData?.MRN) { setIsLoading(false); return; }
      if (savedReport) {
        setReport({
          ...emptyOBReport,
          ...savedReport,
          patientOverview: { ...emptyOBReport.patientOverview, ...savedReport.patientOverview },
          patientPlanPreferences: { ...emptyOBReport.patientPlanPreferences, ...savedReport.patientPlanPreferences },
          vitals: { ...emptyOBReport.vitals, ...savedReport.vitals },
          maternalAssessment: { ...emptyOBReport.maternalAssessment, ...savedReport.maternalAssessment },
          postpartumStatus: { ...emptyOBReport.postpartumStatus, ...savedReport.postpartumStatus },
          breastfeedingLactation: { ...emptyOBReport.breastfeedingLactation, ...savedReport.breastfeedingLactation },
          giGu: { ...emptyOBReport.giGu, ...savedReport.giGu },
          medications: { ...emptyOBReport.medications, ...savedReport.medications },
          labs: { ...emptyOBReport.labs, ...savedReport.labs },
          infantStatus: { ...emptyOBReport.infantStatus, ...savedReport.infantStatus },
          educationSafety: { ...emptyOBReport.educationSafety, ...savedReport.educationSafety },
          psychosocial: { ...emptyOBReport.psychosocial, ...savedReport.psychosocial },
          dischargePlanning: { ...emptyOBReport.dischargePlanning, ...savedReport.dischargePlanning },
          additionalNotes: { ...emptyOBReport.additionalNotes, ...savedReport.additionalNotes },
        });
        setIsLoading(false);
        try {
          const response = await axios.get<{ success: boolean; report?: { submitted_by: string; timestamp: string; }; }>(`${BACKEND_URL}/report/ob/${patientData.MRN}`);
          if (response.data.success && response.data.report) {
            setPreviousSubmitter({ name: response.data.report.submitted_by, timestamp: response.data.report.timestamp });
          }
        } catch { /* ignore */ }
        return;
      }
      setIsLoading(true);
      try {
        const response = await axios.get<{ success: boolean; report?: { submitted_by: string; timestamp: string; report_data: OBReportData; }; }>(`${BACKEND_URL}/report/ob/${patientData.MRN}`);
        if (response.data.success && response.data.report) {
          const dbReport = response.data.report;
          setReport(dbReport.report_data as OBReportData);
          onSaveReport(patientData.MRN, dbReport.report_data as OBReportData);
          setPreviousSubmitter({ name: dbReport.submitted_by, timestamp: dbReport.timestamp });
        } else {
          setReport(emptyOBReport);
          setPreviousSubmitter(null);
        }
      } catch (error) {
        setReport(emptyOBReport);
        setPreviousSubmitter(null);
      }
      setIsLoading(false);
    };
    fetchSavedReport();
  }, [patientData?.MRN, savedReport]);

  useEffect(() => {
    if (savedReport) {
      setReport({
        ...emptyOBReport,
        ...savedReport,
        patientOverview: { ...emptyOBReport.patientOverview, ...savedReport.patientOverview },
        patientPlanPreferences: { ...emptyOBReport.patientPlanPreferences, ...savedReport.patientPlanPreferences },
        vitals: { ...emptyOBReport.vitals, ...savedReport.vitals },
        maternalAssessment: { ...emptyOBReport.maternalAssessment, ...savedReport.maternalAssessment },
        postpartumStatus: { ...emptyOBReport.postpartumStatus, ...savedReport.postpartumStatus },
        breastfeedingLactation: { ...emptyOBReport.breastfeedingLactation, ...savedReport.breastfeedingLactation },
        giGu: { ...emptyOBReport.giGu, ...savedReport.giGu },
        medications: { ...emptyOBReport.medications, ...savedReport.medications },
        labs: { ...emptyOBReport.labs, ...savedReport.labs },
        infantStatus: { ...emptyOBReport.infantStatus, ...savedReport.infantStatus },
        educationSafety: { ...emptyOBReport.educationSafety, ...savedReport.educationSafety },
        psychosocial: { ...emptyOBReport.psychosocial, ...savedReport.psychosocial },
        dischargePlanning: { ...emptyOBReport.dischargePlanning, ...savedReport.dischargePlanning },
        additionalNotes: { ...emptyOBReport.additionalNotes, ...savedReport.additionalNotes },
      });
    }
  }, [savedReport]);

  const cardBgColor = theme === 'dark' ? '#1e293b' : '#ffffff';
  const borderColor = theme === 'dark' ? '#334155' : '#e2e8f0';
  const headerBgColor = theme === 'dark' ? '#0f172a' : '#f1f5f9';
  const textColor = theme === 'dark' ? '#f1f5f9' : '#1e293b';
  const labelColor = theme === 'dark' ? '#94a3b8' : '#64748b';

  const toggleSection = (section: string) => { setExpandedSections(prev => ({ ...prev, [section]: !prev[section] })); };
  const handleInputChange = (section: keyof OBReportData, field: string, value: string) => {
    setReport(prev => {
      const newReport = section === 'otherNotes' ? { ...prev, otherNotes: value } : { ...prev, [section]: { ...(prev[section] as Record<string, string>), [field]: value } };
      onSaveReport(patientData.MRN, newReport);
      return newReport;
    });
  };

  const handleSave = async () => {
    onSaveReport(patientData.MRN, report);
    if (loggedInUser) {
      setIsSaving(true);
      try {
        const submittedBy = `${loggedInUser.first_name} ${loggedInUser.last_name}`;
        await axios.post(`${BACKEND_URL}/report/ob/save`, {
          patient_name: patientData.Name, mrn: patientData.MRN, submitted_by: submittedBy, email: loggedInUser.email, report_data: report
        });
        setPreviousSubmitter({ name: submittedBy, timestamp: new Date().toISOString() });
        setSaveStatus('Report saved successfully!');
        onReportSubmitted?.();
      } catch (error) {
        console.error('Error saving report to database:', error);
        setSaveStatus('Saved locally (database error)');
      }
      setIsSaving(false);
    } else { setSaveStatus('Report saved locally!'); }
    setTimeout(() => setSaveStatus(''), 3000);
  };

  const styleProps = { cardBgColor, borderColor, textColor, labelColor };
  const formatTimestamp = (timestamp: string) => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
    } catch { return timestamp; }
  };

  if (isLoading) {
    return (<div className="patient-report-container" style={{ backgroundColor: cardBgColor, padding: '40px', textAlign: 'center' }}><div style={{ color: labelColor }}>Loading report...</div></div>);
  }

  return (
    <>
      <div className="report-header-container" style={{ backgroundColor: '#3b82f6', borderRadius: '12px', padding: '20px 24px', marginBottom: '20px' }}>
        <h2 style={{ color: 'white', margin: 0 }}>Report For: {patientData.Name} | {patientData.MRN} | Room {patientData.Room || 'N/A'}</h2>
        {previousSubmitter && (<p style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '14px', margin: '8px 0 0 0' }}>Previous report by: <strong>{previousSubmitter.name}</strong> • {formatTimestamp(previousSubmitter.timestamp)}</p>)}
        {!previousSubmitter && (<p style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '14px', margin: '8px 0 0 0' }}>No previous report found for this patient</p>)}
      </div>
      <div className="patient-report-container" style={{ backgroundColor: cardBgColor, borderRadius: '12px', padding: '24px' }}>
        {/* Patient Overview */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={User} title="Patient Overview" isExpanded={expandedSections.patientOverview} onToggle={() => toggleSection('patientOverview')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.patientOverview && (<div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Age" value={report.patientOverview.age} onChange={(v) => handleInputChange('patientOverview', 'age', v)} placeholder="e.g., 28 y/o" {...styleProps} />
            <InputField label="G/P" value={report.patientOverview.gP} onChange={(v) => handleInputChange('patientOverview', 'gP', v)} placeholder="e.g., G2P1" {...styleProps} />
            <InputField label="Gestational Age" value={report.patientOverview.gestationalAge} onChange={(v) => handleInputChange('patientOverview', 'gestationalAge', v)} placeholder="e.g., 39+2 weeks" {...styleProps} />
            <InputField label="Postpartum Day" value={report.patientOverview.postpartumDay} onChange={(v) => handleInputChange('patientOverview', 'postpartumDay', v)} placeholder="e.g., PPD #1" {...styleProps} />
            <InputField label="Admit Dx" value={report.patientOverview.admitDx || patientData.Diagnosis} onChange={(v) => handleInputChange('patientOverview', 'admitDx', v)} placeholder="e.g., NSVD, C-section" {...styleProps} />
            <InputField label="Code Status" value={report.patientOverview.codeStatus} onChange={(v) => handleInputChange('patientOverview', 'codeStatus', v)} placeholder="e.g., Full code" {...styleProps} />
            <InputField label="Allergies" value={report.patientOverview.allergies} onChange={(v) => handleInputChange('patientOverview', 'allergies', v)} placeholder="e.g., NKDA, Penicillin" {...styleProps} />
          </div>)}
        </div>
        {/* Patient Plan & Preferences */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={ClipboardList} title="Patient Plan & Preferences" isExpanded={expandedSections.patientPlanPreferences} onToggle={() => toggleSection('patientPlanPreferences')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.patientPlanPreferences && (<div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Infant Sex Disclosure" value={report.patientPlanPreferences.infantSexDisclosure} onChange={(v) => handleInputChange('patientPlanPreferences', 'infantSexDisclosure', v)} placeholder="e.g., Boy, Girl, Surprise" {...styleProps} />
            <InputField label="Feeding Preference" value={report.patientPlanPreferences.feedingPreference} onChange={(v) => handleInputChange('patientPlanPreferences', 'feedingPreference', v)} placeholder="e.g., Breastfeeding, Formula, Combination" {...styleProps} />
            <InputField label="Bath Timing" value={report.patientPlanPreferences.bathTiming} onChange={(v) => handleInputChange('patientPlanPreferences', 'bathTiming', v)} placeholder="e.g., Immediate bath, Delayed bath (24 hrs)" {...styleProps} />
            <InputField label="Vaccines (Newborn)" value={report.patientPlanPreferences.vaccinesNewborn} onChange={(v) => handleInputChange('patientPlanPreferences', 'vaccinesNewborn', v)} placeholder="e.g., Hep B accepted, Declined, Pending discussion" {...styleProps} />
            <InputField label="Circumcision Preference" value={report.patientPlanPreferences.circumcisionPreference} onChange={(v) => handleInputChange('patientPlanPreferences', 'circumcisionPreference', v)} placeholder="e.g., Yes, No, Undecided" {...styleProps} />
            <InputField label="Rooming-In Preference" value={report.patientPlanPreferences.roomingInPreference} onChange={(v) => handleInputChange('patientPlanPreferences', 'roomingInPreference', v)} placeholder="e.g., Yes, No, Partial" {...styleProps} />
            <InputField label="Cultural/Special Requests" value={report.patientPlanPreferences.culturalSpecialRequests} onChange={(v) => handleInputChange('patientPlanPreferences', 'culturalSpecialRequests', v)} placeholder="e.g., Delayed cord clamping, cultural bathing preference" {...styleProps} />
          </div>)}
        </div>
        {/* Vitals */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={Activity} title="Vitals" isExpanded={expandedSections.vitals} onToggle={() => toggleSection('vitals')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.vitals && (<div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="BP" value={report.vitals.bp} onChange={(v) => handleInputChange('vitals', 'bp', v)} placeholder="e.g., 118/72" {...styleProps} />
            <InputField label="HR" value={report.vitals.hr} onChange={(v) => handleInputChange('vitals', 'hr', v)} placeholder="e.g., 78" {...styleProps} />
            <InputField label="RR" value={report.vitals.rr} onChange={(v) => handleInputChange('vitals', 'rr', v)} placeholder="e.g., 16" {...styleProps} />
            <InputField label="Temp" value={report.vitals.temp} onChange={(v) => handleInputChange('vitals', 'temp', v)} placeholder="e.g., 98.4°F" {...styleProps} />
            <InputField label="SpO2" value={report.vitals.spo2} onChange={(v) => handleInputChange('vitals', 'spo2', v)} placeholder="e.g., 98% RA" {...styleProps} />
            <InputField label="Pain Score" value={report.vitals.painScore} onChange={(v) => handleInputChange('vitals', 'painScore', v)} placeholder="e.g., 4/10" {...styleProps} />
          </div>)}
        </div>
        {/* Maternal Assessment */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={Heart} title="Maternal Assessment" isExpanded={expandedSections.maternalAssessment} onToggle={() => toggleSection('maternalAssessment')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.maternalAssessment && (<div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Fundus" value={report.maternalAssessment.fundus} onChange={(v) => handleInputChange('maternalAssessment', 'fundus', v)} placeholder="e.g., Firm, U/U" {...styleProps} />
            <InputField label="Lochia" value={report.maternalAssessment.lochia} onChange={(v) => handleInputChange('maternalAssessment', 'lochia', v)} placeholder="e.g., Rubra, moderate" {...styleProps} />
            <InputField label="Perineum/Incision Status" value={report.maternalAssessment.perineumIncisionStatus} onChange={(v) => handleInputChange('maternalAssessment', 'perineumIncisionStatus', v)} placeholder="e.g., Intact, 2nd degree repair" {...styleProps} />
            <InputField label="Edema" value={report.maternalAssessment.edema} onChange={(v) => handleInputChange('maternalAssessment', 'edema', v)} placeholder="e.g., 1+ bilateral LE" {...styleProps} />
          </div>)}
        </div>
        {/* Antepartum/Intrapartum/Postpartum Status */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={ClipboardList} title="Antepartum/Intrapartum/Postpartum Status" isExpanded={expandedSections.postpartumStatus} onToggle={() => toggleSection('postpartumStatus')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.postpartumStatus && (<div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Delivery Type" value={report.postpartumStatus.deliveryType} onChange={(v) => handleInputChange('postpartumStatus', 'deliveryType', v)} placeholder="e.g., NSVD, C-section" {...styleProps} />
            <InputField label="Complications" value={report.postpartumStatus.complications} onChange={(v) => handleInputChange('postpartumStatus', 'complications', v)} placeholder="e.g., None, PPH" {...styleProps} />
            <InputField label="Activity Level" value={report.postpartumStatus.activityLevel} onChange={(v) => handleInputChange('postpartumStatus', 'activityLevel', v)} placeholder="e.g., OOB, ambulating" {...styleProps} />
            <InputField label="Last Cervical Exam (SVE)" value={report.postpartumStatus.lastCervicalExam} onChange={(v) => handleInputChange('postpartumStatus', 'lastCervicalExam', v)} placeholder="e.g., 4 cm / 80% / -2 at 0600" {...styleProps} />
            <InputField label="Membrane Status" value={report.postpartumStatus.membraneStatus} onChange={(v) => handleInputChange('postpartumStatus', 'membraneStatus', v)} placeholder="e.g., Intact, SROM @ 0230, AROM @ 0815" {...styleProps} />
          </div>)}
        </div>
        {/* Breastfeeding & Lactation */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={Heart} title="Breastfeeding & Lactation" isExpanded={expandedSections.breastfeedingLactation} onToggle={() => toggleSection('breastfeedingLactation')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.breastfeedingLactation && (<div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Breastfeeding" value={report.breastfeedingLactation.breastfeeding} onChange={(v) => handleInputChange('breastfeedingLactation', 'breastfeeding', v)} placeholder="e.g., Yes, initiated" {...styleProps} />
            <InputField label="Latch Quality" value={report.breastfeedingLactation.latchQuality} onChange={(v) => handleInputChange('breastfeedingLactation', 'latchQuality', v)} placeholder="e.g., Good, needs assistance" {...styleProps} />
            <InputField label="Pumping" value={report.breastfeedingLactation.pumping} onChange={(v) => handleInputChange('breastfeedingLactation', 'pumping', v)} placeholder="e.g., Q3h, colostrum noted" {...styleProps} />
            <InputField label="Lactation Consult" value={report.breastfeedingLactation.lactationConsult} onChange={(v) => handleInputChange('breastfeedingLactation', 'lactationConsult', v)} placeholder="e.g., Completed, pending" {...styleProps} />
          </div>)}
        </div>
        {/* GI/GU */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={Droplets} title="GI/GU" isExpanded={expandedSections.giGu} onToggle={() => toggleSection('giGu')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.giGu && (<div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Voiding Status" value={report.giGu.voidingStatus} onChange={(v) => handleInputChange('giGu', 'voidingStatus', v)} placeholder="e.g., Voiding well, Foley in place" {...styleProps} />
            <InputField label="Flatus" value={report.giGu.flatus} onChange={(v) => handleInputChange('giGu', 'flatus', v)} placeholder="e.g., Yes, passing flatus" {...styleProps} />
            <InputField label="Bowel Function" value={report.giGu.bowelFunction} onChange={(v) => handleInputChange('giGu', 'bowelFunction', v)} placeholder="e.g., No BM yet, BM x1" {...styleProps} />
          </div>)}
        </div>
        {/* Medications */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={Pill} title="Medications" isExpanded={expandedSections.medications} onToggle={() => toggleSection('medications')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.medications && (<div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Scheduled Meds" value={report.medications.scheduledMeds} onChange={(v) => handleInputChange('medications', 'scheduledMeds', v)} placeholder="e.g., Prenatal vitamins, Colace" {...styleProps} />
            <InputField label="PRNs Given" value={report.medications.prnsGiven} onChange={(v) => handleInputChange('medications', 'prnsGiven', v)} placeholder="e.g., Ibuprofen 600mg x2" {...styleProps} />
            <InputField label="Magnesium/Oxytocin" value={report.medications.magnesiumOxytocin} onChange={(v) => handleInputChange('medications', 'magnesiumOxytocin', v)} placeholder="e.g., Pitocin d/c'd, Mag ongoing" {...styleProps} />
          </div>)}
        </div>
        {/* Labs */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={TestTube} title="Labs" isExpanded={expandedSections.labs} onToggle={() => toggleSection('labs')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.labs && (<div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Hgb/Hct" value={report.labs.hgbHct} onChange={(v) => handleInputChange('labs', 'hgbHct', v)} placeholder="e.g., 10.2/30.1" {...styleProps} />
            <InputField label="Blood Type/Rh" value={report.labs.bloodTypeRh} onChange={(v) => handleInputChange('labs', 'bloodTypeRh', v)} placeholder="e.g., O+, RhoGAM given" {...styleProps} />
            <InputField label="GBS Status" value={report.labs.gbsStatus} onChange={(v) => handleInputChange('labs', 'gbsStatus', v)} placeholder="e.g., Negative, Positive-treated" {...styleProps} />
          </div>)}
        </div>
        {/* Infant Status */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={Users} title="Infant Status (if rooming-in)" isExpanded={expandedSections.infantStatus} onToggle={() => toggleSection('infantStatus')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.infantStatus && (<div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Infant Location" value={report.infantStatus.infantLocation} onChange={(v) => handleInputChange('infantStatus', 'infantLocation', v)} placeholder="e.g., Rooming-in, NICU" {...styleProps} />
            <InputField label="Feeding Method" value={report.infantStatus.feedingMethod} onChange={(v) => handleInputChange('infantStatus', 'feedingMethod', v)} placeholder="e.g., Breastfeeding, formula" {...styleProps} />
            <InputField label="Safety Bands Verified" value={report.infantStatus.safetyBandsVerified} onChange={(v) => handleInputChange('infantStatus', 'safetyBandsVerified', v)} placeholder="e.g., Yes, matching" {...styleProps} />
          </div>)}
        </div>
        {/* Education & Safety */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={BookOpen} title="Education & Safety" isExpanded={expandedSections.educationSafety} onToggle={() => toggleSection('educationSafety')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.educationSafety && (<div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Postpartum Education Completed" value={report.educationSafety.postpartumEducationCompleted} onChange={(v) => handleInputChange('educationSafety', 'postpartumEducationCompleted', v)} placeholder="e.g., Yes, in progress" {...styleProps} />
            <InputField label="Safe Sleep Reviewed" value={report.educationSafety.safeSleepReviewed} onChange={(v) => handleInputChange('educationSafety', 'safeSleepReviewed', v)} placeholder="e.g., Yes, verbalized understanding" {...styleProps} />
            <InputField label="Warning Signs Reviewed" value={report.educationSafety.warningSignsReviewed} onChange={(v) => handleInputChange('educationSafety', 'warningSignsReviewed', v)} placeholder="e.g., Yes, given handout" {...styleProps} />
          </div>)}
        </div>
        {/* Psychosocial */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={Smile} title="Psychosocial" isExpanded={expandedSections.psychosocial} onToggle={() => toggleSection('psychosocial')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.psychosocial && (<div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Mood/Affect" value={report.psychosocial.moodAffect} onChange={(v) => handleInputChange('psychosocial', 'moodAffect', v)} placeholder="e.g., Appropriate, tearful" {...styleProps} />
            <InputField label="Support Person Present" value={report.psychosocial.supportPersonPresent} onChange={(v) => handleInputChange('psychosocial', 'supportPersonPresent', v)} placeholder="e.g., Husband at bedside" {...styleProps} />
          </div>)}
        </div>
        {/* Discharge Planning */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={Home} title="Discharge Planning" isExpanded={expandedSections.dischargePlanning} onToggle={() => toggleSection('dischargePlanning')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.dischargePlanning && (<div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Discharge Date" value={report.dischargePlanning.dischargeDate} onChange={(v) => handleInputChange('dischargePlanning', 'dischargeDate', v)} placeholder="e.g., Tomorrow AM" {...styleProps} />
            <InputField label="Follow-up Appointments" value={report.dischargePlanning.followUpAppointments} onChange={(v) => handleInputChange('dischargePlanning', 'followUpAppointments', v)} placeholder="e.g., OB 2 weeks, Peds 3 days" {...styleProps} />
            <InputField label="Home Needs" value={report.dischargePlanning.homeNeeds} onChange={(v) => handleInputChange('dischargePlanning', 'homeNeeds', v)} placeholder="e.g., Car seat check, VNA" {...styleProps} />
          </div>)}
        </div>
        {/* Additional Notes */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={MessageSquare} title="Additional Notes" isExpanded={expandedSections.additionalNotes} onToggle={() => toggleSection('additionalNotes')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.additionalNotes && (<div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Events" value={report.additionalNotes.events} onChange={(v) => handleInputChange('additionalNotes', 'events', v)} placeholder="e.g., Significant events this shift" {...styleProps} />
            <InputField label="Provider Updates" value={report.additionalNotes.providerUpdates} onChange={(v) => handleInputChange('additionalNotes', 'providerUpdates', v)} placeholder="e.g., OB rounded, plan unchanged" {...styleProps} />
          </div>)}
        </div>
        {/* Other Notes */}
        <div className="report-section" style={{ borderColor: borderColor }}>
          <SectionHeader icon={FileText} title="Other Notes" isExpanded={expandedSections.otherNotes} onToggle={() => toggleSection('otherNotes')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
          {expandedSections.otherNotes && (<div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <div className="report-field"><label style={{ color: labelColor }}>Additional Information</label>
            <textarea value={report.otherNotes} onChange={(e) => handleInputChange('otherNotes', '', e.target.value)} placeholder="Any other information not captured in the fields above..." className="report-other-notes-textarea" style={{ width: '100%', minHeight: '100px', padding: '8px 12px', borderRadius: '6px', color: textColor, resize: 'vertical' }} /></div>
          </div>)}
        </div>
        {/* Save Button */}
        <div style={{ marginTop: '24px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <button onClick={handleSave} disabled={isSaving} style={{ backgroundColor: '#3b82f6', color: 'white', border: 'none', padding: '12px 24px', borderRadius: '8px', fontSize: '16px', fontWeight: '600', cursor: isSaving ? 'not-allowed' : 'pointer', opacity: isSaving ? 0.7 : 1, transition: 'all 0.2s ease' }} onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#2563eb'} onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#3b82f6'}>Save Report</button>
          {saveStatus && (<p style={{ color: saveStatus.includes('Error') ? '#ef4444' : '#10b981', marginTop: '8px', fontSize: '14px' }}>{saveStatus}</p>)}
        </div>
      </div>
    </>
  );
};

// ============================================
// Rehab Report Component
// ============================================
const RehabReport: React.FC<{
  patientData: Patient;
  savedReport: RehabReportData | undefined;
  onSaveReport: (mrn: string, report: RehabReportData) => void;
  loggedInUser: { first_name: string; last_name: string; email: string } | null;
  onReportSubmitted?: () => void;
}> = ({ patientData, savedReport, onSaveReport, loggedInUser, onReportSubmitted }) => {
  const { theme } = useTheme();
  // Merge savedReport with empty template to ensure all nested objects exist
  const [report, setReport] = useState<RehabReportData>(() => savedReport ? {
    ...emptyRehabReport,
    ...savedReport,
    patientOverview: { ...emptyRehabReport.patientOverview, ...savedReport.patientOverview },
    cognitionCommunication: { ...emptyRehabReport.cognitionCommunication, ...savedReport.cognitionCommunication },
    mobilityTransfers: { ...emptyRehabReport.mobilityTransfers, ...savedReport.mobilityTransfers },
    adls: { ...emptyRehabReport.adls, ...savedReport.adls },
    therapyServices: { ...emptyRehabReport.therapyServices, ...savedReport.therapyServices },
    painSpasticity: { ...emptyRehabReport.painSpasticity, ...savedReport.painSpasticity },
    medications: { ...emptyRehabReport.medications, ...savedReport.medications },
    skinSafety: { ...emptyRehabReport.skinSafety, ...savedReport.skinSafety },
    eliminationNutrition: { ...emptyRehabReport.eliminationNutrition, ...savedReport.eliminationNutrition },
    psychosocial: { ...emptyRehabReport.psychosocial, ...savedReport.psychosocial },
    dischargePlanning: { ...emptyRehabReport.dischargePlanning, ...savedReport.dischargePlanning },
    additionalNotes: { ...emptyRehabReport.additionalNotes, ...savedReport.additionalNotes },
  } : emptyRehabReport);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    patientOverview: true, cognitionCommunication: true, mobilityTransfers: true, adls: true,
    therapyServices: true, painSpasticity: true, medications: true, skinSafety: true,
    eliminationNutrition: true, psychosocial: true, dischargePlanning: true, additionalNotes: true, otherNotes: true
  });
  const [saveStatus, setSaveStatus] = useState<string>('');
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [previousSubmitter, setPreviousSubmitter] = useState<{ name: string; timestamp: string } | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Fetch the latest saved report from the database when patient changes
  // Skip DB fetch if we already have savedReport in parent state
  useEffect(() => {
    const fetchSavedReport = async () => {
      if (!patientData?.MRN) {
        setIsLoading(false);
        return;
      }
      
      // If we already have savedReport from parent, use it and skip DB fetch
      if (savedReport) {
        setReport({
          ...emptyRehabReport,
          ...savedReport,
          patientOverview: { ...emptyRehabReport.patientOverview, ...savedReport.patientOverview },
          cognitionCommunication: { ...emptyRehabReport.cognitionCommunication, ...savedReport.cognitionCommunication },
          mobilityTransfers: { ...emptyRehabReport.mobilityTransfers, ...savedReport.mobilityTransfers },
          adls: { ...emptyRehabReport.adls, ...savedReport.adls },
          therapyServices: { ...emptyRehabReport.therapyServices, ...savedReport.therapyServices },
          painSpasticity: { ...emptyRehabReport.painSpasticity, ...savedReport.painSpasticity },
          medications: { ...emptyRehabReport.medications, ...savedReport.medications },
          skinSafety: { ...emptyRehabReport.skinSafety, ...savedReport.skinSafety },
          eliminationNutrition: { ...emptyRehabReport.eliminationNutrition, ...savedReport.eliminationNutrition },
          psychosocial: { ...emptyRehabReport.psychosocial, ...savedReport.psychosocial },
          dischargePlanning: { ...emptyRehabReport.dischargePlanning, ...savedReport.dischargePlanning },
          additionalNotes: { ...emptyRehabReport.additionalNotes, ...savedReport.additionalNotes },
        });
        setIsLoading(false);
        try {
          const response = await axios.get<{
            success: boolean;
            report?: { submitted_by: string; timestamp: string; };
          }>(`${BACKEND_URL}/report/rehab/${patientData.MRN}`);
          if (response.data.success && response.data.report) {
            setPreviousSubmitter({
              name: response.data.report.submitted_by,
              timestamp: response.data.report.timestamp
            });
          }
        } catch { /* ignore */ }
        return;
      }
      
      setIsLoading(true);
      try {
        const response = await axios.get<{
          success: boolean;
          report?: {
            submitted_by: string;
            timestamp: string;
            report_data: RehabReportData;
          };
        }>(`${BACKEND_URL}/report/rehab/${patientData.MRN}`);
        if (response.data.success && response.data.report) {
          const dbReport = response.data.report;
          setReport(dbReport.report_data as RehabReportData);
          onSaveReport(patientData.MRN, dbReport.report_data as RehabReportData);
          setPreviousSubmitter({
            name: dbReport.submitted_by,
            timestamp: dbReport.timestamp
          });
        } else {
          setReport(emptyRehabReport);
          setPreviousSubmitter(null);
        }
      } catch (error) {
        setReport(emptyRehabReport);
        setPreviousSubmitter(null);
      }
      setIsLoading(false);
    };

    fetchSavedReport();
  }, [patientData?.MRN, savedReport]);

  // Update from local savedReport if it changes (e.g., from activity sync)
  useEffect(() => {
    if (savedReport) {
      setReport({
        ...emptyRehabReport,
        ...savedReport,
        patientOverview: { ...emptyRehabReport.patientOverview, ...savedReport.patientOverview },
        cognitionCommunication: { ...emptyRehabReport.cognitionCommunication, ...savedReport.cognitionCommunication },
        mobilityTransfers: { ...emptyRehabReport.mobilityTransfers, ...savedReport.mobilityTransfers },
        adls: { ...emptyRehabReport.adls, ...savedReport.adls },
        therapyServices: { ...emptyRehabReport.therapyServices, ...savedReport.therapyServices },
        painSpasticity: { ...emptyRehabReport.painSpasticity, ...savedReport.painSpasticity },
        medications: { ...emptyRehabReport.medications, ...savedReport.medications },
        skinSafety: { ...emptyRehabReport.skinSafety, ...savedReport.skinSafety },
        eliminationNutrition: { ...emptyRehabReport.eliminationNutrition, ...savedReport.eliminationNutrition },
        psychosocial: { ...emptyRehabReport.psychosocial, ...savedReport.psychosocial },
        dischargePlanning: { ...emptyRehabReport.dischargePlanning, ...savedReport.dischargePlanning },
        additionalNotes: { ...emptyRehabReport.additionalNotes, ...savedReport.additionalNotes },
      });
    }
  }, [savedReport]);

  const cardBgColor = theme === 'dark' ? '#1e293b' : '#ffffff';
  const borderColor = theme === 'dark' ? '#334155' : '#e2e8f0';
  const headerBgColor = theme === 'dark' ? '#0f172a' : '#f1f5f9';
  const textColor = theme === 'dark' ? '#f1f5f9' : '#1e293b';
  const labelColor = theme === 'dark' ? '#94a3b8' : '#64748b';

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const handleInputChange = (section: keyof RehabReportData, field: string, value: string) => {
    setReport(prev => {
      let newReport;
      if (section === 'otherNotes') {
        newReport = { ...prev, otherNotes: value };
      } else {
        newReport = {
          ...prev,
          [section]: { ...(prev[section] as Record<string, string>), [field]: value }
        };
      }
      onSaveReport(patientData.MRN, newReport);
      return newReport;
    });
  };

  const handleSave = async () => {
    onSaveReport(patientData.MRN, report);
    
    if (loggedInUser) {
      setIsSaving(true);
      try {
        const submittedBy = `${loggedInUser.first_name} ${loggedInUser.last_name}`;
        await axios.post(`${BACKEND_URL}/report/rehab/save`, {
          patient_name: patientData.Name,
          mrn: patientData.MRN,
          submitted_by: submittedBy,
          email: loggedInUser.email,
          report_data: report
        });
        setPreviousSubmitter({
          name: submittedBy,
          timestamp: new Date().toISOString()
        });
        setSaveStatus('Report saved successfully!');
        // Notify parent that report was submitted
        onReportSubmitted?.();
      } catch (error) {
        console.error('Error saving report to database:', error);
        setSaveStatus('Saved locally (database error)');
      }
      setIsSaving(false);
    } else {
      setSaveStatus('Report saved locally!');
    }
    setTimeout(() => setSaveStatus(''), 3000);
  };

  const styleProps = { cardBgColor, borderColor, textColor, labelColor };

  const formatTimestamp = (timestamp: string) => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleString('en-US', {
        month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true
      });
    } catch {
      return timestamp;
    }
  };

  if (isLoading) {
    return (
      <div className="patient-report-container" style={{ backgroundColor: cardBgColor, padding: '40px', textAlign: 'center' }}>
        <div style={{ color: labelColor }}>Loading report...</div>
      </div>
    );
  }

  return (
    <>
      <div className="report-header-container" style={{ 
        backgroundColor: '#3b82f6', 
        borderRadius: '12px', 
        padding: '20px 24px',
        marginBottom: '20px'
      }}>
        <h2 style={{ color: 'white', margin: 0 }}>
          Report For: {patientData.Name} | {patientData.MRN} | Room {patientData.Room || 'N/A'}
        </h2>
        {previousSubmitter && (
          <p style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '14px', margin: '8px 0 0 0' }}>
            Previous report by: <strong>{previousSubmitter.name}</strong> • {formatTimestamp(previousSubmitter.timestamp)}
          </p>
        )}
        {!previousSubmitter && (
          <p style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '14px', margin: '8px 0 0 0' }}>
            No previous report found for this patient
          </p>
        )}
      </div>
      <div className="patient-report-container" style={{ backgroundColor: cardBgColor }}>
        {/* Patient Overview */}
      <div className="report-section" style={{ borderColor: borderColor }}>
        <SectionHeader icon={Info} title="Patient Overview" isExpanded={expandedSections.patientOverview} 
          onToggle={() => toggleSection('patientOverview')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
        {expandedSections.patientOverview && (
          <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Rehab Dx" value={report.patientOverview.rehabDx || patientData.Diagnosis} onChange={(v) => handleInputChange('patientOverview', 'rehabDx', v)} placeholder="e.g., CVA, TBI, SCI" {...styleProps} />
            <DateInputField label="Date of Injury" value={report.patientOverview.dateOfInjury} onChange={(v) => handleInputChange('patientOverview', 'dateOfInjury', v)} placeholder="MM/DD/YYYY" {...styleProps} />
            <InputField label="Code Status" value={report.patientOverview.codeStatus} onChange={(v) => handleInputChange('patientOverview', 'codeStatus', v)} placeholder="e.g., Full Code" {...styleProps} />
            <InputField label="Precautions" value={report.patientOverview.precautions} onChange={(v) => handleInputChange('patientOverview', 'precautions', v)} placeholder="e.g., Fall risk, Aspiration precautions" {...styleProps} />
          </div>
        )}
      </div>

      {/* Cognition & Communication */}
      <div className="report-section" style={{ borderColor: borderColor }}>
        <SectionHeader icon={Brain} title="Cognition & Communication" isExpanded={expandedSections.cognitionCommunication} 
          onToggle={() => toggleSection('cognitionCommunication')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
        {expandedSections.cognitionCommunication && (
          <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Orientation (x1-4)" value={report.cognitionCommunication.orientation} onChange={(v) => handleInputChange('cognitionCommunication', 'orientation', v)} placeholder="e.g., A&O x3 (person, place, time)" {...styleProps} />
            <InputField label="Speech" value={report.cognitionCommunication.speech} onChange={(v) => handleInputChange('cognitionCommunication', 'speech', v)} placeholder="e.g., Dysarthric, clear, aphasia" {...styleProps} />
            <InputField label="Follows Commands" value={report.cognitionCommunication.followsCommands} onChange={(v) => handleInputChange('cognitionCommunication', 'followsCommands', v)} placeholder="e.g., 1-step, 2-step, complex" {...styleProps} />
            <InputField label="Memory/Insight" value={report.cognitionCommunication.memoryInsight} onChange={(v) => handleInputChange('cognitionCommunication', 'memoryInsight', v)} placeholder="e.g., Impaired short-term, fair insight" {...styleProps} />
          </div>
        )}
      </div>

      {/* Mobility & Transfers */}
      <div className="report-section" style={{ borderColor: borderColor }}>
        <SectionHeader icon={Accessibility} title="Mobility & Transfers" isExpanded={expandedSections.mobilityTransfers} 
          onToggle={() => toggleSection('mobilityTransfers')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
        {expandedSections.mobilityTransfers && (
          <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Bed Mobility" value={report.mobilityTransfers.bedMobility} onChange={(v) => handleInputChange('mobilityTransfers', 'bedMobility', v)} placeholder="e.g., Min assist, Mod assist" {...styleProps} />
            <InputField label="Transfers (Assist Level)" value={report.mobilityTransfers.transfers} onChange={(v) => handleInputChange('mobilityTransfers', 'transfers', v)} placeholder="e.g., Mod assist x1, stand pivot" {...styleProps} />
            <InputField label="Ambulation (Distance + Device)" value={report.mobilityTransfers.ambulation} onChange={(v) => handleInputChange('mobilityTransfers', 'ambulation', v)} placeholder="e.g., 50ft with RW, CGA" {...styleProps} />
            <InputField label="Wheelchair Use" value={report.mobilityTransfers.wheelchairUse} onChange={(v) => handleInputChange('mobilityTransfers', 'wheelchairUse', v)} placeholder="e.g., W/C for distances, self-propels" {...styleProps} />
            <InputField label="Bracing/Orthotics" value={report.mobilityTransfers.bracingOrthotics} onChange={(v) => handleInputChange('mobilityTransfers', 'bracingOrthotics', v)} placeholder="e.g., L AFO, TLSO" {...styleProps} />
          </div>
        )}
      </div>

      {/* ADLs */}
      <div className="report-section" style={{ borderColor: borderColor }}>
        <SectionHeader icon={ClipboardList} title="ADLs" isExpanded={expandedSections.adls} 
          onToggle={() => toggleSection('adls')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
        {expandedSections.adls && (
          <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Bathing" value={report.adls.bathing} onChange={(v) => handleInputChange('adls', 'bathing', v)} placeholder="e.g., Mod assist, shower chair" {...styleProps} />
            <InputField label="Dressing" value={report.adls.dressing} onChange={(v) => handleInputChange('adls', 'dressing', v)} placeholder="e.g., Min assist UE/LE" {...styleProps} />
            <InputField label="Toileting" value={report.adls.toileting} onChange={(v) => handleInputChange('adls', 'toileting', v)} placeholder="e.g., Supervision, raised toilet seat" {...styleProps} />
            <InputField label="Feeding" value={report.adls.feeding} onChange={(v) => handleInputChange('adls', 'feeding', v)} placeholder="e.g., Independent, setup assist" {...styleProps} />
          </div>
        )}
      </div>

      {/* Therapy Services */}
      <div className="report-section" style={{ borderColor: borderColor }}>
        <SectionHeader icon={Activity} title="Therapy Services" isExpanded={expandedSections.therapyServices} 
          onToggle={() => toggleSection('therapyServices')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
        {expandedSections.therapyServices && (
          <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="PT" value={report.therapyServices.pt} onChange={(v) => handleInputChange('therapyServices', 'pt', v)} placeholder="e.g., PT 2x/day, gait training" {...styleProps} />
            <InputField label="OT" value={report.therapyServices.ot} onChange={(v) => handleInputChange('therapyServices', 'ot', v)} placeholder="e.g., OT 1x/day, ADL training" {...styleProps} />
            <InputField label="SLP" value={report.therapyServices.slp} onChange={(v) => handleInputChange('therapyServices', 'slp', v)} placeholder="e.g., SLP 1x/day, swallow eval" {...styleProps} />
            <InputField label="Last Session Summary" value={report.therapyServices.lastSessionSummary} onChange={(v) => handleInputChange('therapyServices', 'lastSessionSummary', v)} placeholder="e.g., PT: Gait training 100ft, OT: ADL training" {...styleProps} />
            <InputField label="Therapy Tolerance" value={report.therapyServices.therapyTolerance} onChange={(v) => handleInputChange('therapyServices', 'therapyTolerance', v)} placeholder="e.g., Good, fatigues after 30 min" {...styleProps} />
          </div>
        )}
      </div>

      {/* Pain & Spasticity */}
      <div className="report-section" style={{ borderColor: borderColor }}>
        <SectionHeader icon={Thermometer} title="Pain & Spasticity" isExpanded={expandedSections.painSpasticity} 
          onToggle={() => toggleSection('painSpasticity')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
        {expandedSections.painSpasticity && (
          <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Pain Score" value={report.painSpasticity.painScore} onChange={(v) => handleInputChange('painSpasticity', 'painScore', v)} placeholder="e.g., 4/10" {...styleProps} />
            <InputField label="Pain Location" value={report.painSpasticity.painLocation} onChange={(v) => handleInputChange('painSpasticity', 'painLocation', v)} placeholder="e.g., R shoulder, low back" {...styleProps} />
            <InputField label="Spasticity" value={report.painSpasticity.spasticity} onChange={(v) => handleInputChange('painSpasticity', 'spasticity', v)} placeholder="e.g., Mod spasticity L LE, Ashworth 2" {...styleProps} />
            <InputField label="Pain Control Effectiveness" value={report.painSpasticity.painControlEffectiveness} onChange={(v) => handleInputChange('painSpasticity', 'painControlEffectiveness', v)} placeholder="e.g., Well controlled with scheduled meds" {...styleProps} />
          </div>
        )}
      </div>

      {/* Medications */}
      <div className="report-section" style={{ borderColor: borderColor }}>
        <SectionHeader icon={Pill} title="Medications" isExpanded={expandedSections.medications} 
          onToggle={() => toggleSection('medications')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
        {expandedSections.medications && (
          <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Scheduled Meds" value={report.medications.scheduledMeds} onChange={(v) => handleInputChange('medications', 'scheduledMeds', v)} placeholder="e.g., Lisinopril, Metformin, Gabapentin" {...styleProps} />
            <InputField label="PRNs Used" value={report.medications.prnsUsed} onChange={(v) => handleInputChange('medications', 'prnsUsed', v)} placeholder="e.g., Tylenol x1, effective" {...styleProps} />
            <InputField label="Anticoagulation" value={report.medications.anticoagulation} onChange={(v) => handleInputChange('medications', 'anticoagulation', v)} placeholder="e.g., Lovenox 40mg daily, INR 2.1" {...styleProps} />
            <InputField label="Spasticity Meds" value={report.medications.spasticityMeds} onChange={(v) => handleInputChange('medications', 'spasticityMeds', v)} placeholder="e.g., Baclofen 10mg TID" {...styleProps} />
          </div>
        )}
      </div>

      {/* Skin & Safety */}
      <div className="report-section" style={{ borderColor: borderColor }}>
        <SectionHeader icon={ShieldCheck} title="Skin & Safety" isExpanded={expandedSections.skinSafety} 
          onToggle={() => toggleSection('skinSafety')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
        {expandedSections.skinSafety && (
          <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Skin Integrity" value={report.skinSafety.skinIntegrity} onChange={(v) => handleInputChange('skinSafety', 'skinIntegrity', v)} placeholder="e.g., Intact, healed surgical site" {...styleProps} />
            <InputField label="Pressure Areas" value={report.skinSafety.pressureAreas} onChange={(v) => handleInputChange('skinSafety', 'pressureAreas', v)} placeholder="e.g., Redness sacrum, offloading" {...styleProps} />
            <InputField label="Fall Risk" value={report.skinSafety.fallRisk} onChange={(v) => handleInputChange('skinSafety', 'fallRisk', v)} placeholder="e.g., High, Morse score 65" {...styleProps} />
            <InputField label="Alarms/Safety Devices" value={report.skinSafety.alarmsSafetyDevices} onChange={(v) => handleInputChange('skinSafety', 'alarmsSafetyDevices', v)} placeholder="e.g., Bed alarm on, call light in reach" {...styleProps} />
          </div>
        )}
      </div>

      {/* Elimination & Nutrition */}
      <div className="report-section" style={{ borderColor: borderColor }}>
        <SectionHeader icon={Utensils} title="Elimination & Nutrition" isExpanded={expandedSections.eliminationNutrition} 
          onToggle={() => toggleSection('eliminationNutrition')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
        {expandedSections.eliminationNutrition && (
          <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Diet Type" value={report.eliminationNutrition.dietType} onChange={(v) => handleInputChange('eliminationNutrition', 'dietType', v)} placeholder="e.g., Mechanical soft, thin liquids" {...styleProps} />
            <InputField label="Swallow Precautions" value={report.eliminationNutrition.swallowPrecautions} onChange={(v) => handleInputChange('eliminationNutrition', 'swallowPrecautions', v)} placeholder="e.g., Chin tuck, small bites" {...styleProps} />
            <InputField label="Bladder Program" value={report.eliminationNutrition.bladderProgram} onChange={(v) => handleInputChange('eliminationNutrition', 'bladderProgram', v)} placeholder="e.g., Foley, I&C q4h, voiding trial" {...styleProps} />
            <InputField label="Bowel Program" value={report.eliminationNutrition.bowelProgram} onChange={(v) => handleInputChange('eliminationNutrition', 'bowelProgram', v)} placeholder="e.g., Colace daily, suppository PRN" {...styleProps} />
          </div>
        )}
      </div>

      {/* Psychosocial */}
      <div className="report-section" style={{ borderColor: borderColor }}>
        <SectionHeader icon={Smile} title="Psychosocial" isExpanded={expandedSections.psychosocial} 
          onToggle={() => toggleSection('psychosocial')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
        {expandedSections.psychosocial && (
          <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Mood/Affect" value={report.psychosocial.moodAffect} onChange={(v) => handleInputChange('psychosocial', 'moodAffect', v)} placeholder="e.g., Appropriate, tearful at times" {...styleProps} />
            <InputField label="Motivation" value={report.psychosocial.motivation} onChange={(v) => handleInputChange('psychosocial', 'motivation', v)} placeholder="e.g., Good, engaged in therapy" {...styleProps} />
            <InputField label="Family Involvement" value={report.psychosocial.familyInvolvement} onChange={(v) => handleInputChange('psychosocial', 'familyInvolvement', v)} placeholder="e.g., Spouse visits daily, involved in training" {...styleProps} />
          </div>
        )}
      </div>

      {/* Discharge Planning */}
      <div className="report-section" style={{ borderColor: borderColor }}>
        <SectionHeader icon={Calendar} title="Discharge Planning" isExpanded={expandedSections.dischargePlanning} 
          onToggle={() => toggleSection('dischargePlanning')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
        {expandedSections.dischargePlanning && (
          <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <DateInputField label="Est. Discharge Date" value={report.dischargePlanning.estDischargeDate} onChange={(v) => handleInputChange('dischargePlanning', 'estDischargeDate', v)} placeholder="MM/DD/YYYY" {...styleProps} />
            <InputField label="Discharge Location" value={report.dischargePlanning.dischargeLocation} onChange={(v) => handleInputChange('dischargePlanning', 'dischargeLocation', v)} placeholder="e.g., Home, SNF, LTAC" {...styleProps} />
            <InputField label="Equipment Needs" value={report.dischargePlanning.equipmentNeeds} onChange={(v) => handleInputChange('dischargePlanning', 'equipmentNeeds', v)} placeholder="e.g., W/C, hospital bed, shower chair" {...styleProps} />
            <InputField label="Caregiver Training" value={report.dischargePlanning.caregiverTraining} onChange={(v) => handleInputChange('dischargePlanning', 'caregiverTraining', v)} placeholder="e.g., Transfer training in progress" {...styleProps} />
            <InputField label="Barriers" value={report.dischargePlanning.barriers} onChange={(v) => handleInputChange('dischargePlanning', 'barriers', v)} placeholder="e.g., Home modifications needed, insurance" {...styleProps} />
          </div>
        )}
      </div>

      {/* Additional Notes */}
      <div className="report-section" style={{ borderColor: borderColor }}>
        <SectionHeader icon={MessageSquare} title="Additional Notes" isExpanded={expandedSections.additionalNotes} 
          onToggle={() => toggleSection('additionalNotes')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
        {expandedSections.additionalNotes && (
          <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <InputField label="Events" value={report.additionalNotes.events} onChange={(v) => handleInputChange('additionalNotes', 'events', v)} placeholder="e.g., Fall (no injury), seizure" {...styleProps} />
            <InputField label="Changes from Prior Shift" value={report.additionalNotes.changesFromPriorShift} onChange={(v) => handleInputChange('additionalNotes', 'changesFromPriorShift', v)} placeholder="e.g., New order for PT eval, diet upgrade" {...styleProps} />
            <InputField label="Follow-Ups" value={report.additionalNotes.followUps} onChange={(v) => handleInputChange('additionalNotes', 'followUps', v)} placeholder="e.g., MD to reassess pain meds, family meeting" {...styleProps} />
          </div>
        )}
      </div>

      {/* Other Notes */}
      <div className="report-section" style={{ borderColor: borderColor }}>
        <SectionHeader icon={FileText} title="Other Notes" isExpanded={expandedSections.otherNotes} 
          onToggle={() => toggleSection('otherNotes')} headerBgColor={headerBgColor} borderColor={borderColor} labelColor={labelColor} />
        {expandedSections.otherNotes && (
          <div className="report-section-content" style={{ backgroundColor: cardBgColor }}>
            <div className="report-field">
              <label style={{ color: labelColor }}>Additional Information</label>
              <textarea value={report.otherNotes} onChange={(e) => handleInputChange('otherNotes', '', e.target.value)} placeholder="Any other information not captured in the fields above..." className="report-other-notes-textarea" style={{ width: '100%', minHeight: '100px', padding: '8px 12px', borderRadius: '6px', color: textColor, resize: 'vertical' }} />
            </div>
          </div>
        )}
      </div>

      {/* Save Button */}
      <div className="report-save-container">
        {saveStatus && <div className="report-save-status" style={{ color: '#10b981' }}>{saveStatus}</div>}
        <button className="report-save-btn" onClick={handleSave} disabled={isSaving}>
          {isSaving ? 'Saving...' : 'Save Report'}
        </button>
      </div>
      </div>
    </>
  );
};

// ============================================
// Main PatientReport Component
// ============================================
const PatientReport: React.FC<PatientReportProps> = ({ patientData, savedReport, onSaveReport, loggedInUser, onReportSubmitted }) => {
  // Empty state - no patient selected
  if (!patientData) {
    return (
      <div className="content-columns">
        <section className="column-left" style={{ width: '100%', maxWidth: '100%' }}>
          <div className="empty-state-container">
            <div className="empty-state-icon">
              <Users size={80} strokeWidth={1.5} />
            </div>
            <h3 className="empty-state-title">Select a Patient to Get Started</h3>
            <p className="empty-state-text">
              Choose a patient from the sidebar to view and edit their shift report.
            </p>
            <div className="empty-state-hint">
              <ArrowLeft size={16} />
              <span>Use the patient list in the sidebar</span>
            </div>
          </div>
        </section>
      </div>
    );
  }

  // Med-Surg Report
  if (patientData.Department === 'Med-Surg') {
    return (
      <div className="content-columns">
        <section className="column-left" style={{ width: '100%', maxWidth: '100%' }}>
          <MedSurgReport
            patientData={patientData}
            savedReport={savedReport as MedSurgReportData | undefined}
            onSaveReport={(mrn, report) => onSaveReport(mrn, report as unknown as Record<string, unknown>)}
            loggedInUser={loggedInUser}
            onReportSubmitted={onReportSubmitted}
          />
        </section>
      </div>
    );
  }

  // ICU Report
  if (patientData.Department === 'ICU') {
    return (
      <div className="content-columns">
        <section className="column-left" style={{ width: '100%', maxWidth: '100%' }}>
          <ICUReport
            patientData={patientData}
            savedReport={savedReport as ICUReportData | undefined}
            onSaveReport={(mrn, report) => onSaveReport(mrn, report as unknown as Record<string, unknown>)}
            loggedInUser={loggedInUser}
            onReportSubmitted={onReportSubmitted}
          />
        </section>
      </div>
    );
  }

  // PACU Report
  if (patientData.Department === 'PACU') {
    return (
      <div className="content-columns">
        <section className="column-left" style={{ width: '100%', maxWidth: '100%' }}>
          <PACUReport
            patientData={patientData}
            savedReport={savedReport as PACUReportData | undefined}
            onSaveReport={(mrn, report) => onSaveReport(mrn, report as unknown as Record<string, unknown>)}
            loggedInUser={loggedInUser}
            onReportSubmitted={onReportSubmitted}
          />
        </section>
      </div>
    );
  }

  // ER Report
  if (patientData.Department === 'ER') {
    return (
      <div className="content-columns">
        <section className="column-left" style={{ width: '100%', maxWidth: '100%' }}>
          <ERReport
            patientData={patientData}
            savedReport={savedReport as ERReportData | undefined}
            onSaveReport={(mrn, report) => onSaveReport(mrn, report as unknown as Record<string, unknown>)}
            loggedInUser={loggedInUser}
            onReportSubmitted={onReportSubmitted}
          />
        </section>
      </div>
    );
  }

  // NICU Report
  if (patientData.Department === 'NICU') {
    return (
      <div className="content-columns">
        <section className="column-left" style={{ width: '100%', maxWidth: '100%' }}>
          <NICUReport
            patientData={patientData}
            savedReport={savedReport as NICUReportData | undefined}
            onSaveReport={(mrn, report) => onSaveReport(mrn, report as unknown as Record<string, unknown>)}
            loggedInUser={loggedInUser}
            onReportSubmitted={onReportSubmitted}
          />
        </section>
      </div>
    );
  }

  // Rehab Report
  if (patientData.Department === 'Rehab') {
    return (
      <div className="content-columns">
        <section className="column-left" style={{ width: '100%', maxWidth: '100%' }}>
          <RehabReport
            patientData={patientData}
            savedReport={savedReport as RehabReportData | undefined}
            onSaveReport={(mrn, report) => onSaveReport(mrn, report as unknown as Record<string, unknown>)}
            loggedInUser={loggedInUser}
            onReportSubmitted={onReportSubmitted}
          />
        </section>
      </div>
    );
  }

  // Pediatrics Report
  if (patientData.Department === 'Pediatrics') {
    return (
      <div className="content-columns">
        <section className="column-left" style={{ width: '100%', maxWidth: '100%' }}>
          <PediatricsReport
            patientData={patientData}
            savedReport={savedReport as PediatricsReportData | undefined}
            onSaveReport={(mrn, report) => onSaveReport(mrn, report as unknown as Record<string, unknown>)}
            loggedInUser={loggedInUser}
            onReportSubmitted={onReportSubmitted}
          />
        </section>
      </div>
    );
  }

  // Oncology Report
  if (patientData.Department === 'Oncology') {
    return (
      <div className="content-columns">
        <section className="column-left" style={{ width: '100%', maxWidth: '100%' }}>
          <OncologyReport
            patientData={patientData}
            savedReport={savedReport as OncologyReportData | undefined}
            onSaveReport={(mrn, report) => onSaveReport(mrn, report as unknown as Record<string, unknown>)}
            loggedInUser={loggedInUser}
            onReportSubmitted={onReportSubmitted}
          />
        </section>
      </div>
    );
  }

  // Neurology Report
  if (patientData.Department === 'Neurology') {
    return (
      <div className="content-columns">
        <section className="column-left" style={{ width: '100%', maxWidth: '100%' }}>
          <NeurologyReport
            patientData={patientData}
            savedReport={savedReport as NeurologyReportData | undefined}
            onSaveReport={(mrn, report) => onSaveReport(mrn, report as unknown as Record<string, unknown>)}
            loggedInUser={loggedInUser}
            onReportSubmitted={onReportSubmitted}
          />
        </section>
      </div>
    );
  }

  // Cardiology Report
  if (patientData.Department === 'Cardiology') {
    return (
      <div className="content-columns">
        <section className="column-left" style={{ width: '100%', maxWidth: '100%' }}>
          <CardiologyReport
            patientData={patientData}
            savedReport={savedReport as CardiologyReportData | undefined}
            onSaveReport={(mrn, report) => onSaveReport(mrn, report as unknown as Record<string, unknown>)}
            loggedInUser={loggedInUser}
            onReportSubmitted={onReportSubmitted}
          />
        </section>
      </div>
    );
  }

  // Labor & Delivery Report
  if (patientData.Department === 'Labor & Delivery') {
    return (
      <div className="content-columns">
        <section className="column-left" style={{ width: '100%', maxWidth: '100%' }}>
          <LaborDeliveryReport
            patientData={patientData}
            savedReport={savedReport as LaborDeliveryReportData | undefined}
            onSaveReport={(mrn, report) => onSaveReport(mrn, report as unknown as Record<string, unknown>)}
            loggedInUser={loggedInUser}
            onReportSubmitted={onReportSubmitted}
          />
        </section>
      </div>
    );
  }

  // Psychiatry Report
  if (patientData.Department === 'Psychiatry') {
    return (
      <div className="content-columns">
        <section className="column-left" style={{ width: '100%', maxWidth: '100%' }}>
          <PsychiatryReport
            patientData={patientData}
            savedReport={savedReport as PsychiatryReportData | undefined}
            onSaveReport={(mrn, report) => onSaveReport(mrn, report as unknown as Record<string, unknown>)}
            loggedInUser={loggedInUser}
            onReportSubmitted={onReportSubmitted}
          />
        </section>
      </div>
    );
  }

  // Women's Services (OB) Report
  if (patientData.Department === 'Women\'s Services (OB)') {
    return (
      <div className="content-columns">
        <section className="column-left" style={{ width: '100%', maxWidth: '100%' }}>
          <OBReport
            patientData={patientData}
            savedReport={savedReport as OBReportData | undefined}
            onSaveReport={(mrn, report) => onSaveReport(mrn, report as unknown as Record<string, unknown>)}
            loggedInUser={loggedInUser}
            onReportSubmitted={onReportSubmitted}
          />
        </section>
      </div>
    );
  }

  // Under construction - other departments
  return (
    <div className="content-columns">
      <section className="column-left" style={{ width: '100%', maxWidth: '100%' }}>
        <div className="under-construction-container">
          <div className="under-construction-icon">
            <Construction size={80} strokeWidth={1.5} />
          </div>
          <h3 className="under-construction-title">Under Construction</h3>
          <p className="under-construction-text">
            The {patientData.Department} department report is currently being developed.
          </p>
          <p className="under-construction-subtext">
            Med-Surg, ICU, PACU, NICU, Rehab, Pediatrics, ER, Oncology, Neurology, Cardiology, Labor & Delivery, Psychiatry, and Women's Services (OB) reports are available at this time.
          </p>
        </div>
      </section>
    </div>
  );
};

export default PatientReport;
